/* ============================================
   ROOM DESIGNER — Renderer Module
   Asset file: custom-room-designer-render.js

   Handles grid, room drawing, selection overlays,
   resize/rotate/endpoint handles, object rendering,
   wall & room labels.
   Loaded before custom-room-designer.js via <script defer>.
   Instantiated by the RoomDesigner constructor.
   ============================================ */

class RoomDesignerRender {
  constructor(rd) {
    this.rd = rd;
    this._installMethods();
  }

  _installMethods() {
    const rd = this.rd;
    // Bind each method onto the RD instance so existing call sites
    // (this.drawRoom(...), this._drawSelection(...), etc.) keep working.
    // .bind(rd) ensures 'this' inside each method refers to the RD instance.
    rd._drawSelection = this._drawSelection.bind(rd);
    rd._updateScaleBar = this._updateScaleBar.bind(rd);
    rd.drawGrid = this.drawGrid.bind(rd);
    rd._roomGeoFingerprint = this._roomGeoFingerprint.bind(rd);
    rd._getRoomGeoCache = this._getRoomGeoCache.bind(rd);
    rd.drawRoom = this.drawRoom.bind(rd);
    rd._hitTestHandles = this._hitTestHandles.bind(rd);
    rd._getGroupRotateHandle = this._getGroupRotateHandle.bind(rd);
    rd._drawHandles = this._drawHandles.bind(rd);
    rd._hitTestWallEndpoints = this._hitTestWallEndpoints.bind(rd);
    rd._drawWallEndpointDots = this._drawWallEndpointDots.bind(rd);
    rd._findSnapEndpoint = this._findSnapEndpoint.bind(rd);
    rd._getWallGrabHandle = this._getWallGrabHandle.bind(rd);
    rd._hitTestWallGrabHandle = this._hitTestWallGrabHandle.bind(rd);
    rd._drawWallGrabHandle = this._drawWallGrabHandle.bind(rd);
    rd._getWallCornerHandles = this._getWallCornerHandles.bind(rd);
    rd._getOffsetCorner = this._getOffsetCorner.bind(rd);
    rd._hitTestWallCornerHandles = this._hitTestWallCornerHandles.bind(rd);
    rd._drawWallCornerHandles = this._drawWallCornerHandles.bind(rd);
    rd._isObjectLabelObstructed = this._isObjectLabelObstructed.bind(rd);
    rd._drawObjects = this._drawObjects.bind(rd);
    rd._drawWallLabels = this._drawWallLabels.bind(rd);
    rd._drawPrintWallLabels = this._drawPrintWallLabels.bind(rd);
    rd._drawTurfLabels = this._drawTurfLabels.bind(rd);
    rd._drawTurfMarkings = this._drawTurfMarkings.bind(rd);
    rd._drawTurfMarkingsInner = this._drawTurfMarkingsInner.bind(rd);
    rd._drawTurfSledLanes = this._drawTurfSledLanes.bind(rd);
    rd._rotateEdges = this._rotateEdges.bind(rd);
    rd._getDoorSwingBounds = this._getDoorSwingBounds.bind(rd);
    rd._drawRoomLabels = this._drawRoomLabels.bind(rd);
    rd._getExteriorZones = this._getExteriorZones.bind(rd);
    rd._drawExteriorZoneFills = this._drawExteriorZoneFills.bind(rd);
    rd._drawTurfLogoHandles = this._drawTurfLogoHandles.bind(rd);
    rd._getTurfLogoHandles = this._getTurfLogoHandles.bind(rd);
    rd._hitTestTurfLogoHandle = this._hitTestTurfLogoHandle.bind(rd);
    rd._drawLightSource = this._drawLightSource.bind(rd);
    rd._drawTiledPlatformTexture = this._drawTiledPlatformTexture.bind(rd);
  }

  _drawSelection(scale) {
    const highlightWalls = []; const processedRooms = new Set();
    const visibleWallRoomMap = new Map();
    for (const room of this.rooms) {
      if (!room.cutouts || room.cutouts.length === 0) continue;
      const hasSelectedWall = room.walls.some(w => this.selectedWalls.has(w));
      const hasSelectedCutout = room.cutouts.some(c => c.walls.some(w => this.selectedWalls.has(w)));
      if (!hasSelectedWall && !hasSelectedCutout) continue;
      processedRooms.add(room);
      const vw = this.getVisibleRoomWalls(room); const icData = this.getInteriorCutoutWalls(room);
      const allW = [...vw, ...icData.map(d => d.wall)]; const rp = this.tracePolygon(room.walls);
      const mainW = this._getLargestComponent(allW, rp);
      // Track visible walls → room for offset calculation
      for (const w of mainW) visibleWallRoomMap.set(w, room);
      if (this.selectionStart && this.selectionEnd) {
        const minSX = Math.min(this.selectionStart.x, this.selectionEnd.x), maxSX = Math.max(this.selectionStart.x, this.selectionEnd.x);
        const minSY = Math.min(this.selectionStart.y, this.selectionEnd.y), maxSY = Math.max(this.selectionStart.y, this.selectionEnd.y);
        mainW.forEach(vw => { const mx = (vw.start.x + vw.end.x) / 2, my = (vw.start.y + vw.end.y) / 2; if (mx >= minSX && mx <= maxSX && my >= minSY && my <= maxSY) highlightWalls.push(vw); });
      }
    }
    this.selectedWalls.forEach(wall => {
      let alreadyProcessed = false;
      for (const room of processedRooms) { if (room.walls.includes(wall) || room.cutouts.some(c => c.walls.includes(wall))) { alreadyProcessed = true; break; } }
      if (!alreadyProcessed) highlightWalls.push(wall);
    });
    // Build wall→room map for offset calculation
    const wallToRoom = new Map();
    for (const rm of this.rooms) {
      for (const w of rm.walls) wallToRoom.set(w, rm);
      if (rm.cutouts) for (const c of rm.cutouts) for (const w of c.walls) wallToRoom.set(w, rm);
    }
    // Include visible walls from cutout rooms (they're new objects from getVisibleRoomWalls)
    for (const [w, rm] of visibleWallRoomMap) wallToRoom.set(w, rm);
    // Draw selected walls with mitered joins (matching normal wall rendering)
    if (highlightWalls.length > 0) {
      const wallComponents = this._getConnectedComponents(highlightWalls);
      this.ctx.strokeStyle = 'rgba(255, 100, 100, 0.8)'; this.ctx.lineWidth = 6 / scale;
      this.ctx.lineJoin = 'miter'; this.ctx.lineCap = 'square'; this.ctx.miterLimit = 10;
      for (const comp of wallComponents) {
        const undrawn = new Set(comp.map((_, i) => i));
        const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
        const startAdj = {}; const endAdj = {};
        comp.forEach((wall, idx) => {
          const sk = key(wall.start); const ek = key(wall.end);
          if (!startAdj[sk]) startAdj[sk] = []; if (!endAdj[ek]) endAdj[ek] = [];
          startAdj[sk].push(idx); endAdj[ek].push(idx);
        });
        while (undrawn.size > 0) {
          const startIdx = undrawn.values().next().value;
          undrawn.delete(startIdx);
          const path = [comp[startIdx].start, comp[startIdx].end];
          let current = key(comp[startIdx].end);
          while (true) {
            let found = false;
            const fromStart = startAdj[current] || [];
            for (const idx of fromStart) {
              if (undrawn.has(idx)) { undrawn.delete(idx); path.push(comp[idx].end); current = key(comp[idx].end); found = true; break; }
            }
            if (!found) {
              const fromEnd = endAdj[current] || [];
              for (const idx of fromEnd) {
                if (undrawn.has(idx)) { undrawn.delete(idx); path.push(comp[idx].start); current = key(comp[idx].start); found = true; break; }
              }
            }
            if (!found) break;
          }
          if (path.length >= 2) {
            const first = path[0], last = path[path.length - 1];
            const isClosed = Math.abs(first.x - last.x) < 0.05 && Math.abs(first.y - last.y) < 0.05;
            // Offset selection highlight to match wall visual position (halfT outward from fill)
            const compRoom = wallToRoom.get(comp[startIdx]) || null;
            const useOffset = compRoom && compRoom.closed;
            let selOutward = 1;
            if (useOffset) {
              const geo = this._getRoomGeoCache(compRoom);
              const { roomPolygon, cutoutPolys } = geo;
              if (roomPolygon && roomPolygon.length >= 3) {
                const midX = (path[0].x + path[1].x) / 2, midY = (path[0].y + path[1].y) / 2;
                const sdx = path[1].x - path[0].x, sdy = path[1].y - path[0].y;
                const sLen = Math.sqrt(sdx * sdx + sdy * sdy) || 1;
                const tD = 0.1;
                const t1x = midX + (-sdy / sLen) * tD, t1y = midY + (sdx / sLen) * tD;
                const t2x = midX - (-sdy / sLen) * tD, t2y = midY - (sdx / sLen) * tD;
                const isFilled = (tx, ty) => {
                  if (!this.pointInPolygon(tx, ty, roomPolygon)) return false;
                  if (cutoutPolys) { for (const cp of cutoutPolys) { if (cp && cp.length >= 3 && this.pointInPolygon(tx, ty, cp)) return false; } }
                  return true;
                };
                const s1 = isFilled(t1x, t1y), s2 = isFilled(t2x, t2y);
                if (s1 && !s2) selOutward = -1;
                else if (!s1 && s2) selOutward = 1;
                else {
                  let signedArea = 0;
                  for (let i = 0; i < path.length - 1; i++) signedArea += (path[i].x * path[i + 1].y - path[i + 1].x * path[i].y);
                  selOutward = signedArea > 0 ? -1 : 1;
                }
              }
            }
            const halfT = this.wallThickness / 2;
            const drawPts = useOffset ? (() => {
              const pts = []; const n = path.length;
              for (let i = 0; i < n; i++) {
                const prev = (i > 0) ? i - 1 : (isClosed ? n - 2 : -1);
                const next = (i < n - 1) ? i + 1 : (isClosed ? 1 : -1);
                let nx, ny;
                if (prev >= 0 && next >= 0) {
                  const d1x = path[i].x - path[prev].x, d1y = path[i].y - path[prev].y;
                  const len1 = Math.sqrt(d1x * d1x + d1y * d1y) || 1;
                  const n1x = -d1y / len1 * selOutward, n1y = d1x / len1 * selOutward;
                  const d2x = path[next].x - path[i].x, d2y = path[next].y - path[i].y;
                  const len2 = Math.sqrt(d2x * d2x + d2y * d2y) || 1;
                  const n2x = -d2y / len2 * selOutward, n2y = d2x / len2 * selOutward;
                  const dot = n1x * n2x + n1y * n2y;
                  if (Math.abs(1 + dot) < 0.001) { nx = n1x * halfT; ny = n1y * halfT; }
                  else {
                    const mx = (n1x + n2x) / 2, my = (n1y + n2y) / 2;
                    const mLen = Math.sqrt(mx * mx + my * my) || 1;
                    const cosHalf = (mx * n1x + my * n1y) / mLen;
                    const miterLen = cosHalf !== 0 ? halfT / cosHalf : halfT;
                    const clampedLen = Math.min(miterLen, halfT * 4);
                    nx = mx / mLen * clampedLen; ny = my / mLen * clampedLen;
                  }
                } else if (prev >= 0) {
                  const dx = path[i].x - path[prev].x, dy = path[i].y - path[prev].y;
                  const len = Math.sqrt(dx * dx + dy * dy) || 1;
                  nx = -dy / len * selOutward * halfT; ny = dx / len * selOutward * halfT;
                } else if (next >= 0) {
                  const dx = path[next].x - path[i].x, dy = path[next].y - path[i].y;
                  const len = Math.sqrt(dx * dx + dy * dy) || 1;
                  nx = -dy / len * selOutward * halfT; ny = dx / len * selOutward * halfT;
                } else { nx = 0; ny = 0; }
                pts.push({ x: path[i].x + nx, y: path[i].y + ny });
              }
              return pts;
            })() : path;
            this.ctx.beginPath(); this.ctx.moveTo(drawPts[0].x, drawPts[0].y);
            for (let pi = 1; pi < drawPts.length; pi++) this.ctx.lineTo(drawPts[pi].x, drawPts[pi].y);
            if (isClosed) this.ctx.closePath();
            this.ctx.stroke();
          }
        }
      }
      this.ctx.lineJoin = 'miter'; this.ctx.lineCap = 'butt';
    }
    // Draw selected doors with solid red outline over door graphic + swing
    this.selectedDoors.forEach(door => {
      if (this._doorSwingEdit && this._doorSwingEdit.door === door) return;
      const wall = door.wall;
      const doorRoom = this.rooms.find(r => r.closed && r.doors && r.doors.includes(door)) || null;
      const hingePoint = this.paramToPoint(wall, door.hingeParam);
      const endPoint = this.paramToPoint(wall, door.endParam);
      const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
      const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
      if (wLen === 0) return;
      const wallDirX = wdx / wLen, wallDirY = wdy / wLen;
      const perpX = -wallDirY, perpY = wallDirX;
      const off = this._getDoorOffset(wall, perpX, perpY, doorRoom);
      const offHinge = { x: hingePoint.x + off.x, y: hingePoint.y + off.y };
      const offEnd = { x: endPoint.x + off.x, y: endPoint.y + off.y };
      // Draw door graphic in red
      this.drawDoorGraphic(offHinge, offEnd, wallDirX, wallDirY, perpX, perpY, door.type, scale, 'rgba(255, 100, 100, 0.9)', 1.0);
      // Draw swing arc in red if present (hide in floor mode when seams are on)
      if (door.swing && !(this._floorMode && this.showSeamLines)) {
        const doorWidth = this.getDistance(offHinge, offEnd);
        if (doorWidth > 0) {
          const s = door.swing;
          this._drawDoorSwingArc(offHinge, offEnd, doorWidth, wallDirX, wallDirY, perpX, perpY, s.perpSign, s.hingeEnd || null, door.type, scale, 'rgba(255, 100, 100, 0.9)', 1.0);
        }
      }
      // Midpoint grab handle (skip during swing edit — dots need to be on top)
      if (this._selectHandleMode === 'doorSwingEdit' && this._selectHandleInfo && this._selectHandleInfo.door === door) return;
      const midParam = (door.hingeParam + door.endParam) / 2;
      const midPoint = this.paramToPoint(wall, midParam);
      const offMid = { x: midPoint.x + off.x, y: midPoint.y + off.y };
      const handleSize = this._handleSize / scale;
      const isCyclePreviewDoor = this._isCycleHighlighted('doorGrab', { door });
      const isActiveDoor = this._selectHandleMode === 'doorGrab' && this._selectHandleInfo && this._selectHandleInfo.door === door;
      const sz = (isActiveDoor || isCyclePreviewDoor) ? handleSize * 1.5 : handleSize;
      if (isActiveDoor) {
        this.ctx.save();
        this.ctx.shadowColor = '#00cc00'; this.ctx.shadowBlur = 8 / scale;
        this.ctx.fillStyle = 'rgba(0, 200, 0, 0.3)';
        this.ctx.fillRect(offMid.x - sz / 2, offMid.y - sz / 2, sz, sz);
        this.ctx.restore();
        this.ctx.strokeStyle = '#00aa00'; this.ctx.lineWidth = 2 / scale;
        this.ctx.strokeRect(offMid.x - sz / 2, offMid.y - sz / 2, sz, sz);
      } else if (isCyclePreviewDoor) {
        this.ctx.fillStyle = '#fff'; this.ctx.strokeStyle = '#00aa00'; this.ctx.lineWidth = 2.5 / scale;
        this.ctx.fillRect(offMid.x - sz / 2, offMid.y - sz / 2, sz, sz);
        this.ctx.strokeRect(offMid.x - sz / 2, offMid.y - sz / 2, sz, sz);
      } else {
        // Sole door selection → green (it's the only handle); multi-selection → blue
        const isSoleDoor = this.selectedDoors.size === 1 && this.selectedWalls.size === 0 && this.selectedObjects.size === 0;
        const stroke = isSoleDoor ? '#00aa00' : '#006BCC';
        const lw = isSoleDoor ? 2.5 : 1.5;
        this.ctx.fillStyle = '#fff'; this.ctx.strokeStyle = stroke; this.ctx.lineWidth = lw / scale;
        this.ctx.fillRect(offMid.x - sz / 2, offMid.y - sz / 2, sz, sz);
        this.ctx.strokeRect(offMid.x - sz / 2, offMid.y - sz / 2, sz, sz);
      }
    });
    // Draw selected objects — individual outlines for ungrouped, group AABB for grouped
    // On Customize tab: blue outlines (non-destructive selection); elsewhere: red outlines
    const _onCustomizeSel = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
    const _selStroke = _onCustomizeSel ? 'rgba(0, 107, 204, 0.8)' : 'rgba(255, 100, 100, 0.8)';
    const drawnGroups = new Set();
    this.selectedObjects.forEach(obj => {
      // Grouped objects get a single group outline instead of individual outlines
      if (obj.groupId) {
        if (drawnGroups.has(obj.groupId)) return;
        drawnGroups.add(obj.groupId);
        const members = this.objects.filter(o => o.groupId === obj.groupId);
        if (members.length < 2) return; // shouldn't happen, but fall through to individual
        // Compute AABB across all group members using tight bounds (rotation-aware)
        let gMinX = Infinity, gMinY = Infinity, gMaxX = -Infinity, gMaxY = -Infinity;
        for (const m of members) {
          const rot = m.rotation || 0;
          const tw = (m.type === 'equipment' ? (m.tightWidth || m.width) : m.width) || 1;
          const td = (m.type === 'equipment' ? (m.tightDepth || m.depth) : (m.depth || m.height)) || 1;
          const tox = (m.type === 'equipment' ? (m.tightOffsetX || 0) : 0);
          const toy = (m.type === 'equipment' ? (m.tightOffsetY || 0) : 0);
          // Rotate tight center offset into world space
          const cosR = Math.cos(rot), sinR = Math.sin(rot);
          const cx = m.x + tox * cosR - toy * sinR;
          const cy = m.y + tox * sinR + toy * cosR;
          const acosR = Math.abs(cosR), asinR = Math.abs(sinR);
          const hx = (tw / 2) * acosR + (td / 2) * asinR;
          const hy = (tw / 2) * asinR + (td / 2) * acosR;
          gMinX = Math.min(gMinX, cx - hx); gMaxX = Math.max(gMaxX, cx + hx);
          gMinY = Math.min(gMinY, cy - hy); gMaxY = Math.max(gMaxY, cy + hy);
        }
        this.ctx.strokeStyle = _selStroke; this.ctx.lineWidth = 3 / scale;
        this.ctx.strokeRect(gMinX, gMinY, gMaxX - gMinX, gMaxY - gMinY);
        return;
      }
      // Ungrouped: individual outline
      this.ctx.strokeStyle = _selStroke; this.ctx.lineWidth = 3 / scale;
      const wt = this.wallThickness;
      if (obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf' || obj.type === 'platform') {
        const rot = obj.rotation || 0;
        this.ctx.save();
        this.ctx.translate(obj.x, obj.y);
        this.ctx.rotate(rot);
        if (obj.type === 'turf' || obj.type === 'platform') {
          this.ctx.strokeRect(-obj.width / 2, -obj.depth / 2, obj.width, obj.depth);
        } else if (obj.type === 'equipment') {
          const tw = obj.tightWidth || obj.width, td = obj.tightDepth || obj.depth;
          const tox = obj.tightOffsetX || 0, toy = obj.tightOffsetY || 0;
          this.ctx.strokeRect(tox - tw / 2, toy - td / 2, tw, td);
        } else if (this.hollowWalls) {
          const hw = obj.width / 2, hd = obj.depth / 2;
          this.ctx.strokeRect(-hw, -hd, obj.width, obj.depth);
          if (obj.width > wt * 2 && obj.depth > wt * 2) this.ctx.strokeRect(-hw + wt, -hd + wt, obj.width - wt * 2, obj.depth - wt * 2);
        } else {
          const halfT = wt / 2;
          this.ctx.strokeRect(-obj.width / 2 + halfT, -obj.depth / 2 + halfT, obj.width - wt, obj.depth - wt);
        }
        this.ctx.restore();
      }
      else if (obj.type === 'circle') {
        const r = obj.diameter / 2;
        if (this.hollowWalls) {
          this.ctx.beginPath(); this.ctx.arc(obj.x, obj.y, r, 0, Math.PI * 2); this.ctx.stroke();
          if (r > wt) { this.ctx.beginPath(); this.ctx.arc(obj.x, obj.y, r - wt, 0, Math.PI * 2); this.ctx.stroke(); }
        } else {
          this.ctx.beginPath(); this.ctx.arc(obj.x, obj.y, r - wt / 2, 0, Math.PI * 2); this.ctx.stroke();
        }
      }
    });
    // Resize/rotate handles
    const _onCustomizeTab = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
    if (!_onCustomizeTab) {
      this._drawHandles(scale);
      // Turf logo handles (drawn after regular handles so they're on top)
      this._drawTurfLogoHandles(scale);
    } else {
      // Customize tab: draw handles only for non-turf (equipment gets rotate handle)
      this._drawHandles(scale, /* skipTurf */ true);
      // But still draw turf logo handles (logos are interactive on Customize tab)
      this._drawTurfLogoHandles(scale);
    }
    // Wall endpoint dots, corner handles, and grab handle
    this._drawWallEndpointDots(scale);
    this._drawWallCornerHandles(scale);
    this._drawWallGrabHandle(scale);
    // Mobile door swing edit (drawn last so dots are on top of grab handle)
    if (this._selectHandleMode === 'doorSwingEdit') this._drawDoorEditSwingMobile(scale);
    if (this.selectionStart && this.selectionEnd && this.isSelecting) {
      const sx = Math.min(this.selectionStart.x, this.selectionEnd.x), sy = Math.min(this.selectionStart.y, this.selectionEnd.y);
      const sw = Math.abs(this.selectionEnd.x - this.selectionStart.x), sh = Math.abs(this.selectionEnd.y - this.selectionStart.y);
      this.ctx.fillStyle = 'rgba(0, 107, 204, 0.1)'; this.ctx.fillRect(sx, sy, sw, sh);
      this.ctx.strokeStyle = 'rgba(0, 107, 204, 0.6)'; this.ctx.lineWidth = 2 / scale; this.ctx.setLineDash([5 / scale, 5 / scale]); this.ctx.strokeRect(sx, sy, sw, sh); this.ctx.setLineDash([]);
    }
  }

  /* ── SCALE BAR ── */

  _updateScaleBar() {
    const barEl = document.getElementById('scaleBar');
    const fillEl = barEl && barEl.querySelector('.scale-bar-fill');
    const labelEl = document.getElementById('scaleBarLabel');
    if (!barEl || !fillEl || !labelEl) return;
    const pxPerFoot = this.basePixelsPerFoot * this.zoomLevel;
    // Pick a "nice" scale distance that produces a bar between 40–140px
    let niceValues;
    if (this._isMetric()) {
      // Nice meter values converted to feet (1 m = 3.28084 ft)
      const niceMeters = [0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 20, 25, 50, 100];
      niceValues = niceMeters.map(m => m * 3.28084);
    } else {
      niceValues = [0.5, 1, 2, 3, 5, 10, 15, 20, 25, 50, 100];
    }
    let bestFeet = niceValues[0];
    for (const ft of niceValues) {
      const px = ft * pxPerFoot;
      if (px >= 40 && px <= 140) { bestFeet = ft; break; }
      if (px > 140) break;
      bestFeet = ft;
    }
    const barPx = Math.round(bestFeet * pxPerFoot);
    fillEl.style.width = barPx + 'px';
    labelEl.textContent = this.formatMeasurement(bestFeet);
  }

  /* ── GRID ── */

  drawGrid() {
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const screenPixelsPerInch = scale / 12; const showInches = screenPixelsPerInch >= 3;
    // Compute visible world bounds from all 4 screen corners (handles rotation)
    const c0 = this.screenToWorld(0, 0), c1 = this.screenToWorld(this._cssWidth, 0);
    const c2 = this.screenToWorld(this._cssWidth, this._cssHeight), c3 = this.screenToWorld(0, this._cssHeight);
    const left = Math.min(c0.x, c1.x, c2.x, c3.x), right = Math.max(c0.x, c1.x, c2.x, c3.x);
    const top = Math.min(c0.y, c1.y, c2.y, c3.y), bottom = Math.max(c0.y, c1.y, c2.y, c3.y);
    // Batched foot-level grid (single path + single stroke)
    this.ctx.strokeStyle = this._tc.gridMajor; this.ctx.lineWidth = 1 / scale;
    this.ctx.beginPath();
    for (let x = Math.floor(left); x <= Math.ceil(right); x++) { this.ctx.moveTo(x, top); this.ctx.lineTo(x, bottom); }
    for (let y = Math.floor(top); y <= Math.ceil(bottom); y++) { this.ctx.moveTo(left, y); this.ctx.lineTo(right, y); }
    this.ctx.stroke();
    // Print mode: faint 1ft lines only — no super-major, no inch grid
    if (this._printMode) return;
    // 10-foot super-major lines — fade in from 150% down to 80% zoom
    const zoom = this.zoomLevel;
    if (zoom <= 1.5) {
      const alpha = Math.min(Math.max((1.5 - zoom) / 0.7, 0), 1) * 0.35;
      const prevAlpha = this.ctx.globalAlpha;
      this.ctx.globalAlpha = alpha;
      this.ctx.strokeStyle = this.darkMode ? '#5a7aaa' : '#888888';
      this.ctx.lineWidth = 1.5 / scale;
      this.ctx.beginPath();
      for (let x = Math.floor(left / 10) * 10; x <= Math.ceil(right); x += 10) { this.ctx.moveTo(x, top); this.ctx.lineTo(x, bottom); }
      for (let y = Math.floor(top / 10) * 10; y <= Math.ceil(bottom); y += 10) { this.ctx.moveTo(left, y); this.ctx.lineTo(right, y); }
      this.ctx.stroke();
      this.ctx.globalAlpha = prevAlpha;
    }
    if (showInches) {
      this.ctx.strokeStyle = this._tc.gridMinor; this.ctx.lineWidth = 0.7 / scale;
      this.ctx.beginPath();
      for (let x = Math.floor(left * 12) / 12; x <= right; x += 1 / 12) { if (Math.abs(x - Math.round(x)) > 0.01) { this.ctx.moveTo(x, top); this.ctx.lineTo(x, bottom); } }
      for (let y = Math.floor(top * 12) / 12; y <= bottom; y += 1 / 12) { if (Math.abs(y - Math.round(y)) > 0.01) { this.ctx.moveTo(left, y); this.ctx.lineTo(right, y); } }
      this.ctx.stroke();
    }
  }

  /* ── LIGHT SOURCE — overlays entire canvas for 3D depth ── */

  /* ── TURF LOGO HANDLES ── */

  /** Get logo handle positions in world coordinates for a turf object's focused logo. */
  _getTurfLogoHandles(obj) {
    const idx = this._turfLogoFocusedIdx;
    if (!obj || obj.type !== 'turf' || idx == null || !obj.turfLogos || !obj.turfLogos[idx]) return null;
    const logo = obj.turfLogos[idx];
    if ((!logo.image && logo.type !== 'ladder' && logo.type !== 'dotdrill') || !logo.width || !logo.height) return null;
    const lw = logo.width, lh = logo.height;
    const lx = logo.x || 0, ly = logo.y || 0;
    const lr = logo.rotation || 0;
    const objRot = obj.rotation || 0;
    // Logo corners in logo-local space
    const corners = [
      { name: 'nw', cx: -lw / 2, cy: -lh / 2 },
      { name: 'ne', cx: lw / 2,  cy: -lh / 2 },
      { name: 'se', cx: lw / 2,  cy: lh / 2 },
      { name: 'sw', cx: -lw / 2, cy: lh / 2 }
    ];
    // Transform: logo-local → turf-local → world
    const cosL = Math.cos(lr), sinL = Math.sin(lr);
    const cosO = Math.cos(objRot), sinO = Math.sin(objRot);
    const handles = [];
    // Ladders/dot drills: only rotate handle, no corner resize handles
    if (logo.type !== 'ladder' && logo.type !== 'dotdrill') {
      for (const c of corners) {
        const tlx = lx + c.cx * cosL - c.cy * sinL;
        const tly = ly + c.cx * sinL + c.cy * cosL;
        const wx = obj.x + tlx * cosO - tly * sinO;
        const wy = obj.y + tlx * sinO + tly * cosO;
        handles.push({ name: c.name, wx, wy });
      }
    }
    // Rotate handle positioning — mirrors equipment pattern exactly
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const stemWorld = 20 / scale;
    const selSet = this._turfLogoSelectedIdxs;
    const isActiveRot = this._turfLogoDrag === 'rotate';
    if (selSet && selSet.size > 1 && obj.turfLogos) {
      // Group rotate: bounding box of all selected logos in world space
      let minWx = Infinity, minWy = Infinity, maxWx = -Infinity, maxWy = -Infinity;
      for (const si of selSet) {
        const sl = obj.turfLogos[si];
        if (!sl || !sl.width || !sl.height) continue;
        const slx = sl.x || 0, sly = sl.y || 0, slr = sl.rotation || 0;
        const cSlr = Math.cos(slr), sSlr = Math.sin(slr);
        const shw = sl.width / 2, shh = sl.height / 2;
        for (const [cx, cy] of [[-shw,-shh],[shw,-shh],[shw,shh],[-shw,shh]]) {
          const tlx = slx + cx * cSlr - cy * sSlr;
          const tly = sly + cx * sSlr + cy * cSlr;
          const wx = obj.x + tlx * cosO - tly * sinO;
          const wy = obj.y + tlx * sinO + tly * cosO;
          if (wx < minWx) minWx = wx; if (wx > maxWx) maxWx = wx;
          if (wy < minWy) minWy = wy; if (wy > maxWy) maxWy = wy;
        }
      }
      const gcx = (minWx + maxWx) / 2, gcy = (minWy + maxWy) / 2;
      const topOffset = gcy - minWy;
      const circumR = topOffset + stemWorld;
      if (isActiveRot && this.mouseWorldRaw) {
        const dx = this.mouseWorldRaw.x - gcx, dy = this.mouseWorldRaw.y - gcy;
        const angle = Math.atan2(dy, dx);
        handles.push({ name: 'rotate', wx: gcx + Math.cos(angle) * circumR, wy: gcy + Math.sin(angle) * circumR });
      } else {
        handles.push({ name: 'rotate', wx: gcx, wy: gcy - circumR });
      }
    } else {
      // Single logo: same formula as equipment _getRotateHandle
      const cWx = obj.x + lx * cosO - ly * sinO;
      const cWy = obj.y + lx * sinO + ly * cosO;
      const totalRot = lr + objRot;
      const cosT = Math.cos(totalRot), sinT = Math.sin(totalRot);
      const visualHalfH = Math.abs(cosT) * (lh / 2) + Math.abs(sinT) * (lw / 2);
      const circumR = visualHalfH + stemWorld;
      if (isActiveRot && this.mouseWorldRaw) {
        const dx = this.mouseWorldRaw.x - cWx, dy = this.mouseWorldRaw.y - cWy;
        const angle = Math.atan2(dy, dx);
        handles.push({ name: 'rotate', wx: cWx + Math.cos(angle) * circumR, wy: cWy + Math.sin(angle) * circumR });
      } else {
        handles.push({ name: 'rotate', wx: cWx, wy: cWy - circumR });
      }
    }
    return handles;
  }

  /** Hit-test logo handles; returns { name, obj } or null. */
  _hitTestTurfLogoHandle(sx, sy) {
    if (this.activeTool !== 'select') return null;
    if (this._turfLogoFocusedIdx == null) return null;
    if (!this.selectedObjects || this.selectedObjects.size !== 1) return null;
    const obj = [...this.selectedObjects][0];
    const handles = this._getTurfLogoHandles(obj);
    if (!handles) return null;
    const hitR = this._hitRadius;
    for (const h of handles) {
      const _hs = this.worldToScreen(h.wx, h.wy);
      const hsx = _hs.x, hsy = _hs.y;
      if (Math.abs(sx - hsx) <= hitR && Math.abs(sy - hsy) <= hitR) {
        return { name: h.name, obj };
      }
    }
    return null;
  }

  /** Draw logo resize/rotate handles for selected turf with logo. */
  _drawTurfLogoHandles(scale) {
    if (this.activeTool !== 'select') return;
    if (this._turfLogoFocusedIdx == null) return;
    if (!this.selectedObjects || this.selectedObjects.size !== 1) return;
    const obj = [...this.selectedObjects][0];
    const handles = this._getTurfLogoHandles(obj);
    if (!handles) return;
    const logo = obj.turfLogos[this._turfLogoFocusedIdx];
    const ctx = this.ctx;
    const handleSize = this._handleSize / scale;
    // Draw dashed outline around all selected logos
    const selSet = this._turfLogoSelectedIdxs;
    if (obj.turfLogos && selSet && selSet.size > 0) {
      for (const si of selSet) {
        const sl = obj.turfLogos[si];
        if (!sl || !sl.width || !sl.height) continue;
        ctx.save();
        ctx.translate(obj.x, obj.y);
        ctx.rotate(obj.rotation || 0);
        ctx.translate(sl.x || 0, sl.y || 0);
        ctx.rotate(sl.rotation || 0);
        if (sl.type === 'ladder' || sl.type === 'dotdrill') {
          // Solid thick blue outline for ladders/dot drills (matches equipment selection style)
          ctx.strokeStyle = 'rgba(0, 107, 204, 0.8)';
          ctx.lineWidth = 3 / scale;
        } else {
          ctx.setLineDash([4 / scale, 4 / scale]);
          ctx.strokeStyle = si === this._turfLogoFocusedIdx ? 'rgba(0, 107, 204, 0.6)' : 'rgba(0, 107, 204, 0.35)';
          ctx.lineWidth = 1.5 / scale;
        }
        ctx.strokeRect(-sl.width / 2, -sl.height / 2, sl.width, sl.height);
        ctx.setLineDash([]);
        ctx.restore();
      }
    }
    // Snap lines when logo is centered on turf axis
    if (logo._snapX || logo._snapY) {
      const halfW = obj.width / 2, halfD = obj.depth / 2;
      ctx.save();
      ctx.translate(obj.x, obj.y);
      ctx.rotate(obj.rotation || 0);
      ctx.setLineDash([6 / scale, 4 / scale]);
      ctx.strokeStyle = 'rgba(0, 170, 0, 0.7)';
      ctx.lineWidth = 1 / scale;
      if (logo.type === 'ladder' || logo.type === 'dotdrill') {
        // Full turf-spanning crosshair lines through the field center (excluding sled lanes)
        const _fc = this._getTurfFieldCenter(obj);
        const fcX = _fc.x;
        const fcY = _fc.y;
        if (logo._snapX) {
          ctx.beginPath(); ctx.moveTo(fcX, -halfD); ctx.lineTo(fcX, halfD); ctx.stroke();
        }
        if (logo._snapY) {
          ctx.beginPath(); ctx.moveTo(-halfW, fcY); ctx.lineTo(halfW, fcY); ctx.stroke();
        }
        // 5-yard gap center snap line (parallel to field lines)
        if (logo._snapGapRunY != null) {
          const _gapV = logo._snapGapRunY;
          const _inset = (1 / scale) || 0;
          const _fL2 = -_cH + _inset + _slL;
          const _fR2 = _cH - _inset - _slR;
          if (_markRot) {
            // Run axis maps to turf X; cross axis line is vertical in turf space
            ctx.beginPath(); ctx.moveTo(_gapV, _fL2); ctx.lineTo(_gapV, _fR2); ctx.stroke();
          } else {
            // Run axis is turf Y; cross axis line is horizontal in turf space
            ctx.beginPath(); ctx.moveTo(_fL2, _gapV); ctx.lineTo(_fR2, _gapV); ctx.stroke();
          }
        }
      } else {
        // Crosshair lines through field center (accounts for sled lane offset)
        const _fc = this._getTurfFieldCenter(obj);
        if (logo._snapX) {
          ctx.beginPath(); ctx.moveTo(_fc.x, -halfD); ctx.lineTo(_fc.x, halfD); ctx.stroke();
        }
        if (logo._snapY) {
          ctx.beginPath(); ctx.moveTo(-halfW, _fc.y); ctx.lineTo(halfW, _fc.y); ctx.stroke();
        }
      }
      ctx.setLineDash([]);
      ctx.restore();
    }
    // Ladder-to-ladder gap measurement lines
    if (this._ladderSnapGaps && this._ladderSnapGaps.turfObj === obj) {
      const _lsg = this._ladderSnapGaps;
      ctx.save();
      ctx.translate(obj.x, obj.y);
      ctx.rotate(obj.rotation || 0);
      const _gapLineColor = this.darkMode ? 'rgba(80, 170, 255, 0.75)' : 'rgba(0, 90, 180, 0.65)';
      const _gapLabelColor = this.darkMode ? '#50aaff' : '#005ab4';
      for (const g of _lsg.gaps) {
        const e1 = g.e1, e2 = g.e2;
        ctx.strokeStyle = _gapLineColor;
        ctx.lineWidth = 1.5 / scale;
        if (g.gap > 0) {
          // Dashed line between edges
          ctx.setLineDash([2 / scale, 2 / scale]);
          ctx.beginPath(); ctx.moveTo(e1.x, e1.y); ctx.lineTo(e2.x, e2.y); ctx.stroke();
          ctx.setLineDash([]);
          // Nub marks at each edge
          const nubLen = Math.max(0.375 * 0.4, 4 / scale);
          const nubX = g.axis === 'y' ? 1 : 0;
          const nubY = g.axis === 'y' ? 0 : 1;
          ctx.beginPath();
          ctx.moveTo(e1.x - nubX * nubLen, e1.y - nubY * nubLen);
          ctx.lineTo(e1.x + nubX * nubLen, e1.y + nubY * nubLen);
          ctx.moveTo(e2.x - nubX * nubLen, e2.y - nubY * nubLen);
          ctx.lineTo(e2.x + nubX * nubLen, e2.y + nubY * nubLen);
          ctx.stroke();
          // Gap label at midpoint — drawn upright (screen-aligned)
          const lblTlx = (e1.x + e2.x) / 2;
          const lblTly = (e1.y + e2.y) / 2;
          const _oRot = obj.rotation || 0;
          const _cosO = Math.cos(_oRot), _sinO = Math.sin(_oRot);
          const lblWx = obj.x + lblTlx * _cosO - lblTly * _sinO;
          const lblWy = obj.y + lblTlx * _sinO + lblTly * _cosO;
          const lblOff = 16; // screen pixels
          // Nub direction in world space
          const _nwX = nubX * _cosO - nubY * _sinO;
          const _nwY = nubX * _sinO + nubY * _cosO;
          ctx.save();
          ctx.setTransform(1, 0, 0, 1, 0, 0); // reset to screen space
          const dpr = this._dpr || 1;
          const sx = (lblWx * scale + this.panX) * dpr;
          const sy = (lblWy * scale + this.panY) * dpr;
          const sFontSize = 11 + Math.min(7, Math.max(0, (scale - 20) / 12));
          ctx.font = `${sFontSize * dpr}px Arial`;
          ctx.fillStyle = _gapLabelColor;
          ctx.textAlign = _nwX > 0.5 ? 'left' : (_nwX < -0.5 ? 'right' : 'center');
          ctx.textBaseline = _nwY < -0.5 ? 'bottom' : (_nwY > 0.5 ? 'top' : 'middle');
          ctx.fillText(this.formatMeasurement(g.gap), sx + _nwX * lblOff * dpr, sy + _nwY * lblOff * dpr);
          ctx.restore();
        }
      }
      ctx.restore();
    }
    // Dimension labels during resize — always drawn upright (screen-aligned)
    if (logo._resizing) {
      const lw = logo.width, lh = logo.height;
      // Use tight (opaque) bounds when available (PNG/WebP with transparency)
      const tb = logo.tightBounds;
      const dispW = tb ? tb.w * lw : lw;
      const dispH = tb ? tb.h * lh : lh;
      const wLabel = this.formatMeasurement(dispW);
      const hLabel = this.formatMeasurement(dispH);
      const lr = logo.rotation || 0;
      const or = obj.rotation || 0;
      const cosL = Math.cos(lr), sinL = Math.sin(lr);
      const cosO = Math.cos(or), sinO = Math.sin(or);
      const lx = logo.x || 0, ly = logo.y || 0;
      const offsetW = 10 / scale; // world-space offset from edge
      // Helper: logo-local offset → world position
      const toWorld = (dx, dy) => {
        const tlx = lx + dx * cosL - dy * sinL;
        const tly = ly + dx * sinL + dy * cosL;
        return { x: obj.x + tlx * cosO - tly * sinO, y: obj.y + tlx * sinO + tly * cosO };
      };
      const sFontSize = 11, sPad = 3;
      // Helper: draw upright label at world position
      const drawLabel = (wx, wy, text) => {
        ctx.save();
        ctx.translate(wx, wy);
        ctx.scale(1 / scale, 1 / scale);
        ctx.font = `${sFontSize}px sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        const tm = ctx.measureText(text);
        const bgW = tm.width + sPad * 2, bgH = sFontSize + sPad * 2;
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(-bgW / 2, -bgH / 2, bgW, bgH);
        ctx.fillStyle = '#fff';
        ctx.fillText(text, 0, 0);
        ctx.restore();
      };
      // Width label — bottom edge center
      const wPos = toWorld(0, lh / 2 + offsetW);
      drawLabel(wPos.x, wPos.y, wLabel);
      // Height label — right edge center
      const hPos = toWorld(lw / 2 + offsetW, 0);
      drawLabel(hPos.x, hPos.y, hLabel);
    }
    // Corner handles
    for (const h of handles) {
      if (h.name === 'rotate') continue;
      const isActive = this._turfLogoDrag === h.name;
      const sz = isActive ? handleSize * 1.5 : handleSize;
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = isActive ? '#00aa00' : '#006BCC';
      ctx.lineWidth = 1.5 / scale;
      ctx.fillRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
      ctx.strokeRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
    }
    // Rotate handle — same style as equipment
    const rotH = handles.find(h => h.name === 'rotate');
    if (rotH) {
      const isActive = this._turfLogoDrag === 'rotate';
      const circR = isActive ? 9 / scale : 7 / scale;
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 4;
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = isActive ? '#00aa00' : '#006BCC';
      ctx.lineWidth = 2 / scale;
      ctx.beginPath(); ctx.arc(rotH.wx, rotH.wy, circR, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.restore();
      // Curved arrow
      ctx.strokeStyle = '#006BCC'; ctx.lineWidth = 1.2 / scale;
      const arrowR = circR * 0.55;
      ctx.beginPath(); ctx.arc(rotH.wx, rotH.wy, arrowR, -Math.PI * 0.8, Math.PI * 0.4); ctx.stroke();
      const tipAngle = Math.PI * 0.4;
      const tipX = rotH.wx + arrowR * Math.cos(tipAngle), tipY = rotH.wy + arrowR * Math.sin(tipAngle);
      const aLen = 2.5 / scale;
      ctx.beginPath();
      ctx.moveTo(tipX + aLen * Math.cos(tipAngle - 0.3), tipY + aLen * Math.sin(tipAngle - 0.3));
      ctx.lineTo(tipX, tipY);
      ctx.lineTo(tipX + aLen * Math.cos(tipAngle + Math.PI / 2 + 0.3), tipY + aLen * Math.sin(tipAngle + Math.PI / 2 + 0.3));
      ctx.stroke();
    }
  }

  _drawLightSource() {
    const dpr = this._dpr || 1;
    const cw = this._cssWidth || (this.canvas.width / dpr);
    const ch = this._cssHeight || (this.canvas.height / dpr);
    const zoom = this.zoomLevel || 1;
    // Quantize zoom factor to avoid re-rendering on tiny zoom changes
    const zf = Math.min(Math.max((1.5 - zoom) / 1.2, 0), 1);
    const zfQ = Math.round(zf * 20) / 20; // 5% steps
    const cacheKey = cw + ',' + ch + ',' + (this.darkMode ? 1 : 0) + ',' + zfQ;
    if (this._lightCacheKey !== cacheKey) {
      this._lightCacheKey = cacheKey;
      if (!this._lightCanvas) this._lightCanvas = document.createElement('canvas');
      const lc = this._lightCanvas;
      lc.width = cw; lc.height = ch;
      const lctx = lc.getContext('2d');
      lctx.clearRect(0, 0, cw, ch);
      const diag = Math.sqrt(cw * cw + ch * ch);
      // 1. Bright beam from top-right
      const bright = lctx.createRadialGradient(cw, 0, 0, cw, 0, diag * 0.85);
      if (this.darkMode) {
        bright.addColorStop(0, 'rgba(30,45,75,0.50)');
        bright.addColorStop(0.4, 'rgba(25,38,60,0.18)');
        bright.addColorStop(1, 'rgba(20,28,46,0)');
      } else {
        bright.addColorStop(0, 'rgba(255,255,255,0.22)');
        bright.addColorStop(0.4, 'rgba(255,255,255,0.08)');
        bright.addColorStop(1, 'rgba(255,255,255,0)');
      }
      lctx.fillStyle = bright;
      lctx.fillRect(0, 0, cw, ch);
      // 2. Shadow in bottom-left
      const blAlpha = this.darkMode ? (0.18 + zfQ * 0.22) : (0.04 + zfQ * 0.08);
      const bottomLeft = lctx.createRadialGradient(0, ch, 0, 0, ch, diag * 0.75);
      bottomLeft.addColorStop(0, `rgba(0,0,0,${blAlpha.toFixed(2)})`);
      bottomLeft.addColorStop(1, 'rgba(0,0,0,0)');
      lctx.fillStyle = bottomLeft;
      lctx.fillRect(0, 0, cw, ch);
      // 3. Corner vignettes
      const cAlpha = this.darkMode ? (0.06 + zfQ * 0.22) : (0.02 + zfQ * 0.08);
      const topLeft = lctx.createRadialGradient(0, 0, 0, 0, 0, diag * 0.55);
      topLeft.addColorStop(0, `rgba(0,0,0,${cAlpha.toFixed(2)})`);
      topLeft.addColorStop(1, 'rgba(0,0,0,0)');
      lctx.fillStyle = topLeft;
      lctx.fillRect(0, 0, cw, ch);
      const bottomRight = lctx.createRadialGradient(cw, ch, 0, cw, ch, diag * 0.55);
      bottomRight.addColorStop(0, `rgba(0,0,0,${cAlpha.toFixed(2)})`);
      bottomRight.addColorStop(1, 'rgba(0,0,0,0)');
      lctx.fillStyle = bottomRight;
      lctx.fillRect(0, 0, cw, ch);
    }
    // Blit cached light overlay
    this.ctx.save();
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.ctx.drawImage(this._lightCanvas, 0, 0);
    this.ctx.restore();
  }

  /* ── ROOM DRAWING ── */

  _roomGeoFingerprint(room) {
    let fp = '';
    for (const w of room.walls) fp += w.start.x + ',' + w.start.y + ',' + w.end.x + ',' + w.end.y + ';';
    if (room.cutouts) for (const c of room.cutouts) for (const w of c.walls) fp += w.start.x + ',' + w.start.y + ',' + w.end.x + ',' + w.end.y + ';';
    return fp;
  }

  _getRoomGeoCache(room) {
    const fp = this._roomGeoFingerprint(room);
    if (room._geoCache && room._geoCache.fp === fp) return room._geoCache;

    // Compute expensive geometry once and cache
    const roomPolygon = room.closed ? this.tracePolygon(room.walls) : null;
    const cutoutPolys = [];
    if (room.cutouts && room.cutouts.length > 0) {
      for (const cutout of room.cutouts) {
        const cp = this.tracePolygon(cutout.walls);
        if (cp && cp.length >= 3) cutoutPolys.push(cp);
        else { const pts = cutout.walls.map(w => w.start); if (pts.length >= 3) cutoutPolys.push(pts); }
      }
    }

    const visibleRoomWalls = this.getVisibleRoomWalls(room);
    const interiorCutoutData = this.getInteriorCutoutWalls(room);
    const cutoutCenterMap = new Map();
    const interiorCutoutWalls = interiorCutoutData.map(d => { cutoutCenterMap.set(d.wall, d.cutoutCenter); return d.wall; });
    const allVisibleWallsRaw = [...visibleRoomWalls, ...interiorCutoutWalls];
    // Remove duplicate/overlapping walls from coincident cutout edges (same fix as _rebuildRoomAfterSubtract)
    const dedupedRaw = this._removeCoincidentWalls(allVisibleWallsRaw, 0.02);
    const allVisibleWalls = room.closed ? this._getLargestComponent(dedupedRaw, roomPolygon) : dedupedRaw;

    // Cache connected components (O(W²) BFS)
    const wallComponents = this._getConnectedComponents(allVisibleWalls);

    // Detect interior holes — disconnected wall loops whose centroid is inside
    // the outer boundary (e.g. a rectangle cut from the middle of a room).
    const holePolygons = [];
    if (room.closed && wallComponents.length > 1 && roomPolygon && roomPolygon.length >= 3) {
      let bigIdx = 0, bigSize = 0;
      for (let c = 0; c < wallComponents.length; c++) {
        let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
        for (const w of wallComponents[c]) {
          mnX = Math.min(mnX, w.start.x, w.end.x); mnY = Math.min(mnY, w.start.y, w.end.y);
          mxX = Math.max(mxX, w.start.x, w.end.x); mxY = Math.max(mxY, w.start.y, w.end.y);
        }
        const s = (mxX - mnX) * (mxY - mnY);
        if (s > bigSize) { bigSize = s; bigIdx = c; }
      }
      for (let c = 0; c < wallComponents.length; c++) {
        if (c === bigIdx) continue;
        const poly = this.tracePolygon(wallComponents[c]);
        if (poly && poly.length >= 3) {
          let cx = 0, cy = 0;
          poly.forEach(p => { cx += p.x; cy += p.y; }); cx /= poly.length; cy /= poly.length;
          if (this.pointInPolygon(cx, cy, roomPolygon)) holePolygons.push(poly);
        }
      }
    }

    // Centroid / visual center (computed after holePolygons so labels avoid holes)
    const allExclusions = [...cutoutPolys, ...holePolygons];
    let centroidX = 0, centroidY = 0;
    if (room.walls.length > 0) {
      if (roomPolygon && roomPolygon.length >= 3) {
        const key = (p) => `${p.x.toFixed(4)},${p.y.toFixed(4)}`;
        const seen = new Set(); const cleanPoly = roomPolygon.filter(p => { const k = key(p); if (seen.has(k)) return false; seen.add(k); return true; });
        this._labelCutoutPolys = allExclusions;
        const vc = this.findVisualCenter(cleanPoly.length >= 3 ? cleanPoly : roomPolygon, allExclusions);
        this._labelCutoutPolys = null;
        centroidX = vc.x; centroidY = vc.y;
      } else {
        const pts = room.walls.map(w => w.start); pts.forEach(p => { centroidX += p.x; centroidY += p.y; }); centroidX /= pts.length; centroidY /= pts.length;
      }
    }

    // Cache merged collinear walls for labels (O(W³) worst case)
    const labelWallsTemp = { walls: allVisibleWalls.map(w => ({ start: { x: w.start.x, y: w.start.y }, end: { x: w.end.x, y: w.end.y } })) };
    this.mergeCollinearWalls(labelWallsTemp);
    const labelWalls = labelWallsTemp.walls;

    // Cache area calculation (O(G × C × P) grid sampling)
    let area = 0;
    if (room.closed && roomPolygon && roomPolygon.length >= 3) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const p of roomPolygon) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); }
      const step = 0.5; let count = 0;
      for (let x = minX + step / 2; x < maxX; x += step) {
        for (let y = minY + step / 2; y < maxY; y += step) {
          if (!this.pointInPolygon(x, y, roomPolygon)) continue;
          let inHole = false;
          for (const hp of allExclusions) { if (this.pointInPolygon(x, y, hp)) { inHole = true; break; } }
          if (!inHole) count++;
        }
      }
      area = count * step * step;
    }

    room._geoCache = { fp, roomPolygon, cutoutPolys, holePolygons, centroidX, centroidY, visibleRoomWalls, interiorCutoutData, cutoutCenterMap, interiorCutoutWalls, allVisibleWalls, wallComponents, labelWalls, area };
    return room._geoCache;
  }

  drawRoom(room, color) {
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    // Background trace mode: only active on Draw tab
    const overlay = window.roomDesignerOverlay;
    const tracing = this._bgTraceActive && (!overlay || overlay.currentStep === 'draw');

    // Fill closed rooms — temp canvas + destination-out for correct cutout rendering
    // Skip fill when tracing a background image so the floor plan stays visible
    if (room.closed && room.walls.length >= 3 && !tracing) {
      const fillGeo = this._getRoomGeoCache(room);
      const polygon = fillGeo.roomPolygon;
      if (polygon && polygon.length >= 3) {
        // Dim grid lines inside unfloored rooms (dark mode only)
        if (this._tc.roomGridDim) {
          this.ctx.fillStyle = this._tc.roomGridDim;
          this.ctx.beginPath();
          this.ctx.moveTo(polygon[0].x, polygon[0].y);
          for (let i = 1; i < polygon.length; i++) this.ctx.lineTo(polygon[i].x, polygon[i].y);
          this.ctx.closePath();
          this.ctx.fill();
        }
        if (fillGeo.cutoutPolys.length > 0 || fillGeo.holePolygons.length > 0) {
          // Use temp canvas to correctly handle cutouts and interior holes
          if (!this._tempCanvas || this._tempCanvas.width !== this.canvas.width || this._tempCanvas.height !== this.canvas.height) {
            this._tempCanvas = document.createElement('canvas');
            this._tempCanvas.width = this.canvas.width;
            this._tempCanvas.height = this.canvas.height;
          }
          const tctx = this._tempCanvas.getContext('2d');
          tctx.setTransform(1, 0, 0, 1, 0, 0);
          tctx.clearRect(0, 0, this._tempCanvas.width, this._tempCanvas.height);
          tctx.save();
          const tcDpr = this._dpr || 1;
          tctx.setTransform(tcDpr, 0, 0, tcDpr, 0, 0);
          tctx.translate(this.panX, this.panY);
          if (this._canvasRotationRad) tctx.rotate(this._canvasRotationRad);
          tctx.scale(scale, scale);
          // Fill room polygon on temp canvas (opaque)
          tctx.fillStyle = room.type === 'subtract' ? 'rgb(204, 0, 0)' : 'rgb(0, 107, 204)';
          tctx.beginPath();
          tctx.moveTo(polygon[0].x, polygon[0].y);
          for (let i = 1; i < polygon.length; i++) tctx.lineTo(polygon[i].x, polygon[i].y);
          tctx.closePath();
          tctx.fill();
          // Erase cutouts and interior holes
          tctx.globalCompositeOperation = 'destination-out';
          const allHoles = [...fillGeo.cutoutPolys, ...fillGeo.holePolygons];
          for (const hp of allHoles) {
            if (hp && hp.length >= 3) {
              tctx.fillStyle = 'rgba(0, 0, 0, 1)';
              tctx.beginPath();
              tctx.moveTo(hp[0].x, hp[0].y);
              for (let i = 1; i < hp.length; i++) tctx.lineTo(hp[i].x, hp[i].y);
              tctx.closePath();
              tctx.fill();
            }
          }
          tctx.restore();
          // Composite onto main canvas at 0.08 opacity
          this.ctx.save();
          this.ctx.setTransform(1, 0, 0, 1, 0, 0);
          this.ctx.globalAlpha = this._tc.roomFillAlpha;
          this.ctx.drawImage(this._tempCanvas, 0, 0);
          this.ctx.restore();
        } else {
          // No cutouts — simple fill
          this.ctx.fillStyle = room.type === 'subtract' ? this._tc.subtractFill : this._tc.roomFill;
          this.ctx.beginPath();
          this.ctx.moveTo(polygon[0].x, polygon[0].y);
          for (let i = 1; i < polygon.length; i++) this.ctx.lineTo(polygon[i].x, polygon[i].y);
          this.ctx.closePath();
          this.ctx.fill();
        }
      }
    }

    // Use cached geometry (polygons, centroid, visible walls, interior cutout data)
    const geo = this._getRoomGeoCache(room);
    const { roomPolygon, cutoutPolys: cachedCutoutPolys, centroidX, centroidY, cutoutCenterMap, allVisibleWalls } = geo;

    // On draw step, re-draw grid lines through room fill at reduced opacity
    // On equipment tab, suppress grid for floored rooms (opaque fill blocks grid)
    const _suppressGrid = this._equipmentMode && room.flooring;
    if (this.showGrid && !this._floorMode && !_suppressGrid && !tracing && room.closed && roomPolygon && roomPolygon.length >= 3) {
      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.moveTo(roomPolygon[0].x, roomPolygon[0].y);
      for (let i = 1; i < roomPolygon.length; i++) this.ctx.lineTo(roomPolygon[i].x, roomPolygon[i].y);
      this.ctx.closePath();
      this.ctx.clip();
      this.ctx.globalAlpha = this._equipmentMode ? 0.42 : 0.85;
      // Bounding box for efficient iteration
      let bxMin = Infinity, byMin = Infinity, bxMax = -Infinity, byMax = -Infinity;
      for (const p of roomPolygon) { if (p.x < bxMin) bxMin = p.x; if (p.y < byMin) byMin = p.y; if (p.x > bxMax) bxMax = p.x; if (p.y > byMax) byMax = p.y; }
      this.ctx.strokeStyle = this._tc.gridMajor;
      this.ctx.lineWidth = 1 / scale;
      this.ctx.beginPath();
      for (let x = Math.floor(bxMin); x <= Math.ceil(bxMax); x++) { this.ctx.moveTo(x, byMin); this.ctx.lineTo(x, byMax); }
      for (let y = Math.floor(byMin); y <= Math.ceil(byMax); y++) { this.ctx.moveTo(bxMin, y); this.ctx.lineTo(bxMax, y); }
      this.ctx.stroke();
      if ((scale / 12) >= 3) {
        this.ctx.strokeStyle = this._tc.gridMinor;
        this.ctx.lineWidth = 0.7 / scale;
        this.ctx.beginPath();
        for (let x = Math.floor(bxMin * 12) / 12; x <= bxMax; x += 1 / 12) { if (Math.abs(x - Math.round(x)) > 0.01) { this.ctx.moveTo(x, byMin); this.ctx.lineTo(x, byMax); } }
        for (let y = Math.floor(byMin * 12) / 12; y <= byMax; y += 1 / 12) { if (Math.abs(y - Math.round(y)) > 0.01) { this.ctx.moveTo(bxMin, y); this.ctx.lineTo(bxMax, y); } }
        this.ctx.stroke();
      }
      this.ctx.restore();
    }

    // Overlay hook — flooring strips render here (after fill, before walls)
    if (this._drawRoomOverlayHook) this._drawRoomOverlayHook(room, geo);

    // Snapshot canvas before walls so door gaps can restore the underlying content
    if (room.doors && room.doors.length > 0) {
      if (!this._doorSnapCanvas || this._doorSnapCanvas.width !== this.canvas.width || this._doorSnapCanvas.height !== this.canvas.height) {
        this._doorSnapCanvas = document.createElement('canvas');
        this._doorSnapCanvas.width = this.canvas.width;
        this._doorSnapCanvas.height = this.canvas.height;
      }
      this._doorSnapCanvas.getContext('2d').drawImage(this.canvas, 0, 0);
    }

    // Walls + doors always fully opaque (even in floor mode)
    const _dimThisRoom = false;

    // Draw walls
    this.ctx.strokeStyle = color;
    const wallPixelWidth = this.getWallPixelWidth(scale); this.ctx.lineWidth = wallPixelWidth / scale;
    const pointInRoomPolygon = (px, py) => {
      if (!roomPolygon || roomPolygon.length < 3) return false;
      let inside = false;
      for (let i = 0, j = roomPolygon.length - 1; i < roomPolygon.length; j = i++) {
        const xi = roomPolygon[i].x, yi = roomPolygon[i].y, xj = roomPolygon[j].x, yj = roomPolygon[j].y;
        if ((yi > py) !== (yj > py) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) inside = !inside;
      }
      return inside;
    };

    // Draw walls as connected paths — handle branching components (cached)
    const wallComponents = geo.wallComponents;
    this.ctx.strokeStyle = color;
    this.ctx.lineJoin = 'miter'; this.ctx.lineCap = 'square'; this.ctx.miterLimit = 10;
    for (const comp of wallComponents) {
      // Trace multiple paths through the component to cover all walls
      const undrawn = new Set(comp.map((_, i) => i));
      const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
      const startAdj = {}; const endAdj = {};
      comp.forEach((wall, idx) => {
        const sk = key(wall.start); const ek = key(wall.end);
        if (!startAdj[sk]) startAdj[sk] = []; if (!endAdj[ek]) endAdj[ek] = [];
        startAdj[sk].push(idx); endAdj[ek].push(idx);
      });

      while (undrawn.size > 0) {
        const startIdx = undrawn.values().next().value;
        undrawn.delete(startIdx);
        const path = [comp[startIdx].start, comp[startIdx].end];
        let current = key(comp[startIdx].end);

        while (true) {
          let found = false;
          const fromStart = startAdj[current] || [];
          for (const idx of fromStart) {
            if (undrawn.has(idx)) { undrawn.delete(idx); path.push(comp[idx].end); current = key(comp[idx].end); found = true; break; }
          }
          if (!found) {
            const fromEnd = endAdj[current] || [];
            for (const idx of fromEnd) {
              if (undrawn.has(idx)) { undrawn.delete(idx); path.push(comp[idx].start); current = key(comp[idx].start); found = true; break; }
            }
          }
          if (!found) break;
        }

        if (path.length >= 2) {
          const isClosed = Math.abs(path[0].x - path[path.length - 1].x) < 0.05 && Math.abs(path[0].y - path[path.length - 1].y) < 0.05;
          // Rooms that were closed once keep full wall thickness even if a wall is deleted
          const showThickness = isClosed || !!room._wasClosedOnce;

          // Determine outward direction by testing which side of the wall borders the blue fill
          let outwardSign = 1;
          if (showThickness && path.length >= 2) {
            const midX = (path[0].x + path[1].x) / 2, midY = (path[0].y + path[1].y) / 2;
            const dx = path[1].x - path[0].x, dy = path[1].y - path[0].y;
            const segLen = Math.sqrt(dx * dx + dy * dy) || 1;
            const testDist = 0.1;
            const t1x = midX + (-dy / segLen) * testDist, t1y = midY + (dx / segLen) * testDist;
            const t2x = midX - (-dy / segLen) * testDist, t2y = midY - (dx / segLen) * testDist;
            const isFilled = (tx, ty) => {
              if (!pointInRoomPolygon(tx, ty)) return false;
              if (cachedCutoutPolys) {
                for (const cp of cachedCutoutPolys) {
                  if (cp && cp.length >= 3 && this.pointInPolygon(tx, ty, cp)) return false;
                }
              }
              if (geo.holePolygons) {
                for (const hp of geo.holePolygons) {
                  if (this.pointInPolygon(tx, ty, hp)) return false;
                }
              }
              return true;
            };
            const s1 = isFilled(t1x, t1y), s2 = isFilled(t2x, t2y);
            if (s1 && !s2) outwardSign = -1;
            else if (!s1 && s2) outwardSign = 1;
            else {
              let signedArea = 0;
              for (let i = 0; i < path.length - 1; i++) {
                signedArea += (path[i].x * path[i + 1].y - path[i + 1].x * path[i].y);
              }
              outwardSign = signedArea > 0 ? -1 : 1;
            }
          }
          // Offset path helper (miter-joined parallel path at given distance)
          const offsetPath = (sign, dist) => {
            const pts = [];
            const n = path.length;
            for (let i = 0; i < n; i++) {
              const prev = (i > 0) ? i - 1 : (isClosed ? n - 2 : -1);
              const next = (i < n - 1) ? i + 1 : (isClosed ? 1 : -1);
              let nx = 0, ny = 0;
              if (prev >= 0 && next >= 0) {
                const d1x = path[i].x - path[prev].x, d1y = path[i].y - path[prev].y;
                const len1 = Math.sqrt(d1x * d1x + d1y * d1y) || 1;
                const n1x = -d1y / len1 * sign, n1y = d1x / len1 * sign;
                const d2x = path[next].x - path[i].x, d2y = path[next].y - path[i].y;
                const len2 = Math.sqrt(d2x * d2x + d2y * d2y) || 1;
                const n2x = -d2y / len2 * sign, n2y = d2x / len2 * sign;
                const dot = n1x * n2x + n1y * n2y;
                if (Math.abs(1 + dot) < 0.001) {
                  nx = n1x * dist; ny = n1y * dist;
                } else {
                  const mx = (n1x + n2x) / 2, my = (n1y + n2y) / 2;
                  const mLen = Math.sqrt(mx * mx + my * my) || 1;
                  const cosHalf = (mx * n1x + my * n1y) / mLen;
                  const miterLen = cosHalf !== 0 ? dist / cosHalf : dist;
                  const clampedLen = Math.min(miterLen, dist * 4);
                  nx = mx / mLen * clampedLen; ny = my / mLen * clampedLen;
                }
              } else if (prev >= 0) {
                const dx = path[i].x - path[prev].x, dy = path[i].y - path[prev].y;
                const len = Math.sqrt(dx * dx + dy * dy) || 1;
                nx = -dy / len * sign * dist; ny = dx / len * sign * dist;
              } else if (next >= 0) {
                const dx = path[next].x - path[i].x, dy = path[next].y - path[i].y;
                const len = Math.sqrt(dx * dx + dy * dy) || 1;
                nx = -dy / len * sign * dist; ny = dx / len * sign * dist;
              } else {
                nx = 0; ny = 0;
              }
              pts.push({ x: path[i].x + nx, y: path[i].y + ny });
            }
            return pts;
          };

          if (tracing) {
            // Trace mode: solid blue walls + white outline (ignores hollowWalls setting)
            const _trThin = 1.5 / scale;
            if (showThickness) {
              const outerPts = offsetPath(outwardSign, this.wallThickness);
              // Fill: wall polygon (inner forward, outer backward)
              this.ctx.beginPath();
              this.ctx.moveTo(path[0].x, path[0].y);
              for (let pi = 1; pi < path.length; pi++) this.ctx.lineTo(path[pi].x, path[pi].y);
              for (let pi = outerPts.length - 1; pi >= 0; pi--) this.ctx.lineTo(outerPts[pi].x, outerPts[pi].y);
              this.ctx.closePath();
              this.ctx.fillStyle = '#006BCC';
              this.ctx.fill();
              // Stroke inner and outer edges separately (avoids diagonal at seam)
              this.ctx.strokeStyle = '#ffffff';
              this.ctx.lineWidth = _trThin;
              this.ctx.beginPath();
              this.ctx.moveTo(path[0].x, path[0].y);
              for (let pi = 1; pi < path.length; pi++) this.ctx.lineTo(path[pi].x, path[pi].y);
              this.ctx.closePath();
              this.ctx.stroke();
              this.ctx.beginPath();
              this.ctx.moveTo(outerPts[0].x, outerPts[0].y);
              for (let pi = 1; pi < outerPts.length; pi++) this.ctx.lineTo(outerPts[pi].x, outerPts[pi].y);
              this.ctx.closePath();
              this.ctx.stroke();
            } else {
              // Open path (in-progress): just show centerline, no thickness
              // (outward direction unknown until room closes)
              this.ctx.strokeStyle = '#006BCC';
              this.ctx.lineWidth = 2 / scale;
              this.ctx.beginPath();
              this.ctx.moveTo(path[0].x, path[0].y);
              for (let pi = 1; pi < path.length; pi++) this.ctx.lineTo(path[pi].x, path[pi].y);
              this.ctx.stroke();
            }
          } else if (this.hollowWalls) {
            // Hollow walls: two thin parallel lines pushed away from fill
            const thinWidth = 1.5 / scale;
            this.ctx.lineWidth = thinWidth;
            if (showThickness) {
              const oPts = offsetPath(outwardSign, this.wallThickness);
              // Mask flooring inside hollow wall channels so floor doesn't bleed through
              // Skip in view-only/presentation mode — flooring fills the room and mask creates visible white seams
              if (room.flooring && window.roomDesignerOverlay && window.roomDesignerOverlay.showOverlay && !this._viewOnly) {
                this.ctx.fillStyle = this.darkMode ? '#141c2e' : '#ffffff';
                this.ctx.beginPath();
                this.ctx.moveTo(path[0].x, path[0].y);
                for (let pi = 1; pi < path.length; pi++) this.ctx.lineTo(path[pi].x, path[pi].y);
                for (let pi = oPts.length - 1; pi >= 0; pi--) this.ctx.lineTo(oPts[pi].x, oPts[pi].y);
                this.ctx.closePath();
                this.ctx.fill();
              }
              // Inner line: on the centerline (flush with blue fill edge)
              this.ctx.beginPath();
              this.ctx.moveTo(path[0].x, path[0].y);
              for (let pi = 1; pi < path.length; pi++) this.ctx.lineTo(path[pi].x, path[pi].y);
              this.ctx.closePath();
              this.ctx.stroke();
              // Outer line: offset full wallThickness outward
              this.ctx.beginPath();
              this.ctx.moveTo(oPts[0].x, oPts[0].y);
              for (let pi = 1; pi < oPts.length; pi++) this.ctx.lineTo(oPts[pi].x, oPts[pi].y);
              this.ctx.closePath();
              this.ctx.stroke();
            } else {
              // Open path (room under construction): just show centerline, no thickness
              this.ctx.beginPath();
              this.ctx.moveTo(path[0].x, path[0].y);
              for (let pi = 1; pi < path.length; pi++) this.ctx.lineTo(path[pi].x, path[pi].y);
              this.ctx.stroke();
            }
          } else {
            // Solid walls: thick stroke offset away from fill
            this.ctx.lineWidth = wallPixelWidth / scale;
            if (showThickness) {
              const halfT = this.wallThickness / 2;
              const solidPts = offsetPath(outwardSign, halfT);
              this.ctx.beginPath();
              this.ctx.moveTo(solidPts[0].x, solidPts[0].y);
              for (let pi = 1; pi < solidPts.length; pi++) this.ctx.lineTo(solidPts[pi].x, solidPts[pi].y);
              this.ctx.closePath();
              this.ctx.stroke();
            } else {
              // Open path: just show centerline, no thickness offset
              this.ctx.beginPath();
              this.ctx.moveTo(path[0].x, path[0].y);
              for (let pi = 1; pi < path.length; pi++) this.ctx.lineTo(path[pi].x, path[pi].y);
              this.ctx.stroke();
            }
          }
        }
      }
    }
    this.ctx.lineJoin = 'miter'; this.ctx.lineCap = 'butt';

    // Prepare wall labels (cached merged collinear walls)
    const labelWalls = geo.labelWalls;

    // Reuse cached cutout polygons for label side determination
    const pointInFilledArea = (px, py) => {
      if (!pointInRoomPolygon(px, py)) return false;
      for (const cp of cachedCutoutPolys) {
        let inside = false;
        for (let i = 0, j = cp.length - 1; i < cp.length; j = i++) {
          const xi = cp[i].x, yi = cp[i].y, xj = cp[j].x, yj = cp[j].y;
          if ((yi > py) !== (yj > py) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) inside = !inside;
        }
        if (inside) return false;
      }
      return true;
    };

    // Compute polygon winding for inner dimension calculation
    let windingSign = 1;
    if (roomPolygon && roomPolygon.length >= 3) {
      let sa2 = 0;
      for (let i = 0; i < roomPolygon.length; i++) {
        const j = (i + 1) % roomPolygon.length;
        sa2 += roomPolygon[i].x * roomPolygon[j].y - roomPolygon[j].x * roomPolygon[i].y;
      }
      windingSign = sa2 > 0 ? 1 : -1;
    }

    if (!this.showMeasurements && this._printMode) { /* skip wall label collection in print mode */ }
    else labelWalls.forEach(wall => {
      const midX = (wall.start.x + wall.end.x) / 2, midY = (wall.start.y + wall.end.y) / 2;
      let labelMidX = midX, labelMidY = midY;
      if (this._isMobile && this._selectHandleMode && this._selectHandleInfo) {
        const hInfo = this._selectHandleInfo;
        // Collect moving points from the original walls based on handle mode
        const mPts = [];
        if (this._selectHandleMode === 'wallGrab' && hInfo.wall) {
          mPts.push(hInfo.wall.start, hInfo.wall.end);
        } else if (this._selectHandleMode === 'wallEndpoint' && hInfo.wall) {
          mPts.push(hInfo.whichEnd === 'start' ? hInfo.wall.start : hInfo.wall.end);
        } else if (this._selectHandleMode === 'wallCorner' && hInfo.affectedWalls) {
          for (const aw of hInfo.affectedWalls) { mPts.push(aw.whichEnd === 'start' ? aw.wall.start : aw.wall.end); break; }
        }
        // Match label wall endpoints to moving points by coordinate proximity,
        // then bias toward moving point with a capped pixel offset (like rect drawing)
        const biasMaxPx = 50 / scale;
        for (const mp of mPts) {
          const dS = (wall.start.x - mp.x) ** 2 + (wall.start.y - mp.y) ** 2;
          const dE = (wall.end.x - mp.x) ** 2 + (wall.end.y - mp.y) ** 2;
          if (dS < 0.01 || dE < 0.01) {
            const near = dS < dE ? wall.start : wall.end;
            const far  = dS < dE ? wall.end : wall.start;
            const rawBiasX = (far.x - near.x) * 0.25;
            const rawBiasY = (far.y - near.y) * 0.25;
            const rawDist = Math.sqrt(rawBiasX * rawBiasX + rawBiasY * rawBiasY);
            if (rawDist > biasMaxPx && rawDist > 0) {
              const s = biasMaxPx / rawDist;
              labelMidX = near.x + rawBiasX * s;
              labelMidY = near.y + rawBiasY * s;
            } else {
              labelMidX = near.x + rawBiasX;
              labelMidY = near.y + rawBiasY;
            }
            break;
          }
        }
      }
      const distance = this.getDistance(wall.start, wall.end);
      const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y, len = Math.sqrt(dx * dx + dy * dy);
      if (len > 0) {
        const nx = -dy / len, ny = dx / len;
        const wdx = dx / len, wdy = dy / len;
        // Corner offsets for perimeter-to-perimeter junctions are zero —
        // labels show the drawn wall length. T-junction adjustments from
        // dividing walls / other rooms are handled via segBreaks below.
        let cornerOffStart = 0, cornerOffEnd = 0;
        // Find dividing wall intersections (wall-type segments only, skip lines)
        const segBreaks = [];
        const wallSHT = this.wallSegmentThickness / 2;
        for (const group of this.wallSegmentGroups) {
          if ((group.type || 'wall') === 'line') continue;
          for (const seg of group.segments) {
            const d2x = seg.end.x - seg.start.x, d2y = seg.end.y - seg.start.y;
            const cr = dx * d2y - dy * d2x;
            if (Math.abs(cr) > 1e-10) {
              const ox = seg.start.x - wall.start.x, oy = seg.start.y - wall.start.y;
              const t = (ox * d2y - oy * d2x) / cr;
              const u = (ox * dy - oy * dx) / cr;
              if (t * distance > 0.05 && (1 - t) * distance > 0.05 && u >= -0.02 && u <= 1.02) {
                segBreaks.push({ param: t, adjust: wallSHT });
              }
            }
            // Endpoint fallback — segment ends on this wall
            for (const ep of [seg.start, seg.end]) {
              const p = this.getWallParam(ep, wall);
              if (p * distance > 0.05 && (1 - p) * distance > 0.05) {
                const proj = this.paramToPoint(wall, p);
                if (this.getDistance(ep, proj) < 0.05) {
                  segBreaks.push({ param: p, adjust: wallSHT });
                }
              }
            }
          }
        }
        // Also check walls from other rooms for T-junctions
        const roomHalfWT = this.wallThickness / 2;
        for (const otherRoom of this.rooms) {
          if (otherRoom === room || !otherRoom.closed) continue;
          for (const ow of otherRoom.walls) {
            const d2x = ow.end.x - ow.start.x, d2y = ow.end.y - ow.start.y;
            const cr = dx * d2y - dy * d2x;
            if (Math.abs(cr) > 1e-10) {
              const ox = ow.start.x - wall.start.x, oy = ow.start.y - wall.start.y;
              const t = (ox * d2y - oy * d2x) / cr;
              const u = (ox * dy - oy * dx) / cr;
              if (t * distance > 0.05 && (1 - t) * distance > 0.05 && u >= -0.02 && u <= 1.02) {
                segBreaks.push({ param: t, adjust: roomHalfWT });
              }
            }
            for (const ep of [ow.start, ow.end]) {
              const p = this.getWallParam(ep, wall);
              if (p * distance > 0.05 && (1 - p) * distance > 0.05) {
                const proj = this.paramToPoint(wall, p);
                if (this.getDistance(ep, proj) < 0.05) {
                  segBreaks.push({ param: p, adjust: roomHalfWT });
                }
              }
            }
          }
        }
        // Place label on inside (filled) face
        const testDist = 1;
        const side1X = midX + nx * testDist, side1Y = midY + ny * testDist;
        const side2X = midX - nx * testDist, side2Y = midY - ny * testDist;
        let sign;
        const s1Filled = pointInFilledArea(side1X, side1Y), s2Filled = pointInFilledArea(side2X, side2Y);
        if (s1Filled && !s2Filled) sign = 1;
        else if (!s1Filled && s2Filled) sign = -1;
        else { const d1 = Math.sqrt((side1X - centroidX) ** 2 + (side1Y - centroidY) ** 2); const d2 = Math.sqrt((side2X - centroidX) ** 2 + (side2Y - centroidY) ** 2); sign = d1 > d2 ? -1 : 1; }
        // Emit label helper
        const emitWallLabel = (innerDist, lmx, lmy, wLen) => {
          const label = this.formatMeasurement(innerDist);
          const baseFontPx = this._printMode ? (innerDist < 3 ? 15 : 18) : (this.largeUI ? 14.5 : 12);
          const fontSize = baseFontPx / scale;
          this.ctx.font = `600 ${fontSize}px Arial`;
          const halfW = this.ctx.measureText(label).width / 2;
          const halfH = fontSize / 2;
          const pillPadX = 1 / scale, pillPadY = 2 / scale;
          const labelOffset = (halfW + pillPadX) * Math.abs(nx) + (halfH + pillPadY) * Math.abs(ny) + 4 / scale + this.wallThickness / 2;
          const lx = lmx + sign * nx * labelOffset, ly = lmy + sign * ny * labelOffset;
          this._pendingWallLabels.push({ x: lx, y: ly, text: label, fontSize, wallLength: wLen, halfW: halfW + pillPadX, halfH: halfH + pillPadY, color, sourceWall: wall });
        };
        if (segBreaks.length === 0) {
          // No dividing walls — single label (preserves mobile bias)
          emitWallLabel(distance - cornerOffStart - cornerOffEnd, labelMidX, labelMidY, distance);
        } else {
          // Build sorted, deduplicated break list
          segBreaks.sort((a, b) => a.param - b.param);
          const brks = [{ param: 0, adjust: cornerOffStart }];
          for (const sb of segBreaks) {
            const last = brks[brks.length - 1];
            if (sb.param - last.param > 0.005) brks.push(sb);
            else last.adjust = Math.max(last.adjust, sb.adjust);
          }
          if (1 - brks[brks.length - 1].param > 0.005) brks.push({ param: 1, adjust: cornerOffEnd });
          else brks[brks.length - 1].adjust = Math.max(brks[brks.length - 1].adjust, cornerOffEnd);
          // Emit a label for each sub-segment
          for (let bi = 0; bi < brks.length - 1; bi++) {
            const rawLen = (brks[bi + 1].param - brks[bi].param) * distance;
            const innerLen = rawLen - brks[bi].adjust - brks[bi + 1].adjust;
            if (innerLen < 0.05) continue;
            const mp = (brks[bi].param + brks[bi + 1].param) / 2;
            emitWallLabel(innerLen, wall.start.x + mp * dx, wall.start.y + mp * dy, rawLen);
          }
        }
      }
    });

    // Room label (deferred — pushed to _pendingRoomLabels for overlap avoidance)
    if (this.showRoomLabels && room.closed && room.walls.length >= 3) {
      let area = this.calculateArea(room);
      if (this._exteriorZoneAreaByRoom) {
        const extArea = this._exteriorZoneAreaByRoom.get(room);
        if (extArea) area += extArea;
      }
      if (area < 0.1) { /* skip label for fully subtracted / degenerate rooms */ }
      else {
      // Reuse cached visible walls + interior cutout edges for bounding box
      const visibleWalls = [...geo.visibleRoomWalls, ...geo.interiorCutoutWalls];
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      visibleWalls.forEach(w => { minX = Math.min(minX, w.start.x, w.end.x); minY = Math.min(minY, w.start.y, w.end.y); maxX = Math.max(maxX, w.start.x, w.end.x); maxY = Math.max(maxY, w.start.y, w.end.y); });
      if (minX === Infinity) { /* no visible walls remain */ }
      else {
      const roomCenterX = (minX + maxX) / 2;
      const nameFontSize = (this.largeUI ? 16 : 13) / scale, areaFontSize = (this.largeUI ? 14.5 : 12) / scale, lineHeight = nameFontSize * 1.3;
      const paddingX = 12 / scale, paddingY = 6 / scale, gap = 40 / scale + this.wallThickness;
      const aboveScreen = this.worldToScreen(0, minY - gap - lineHeight * 2);
      const defaultY = aboveScreen.y > 10 ? minY - gap - lineHeight * 0.5 : maxY + gap + lineHeight * 0.5;
      if (nameFontSize * scale > 3) {
        let nameText = room.name || ''; const areaText = this.formatArea(area);
        // Cap name width to room width so long names don't create oversized labels
        const roomWorldW = maxX - minX;
        const maxNameW = Math.max(roomWorldW * 0.9, 8); // 90% of room width, min 8ft
        this.ctx.font = `700 ${nameFontSize}px Arial`;
        if (this.ctx.measureText(nameText).width > maxNameW) {
          while (nameText.length > 1 && this.ctx.measureText(nameText + '\u2026').width > maxNameW) nameText = nameText.slice(0, -1);
          nameText = nameText + '\u2026';
        }
        const nameWidth = this.ctx.measureText(nameText).width;
        this.ctx.font = `${areaFontSize}px Arial`; const areaWidth = this.ctx.measureText(areaText).width;
        const maxTextWidth = Math.max(nameWidth, areaWidth);
        const boxW = maxTextWidth + paddingX * 2, boxH = (nameText ? lineHeight * 2 : lineHeight) + paddingY * 2;
        this._pendingRoomLabels.push({
          room, area, nameText, areaText, nameFontSize, areaFontSize, lineHeight,
          boxW, boxH, paddingX, paddingY, gap,
          minX, minY, maxX, maxY,
          roomCenterX, defaultY,
          centroidX, centroidY,
          aboveY: minY - gap - lineHeight * 0.5,
          belowY: maxY + gap + lineHeight * 0.5
        });
      }
      } // end minX !== Infinity
      } // end area >= 0.1
    }

    room.doors.forEach(door => this.drawDoor(door, room));

    // End floor-mode dimming for walls + doors
    if (_dimThisRoom) this.ctx.restore();

    // First point highlight
    if (!room.closed && room.walls.length > 0 && room === this.currentRoom) {
      const firstPoint = room.walls[0].start;
      this.ctx.fillStyle = room.type === 'subtract' ? '#CC4429' : '#006BCC';
      this.ctx.beginPath(); this.ctx.arc(firstPoint.x, firstPoint.y, 6 / scale, 0, Math.PI * 2); this.ctx.fill();
    }
  }

  /* ──────────────────────────────────────────
     HANDLE HELPERS (Resize / Rotate)
     ────────────────────────────────────────── */

  _hitTestHandles(sx, sy) {
    if (this.activeTool !== 'select') return null;
    if (this.selectedObjects.size < 1) return null;
    const hitR = this._isMobile ? 20 : 14; // Generous hit zone for handles
    // Multi-equipment/platform selection: single group rotate handle
    const groupableObjs = [...this.selectedObjects].filter(o => o.type === 'equipment' || o.type === 'platform');
    if (groupableObjs.length > 1) {
      const gh = this._getGroupRotateHandle(groupableObjs);
      if (gh) {
        const gs = this.worldToScreen(gh.wx, gh.wy);
        if ((sx - gs.x) ** 2 + (sy - gs.y) ** 2 <= hitR * hitR) {
          return { type: 'rotate', group: true };
        }
      }
      return null; // No per-object handles when group selected
    }
    for (const obj of this.selectedObjects) {
      if (obj.type === 'turf' && this._turfLogoFocusedIdx != null) continue;
      // Rotate handle first (drawn on top)
      const rotH = this._getRotateHandle(obj);
      if (rotH) {
        const rs = this.worldToScreen(rotH.wx, rotH.wy);
        if ((sx - rs.x) ** 2 + (sy - rs.y) ** 2 <= hitR * hitR) {
          return { type: 'rotate', obj };
        }
      }
      // Resize handles (not for equipment in equipment mode)
      if (this._equipmentMode && obj.type === 'equipment') continue;
      const handles = this._getResizeHandles(obj);
      for (const h of handles) {
        const hs = this.worldToScreen(h.wx, h.wy);
        if ((sx - hs.x) ** 2 + (sy - hs.y) ** 2 <= hitR * hitR) {
          return { type: 'resize', obj, handle: h.name };
        }
      }
    }
    return null;
  }

  _getGroupRotateHandle(equipObjs, mouseWorld) {
    if (!equipObjs || equipObjs.length < 2) return null;
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const stemWorld = 20 / scale;
    // During active rotation: use frozen centroid + circumR from _selectRotateInfo
    const gi = this._selectRotating && this._selectRotateInfo && this._selectRotateInfo.group ? this._selectRotateInfo : null;
    if (mouseWorld && gi) {
      const circumR = gi.initCircumR + stemWorld;
      const dx = mouseWorld.x - gi.cx, dy = mouseWorld.y - gi.cy;
      const angle = Math.atan2(dy, dx);
      return { wx: gi.cx + Math.cos(angle) * circumR, wy: gi.cy + Math.sin(angle) * circumR };
    }
    // At rest: compute current bounding box using tight bounds
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const obj of equipObjs) {
      const tw = obj.tightWidth || obj.width, td = obj.tightDepth || obj.depth;
      const tox = obj.tightOffsetX || 0, toy = obj.tightOffsetY || 0;
      const hw = tw / 2, hd = td / 2;
      const rot = obj.rotation || 0;
      for (const [lx, ly] of [[-hw + tox, -hd + toy],[hw + tox, -hd + toy],[hw + tox, hd + toy],[-hw + tox, hd + toy]]) {
        const p = this._rotatePoint(obj.x + lx, obj.y + ly, obj.x, obj.y, rot);
        if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
      }
    }
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    // Use distance from center to top edge + stem — keeps handle tight above topmost item
    const topOffset = cy - minY;
    const circumR = topOffset + stemWorld;
    return { wx: cx, wy: cy - circumR };
  }

  _drawHandles(scale, skipTurf) {
    if (this.activeTool !== 'select') return;
    if (this.selectedObjects.size < 1) return;
    const ctx = this.ctx;
    const handleSize = this._handleSize / scale;
    // Multi-equipment/platform: draw single group rotate handle instead of per-object handles
    const groupableObjs = [...this.selectedObjects].filter(o => o.type === 'equipment' || o.type === 'platform');
    if (groupableObjs.length > 1) {
      const isActive = this._selectRotating && this._selectRotateInfo && this._selectRotateInfo.group;
      const gh = this._getGroupRotateHandle(groupableObjs, isActive ? this.mouseWorldRaw : undefined);
      if (gh) {
        // Circle
        const circR = isActive ? 9 / scale : 7 / scale;
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 4;
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = isActive ? '#00aa00' : '#006BCC';
        ctx.lineWidth = 2 / scale;
        ctx.beginPath(); ctx.arc(gh.wx, gh.wy, circR, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.restore();
        // Curved arrow
        ctx.strokeStyle = '#006BCC'; ctx.lineWidth = 1.2 / scale;
        const arrowR = circR * 0.55;
        ctx.beginPath(); ctx.arc(gh.wx, gh.wy, arrowR, -Math.PI * 0.8, Math.PI * 0.4); ctx.stroke();
        const tipAngle = Math.PI * 0.4;
        const tipX = gh.wx + arrowR * Math.cos(tipAngle), tipY = gh.wy + arrowR * Math.sin(tipAngle);
        const aLen = 2.5 / scale;
        ctx.beginPath();
        ctx.moveTo(tipX + aLen * Math.cos(tipAngle - 0.3), tipY + aLen * Math.sin(tipAngle - 0.3));
        ctx.lineTo(tipX, tipY);
        ctx.lineTo(tipX + aLen * Math.cos(tipAngle + Math.PI / 2 + 0.3), tipY + aLen * Math.sin(tipAngle + Math.PI / 2 + 0.3));
        ctx.stroke();
      }
      return; // No per-object handles for multi-equipment
    }
    for (const obj of this.selectedObjects) {
      // Skip turf handles entirely on Customize tab (select-only, no resize/rotate/move)
      if (obj.type === 'turf' && skipTurf) continue;
      // Skip turf body handles when logo is focused
      if (obj.type === 'turf' && this._turfLogoFocusedIdx != null) continue;
      // Don't draw handles for doors
      if (obj.type === 'door') continue;

      // Skip resize handles for equipment and platforms (only rotate)
      if (obj.type === 'equipment' || obj.type === 'platform') {
        const isRotSingle = this._selectRotating && this._selectRotateInfo && this._selectRotateInfo.obj === obj;
        const rotH = this._getRotateHandle(obj, isRotSingle ? this.mouseWorldRaw : undefined);
        if (rotH) {
          const isActivatedRotate = this._selectHandleMode === 'rotate' && this._selectHandleInfo && this._selectHandleInfo.obj === obj;
          const isCyclePreviewRotate = this._isCycleHighlighted('rotate', { obj });
          const isActiveRotate = isActivatedRotate || isCyclePreviewRotate;
          // Circle
          const circR = isActiveRotate ? 9 / scale : 7 / scale;
          ctx.save();
          ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 4;
          ctx.fillStyle = '#fff';
          ctx.strokeStyle = (isActivatedRotate || isCyclePreviewRotate) ? '#00aa00' : '#006BCC';
          ctx.lineWidth = 2 / scale;
          ctx.beginPath(); ctx.arc(rotH.wx, rotH.wy, circR, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
          ctx.restore();
          ctx.strokeStyle = '#006BCC'; ctx.lineWidth = 1.2 / scale;
          const arrowR = circR * 0.55;
          ctx.beginPath(); ctx.arc(rotH.wx, rotH.wy, arrowR, -Math.PI * 0.8, Math.PI * 0.4); ctx.stroke();
          const tipAngle = Math.PI * 0.4;
          const tipX = rotH.wx + arrowR * Math.cos(tipAngle), tipY = rotH.wy + arrowR * Math.sin(tipAngle);
          const aLen = 2.5 / scale;
          ctx.beginPath();
          ctx.moveTo(tipX + aLen * Math.cos(tipAngle - 0.3), tipY + aLen * Math.sin(tipAngle - 0.3));
          ctx.lineTo(tipX, tipY);
          ctx.lineTo(tipX + aLen * Math.cos(tipAngle + Math.PI / 2 + 0.3), tipY + aLen * Math.sin(tipAngle + Math.PI / 2 + 0.3));
          ctx.stroke();
        }
        continue;
      }

      const handles = this._getResizeHandles(obj);

      // Draw resize handles
      for (const h of handles) {
        const isActivatedResize = this._selectHandleMode === 'resize' && this._selectHandleInfo &&
          this._selectHandleInfo.obj === obj && this._selectHandleInfo.handle === h.name;
        const isCyclePreviewResize = this._isCycleHighlighted('resize', { obj, handle: h.name });
        const isActiveResize = isActivatedResize || isCyclePreviewResize;
        const sz = isActiveResize ? handleSize * 1.5 : handleSize;
        if (isActivatedResize) {
          ctx.save();
          ctx.shadowColor = '#00cc00'; ctx.shadowBlur = 8 / scale;
          ctx.fillStyle = 'rgba(0, 200, 0, 0.3)';
          ctx.fillRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
          ctx.restore();
          ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2 / scale;
          ctx.strokeRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
        } else if (isCyclePreviewResize) {
          ctx.fillStyle = '#fff';
          ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2.5 / scale;
          ctx.fillRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
          ctx.strokeRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
        } else {
          ctx.fillStyle = '#fff';
          ctx.strokeStyle = '#006BCC';
          ctx.lineWidth = 1.5 / scale;
          ctx.fillRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
          ctx.strokeRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
        }
      }

      // Draw center move handle (mobile only)
      if (this._isMobile) {
        const isActivatedMove = this._selectHandleMode === 'objectMove' && this._selectHandleInfo && this._selectHandleInfo.obj === obj;
        const isCyclePreviewMove = this._isCycleHighlighted('objectMove', { obj });
        const isActiveMove = isActivatedMove || isCyclePreviewMove;
        const moveR = isActiveMove ? handleSize * 0.9 : handleSize * 0.6;
        if (isActivatedMove) {
          ctx.save();
          ctx.shadowColor = '#00cc00'; ctx.shadowBlur = 8 / scale;
          ctx.fillStyle = 'rgba(0, 200, 0, 0.3)';
          ctx.beginPath(); ctx.arc(obj.x, obj.y, moveR, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
          ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2 / scale;
          ctx.beginPath(); ctx.arc(obj.x, obj.y, moveR, 0, Math.PI * 2); ctx.stroke();
        } else if (isCyclePreviewMove) {
          ctx.fillStyle = '#fff';
          ctx.beginPath(); ctx.arc(obj.x, obj.y, moveR, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2.5 / scale;
          ctx.beginPath(); ctx.arc(obj.x, obj.y, moveR, 0, Math.PI * 2); ctx.stroke();
        } else {
          ctx.fillStyle = '#fff';
          ctx.strokeStyle = '#006BCC'; ctx.lineWidth = 1.5 / scale;
          ctx.beginPath(); ctx.arc(obj.x, obj.y, moveR, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(obj.x, obj.y, moveR, 0, Math.PI * 2); ctx.stroke();
        }
      }

      // Draw rotate handle
      const isRotThisObj = this._selectRotating && this._selectRotateInfo && this._selectRotateInfo.obj === obj;
      const rotH = this._getRotateHandle(obj, isRotThisObj ? this.mouseWorldRaw : undefined);
      if (rotH) {
        const isActivatedRotate = this._selectHandleMode === 'rotate' && this._selectHandleInfo && this._selectHandleInfo.obj === obj;
        const isCyclePreviewRotate = this._isCycleHighlighted('rotate', { obj });
        const isActiveRotate = isActivatedRotate || isCyclePreviewRotate;
        // Circle
        const circR = isActiveRotate ? 7 / scale : 5 / scale;
        if (isActivatedRotate) {
          ctx.save();
          ctx.shadowColor = '#00cc00'; ctx.shadowBlur = 8 / scale;
          ctx.fillStyle = 'rgba(0, 200, 0, 0.3)';
          ctx.beginPath(); ctx.arc(rotH.wx, rotH.wy, circR, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
          ctx.strokeStyle = '#00aa00';
        } else if (isCyclePreviewRotate) {
          ctx.fillStyle = '#fff';
          ctx.beginPath(); ctx.arc(rotH.wx, rotH.wy, circR, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = '#00aa00';
        } else {
          ctx.fillStyle = '#fff';
          ctx.strokeStyle = '#006BCC';
        }
        ctx.lineWidth = 1.5 / scale;
        ctx.beginPath();
        ctx.arc(rotH.wx, rotH.wy, circR, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Curved arrow inside circle
        ctx.strokeStyle = '#006BCC';
        ctx.lineWidth = 1 / scale;
        const arrowR = circR * 0.55;
        ctx.beginPath();
        ctx.arc(rotH.wx, rotH.wy, arrowR, -Math.PI * 0.8, Math.PI * 0.4);
        ctx.stroke();
        // Arrowhead
        const tipAngle = Math.PI * 0.4;
        const tipX = rotH.wx + arrowR * Math.cos(tipAngle);
        const tipY = rotH.wy + arrowR * Math.sin(tipAngle);
        const aLen = 2.5 / scale;
        ctx.beginPath();
        ctx.moveTo(tipX + aLen * Math.cos(tipAngle - 0.3), tipY + aLen * Math.sin(tipAngle - 0.3));
        ctx.lineTo(tipX, tipY);
        ctx.lineTo(tipX + aLen * Math.cos(tipAngle + Math.PI / 2 + 0.3), tipY + aLen * Math.sin(tipAngle + Math.PI / 2 + 0.3));
        ctx.stroke();
      }

      // Draw live dimension label during resize (skip turf — edge labels handle it)
      if (this._selectResizing && this._selectResizeInfo && this._selectResizeInfo.obj === obj && obj.type !== 'turf') {
        const label = (obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf')
          ? `${this.formatMeasurement(obj.width)} × ${this.formatMeasurement(obj.depth)}`
          : `⌀ ${this.formatMeasurement(obj.diameter)}`;
        const fontSize = 12 / scale;
        ctx.font = `600 ${fontSize}px Arial`;
        const tw = ctx.measureText(label).width;
        const padX = 4 / scale, padY = 2 / scale;
        const lx = obj.x, ly = obj.y - ((obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf') ? obj.depth / 2 : obj.diameter / 2) - 18 / scale;
        ctx.fillStyle = 'rgba(0, 107, 204, 0.9)';
        const cornerR = 3 / scale;
        const bx = lx - tw / 2 - padX, by = ly - fontSize / 2 - padY;
        const bw = tw + padX * 2, bh = fontSize + padY * 2;
        ctx.beginPath();
        ctx.moveTo(bx + cornerR, by); ctx.lineTo(bx + bw - cornerR, by);
        ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + cornerR);
        ctx.lineTo(bx + bw, by + bh - cornerR);
        ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - cornerR, by + bh);
        ctx.lineTo(bx + cornerR, by + bh);
        ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - cornerR);
        ctx.lineTo(bx, by + cornerR);
        ctx.quadraticCurveTo(bx, by, bx + cornerR, by);
        ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(label, lx, ly);
        ctx.textAlign = 'start'; ctx.textBaseline = 'alphabetic';
      }
    }
  }

  /* ── WALL ENDPOINT DOTS ── */

  _hitTestWallEndpoints(sx, sy) {
    if (this.activeTool !== 'select') return null;
    if (this.selectedWalls.size < 1) return null;
    const hitR = this._hitRadius;
    for (const wall of this.selectedWalls) {
      const room = this._findRoomForWall(wall);
      if (!room) continue;
      // Skip cutout walls — their endpoints aren't independently draggable
      if (room.cutouts && room.cutouts.some(c => c.walls.includes(wall))) continue;
      for (const whichEnd of this._getWallOpenEndpoints(wall, room)) {
        const pt = whichEnd === 'start' ? wall.start : wall.end;
        const sp = this.worldToScreen(pt.x, pt.y);
        if ((sx - sp.x) ** 2 + (sy - sp.y) ** 2 <= hitR * hitR) return { wall, room, whichEnd, pt };
      }
    }
    return null;
  }

  _drawWallEndpointDots(scale) {
    if (this.activeTool !== 'select') return;
    if (this.selectedWalls.size < 1) return;
    const ctx = this.ctx;
    for (const wall of this.selectedWalls) {
      const room = this._findRoomForWall(wall);
      if (!room) continue;
      // Skip cutout walls — their endpoints aren't independently draggable
      if (room.cutouts && room.cutouts.some(c => c.walls.includes(wall))) continue;
      for (const whichEnd of this._getWallOpenEndpoints(wall, room)) {
        const pt = whichEnd === 'start' ? wall.start : wall.end;
        const hoverR = this._hitRadius;
        const isActivatedEndpoint = this._selectHandleMode === 'wallEndpoint' && this._selectHandleInfo &&
          this._selectHandleInfo.wall === wall && this._selectHandleInfo.whichEnd === whichEnd;
        const isCyclePreviewEndpoint = this._isCycleHighlighted('wallEndpoint', { wall, whichEnd });
        const isActive = isActivatedEndpoint || isCyclePreviewEndpoint;
        const isHover = !isActive && this.mouseScreen && !this._wallEndpointDragging && (() => {
          const sp = this.worldToScreen(pt.x, pt.y);
          return (this.mouseScreen.x - sp.x) ** 2 + (this.mouseScreen.y - sp.y) ** 2 <= hoverR * hoverR;
        })();
        const dotBase = this._endpointDotSize;
        const r = ((isHover || isActive) ? dotBase * 1.5 : dotBase) / scale;
        if (isActivatedEndpoint) {
          // Green glow ring for activated handle
          ctx.save();
          ctx.shadowColor = '#00cc00'; ctx.shadowBlur = 8 / scale;
          ctx.fillStyle = 'rgba(0, 200, 0, 0.7)';
          ctx.beginPath(); ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
          ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2 / scale;
          ctx.beginPath(); ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2); ctx.stroke();
        } else if (isCyclePreviewEndpoint) {
          // Green outline for cycling preview
          ctx.fillStyle = 'rgba(0, 107, 204, 0.4)';
          ctx.beginPath(); ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2.5 / scale;
          ctx.beginPath(); ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2); ctx.stroke();
        } else {
          ctx.fillStyle = isHover ? 'rgba(0, 200, 0, 0.7)' : 'rgba(0, 107, 204, 0.4)';
          ctx.beginPath(); ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = isHover ? '#00aa00' : '#006BCC'; ctx.lineWidth = 1.5 / scale;
          ctx.beginPath(); ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2); ctx.stroke();
        }
      }
    }
    // During endpoint drag: dashed preview line + snap indicator
    if (this._wallEndpointDragging && this._wallEndpointDragInfo) {
      const info = this._wallEndpointDragInfo;
      const anchor = info.anchor;
      const current = info.current;
      // Dashed preview line from anchor to current drag position
      ctx.strokeStyle = 'rgba(0, 107, 204, 0.5)';
      ctx.lineWidth = 2 / scale;
      ctx.setLineDash([4 / scale, 4 / scale]);
      ctx.beginPath(); ctx.moveTo(anchor.x, anchor.y); ctx.lineTo(current.x, current.y); ctx.stroke();
      ctx.setLineDash([]);
      // Green snap indicator if snapped to an open endpoint
      if (info.snapTarget) {
        ctx.fillStyle = 'rgba(0, 200, 0, 0.7)';
        ctx.beginPath(); ctx.arc(info.snapTarget.x, info.snapTarget.y, 6 / scale, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2 / scale;
        ctx.beginPath(); ctx.arc(info.snapTarget.x, info.snapTarget.y, 6 / scale, 0, Math.PI * 2); ctx.stroke();
      }
    }
    // Mobile wallEndpoint snap indicator
    if (this._selectHandleMode === 'wallEndpoint' && this._selectHandleInfo && this._selectHandleInfo.snapTarget) {
      const st = this._selectHandleInfo.snapTarget;
      ctx.fillStyle = 'rgba(0, 200, 0, 0.7)';
      ctx.beginPath(); ctx.arc(st.x, st.y, 6 / scale, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2 / scale;
      ctx.beginPath(); ctx.arc(st.x, st.y, 6 / scale, 0, Math.PI * 2); ctx.stroke();
    }
  }

  _findSnapEndpoint(pos, excludeWall, excludeRoom) {
    const threshold = 4 / 12; // 4 inches in feet
    let best = null, bestDist = threshold;
    const checkRoom = (room) => {
      if (!room || room.closed) return;
      const endpoints = this.getOpenEndpoints(room);
      for (const ep of endpoints) {
        // Skip if this is one of the excluded wall's endpoints
        if (excludeWall && room === excludeRoom) {
          if (Math.abs(ep.x - excludeWall.start.x) < 0.001 && Math.abs(ep.y - excludeWall.start.y) < 0.001) continue;
          if (Math.abs(ep.x - excludeWall.end.x) < 0.001 && Math.abs(ep.y - excludeWall.end.y) < 0.001) continue;
        }
        const d = this.getDistance(pos, ep);
        if (d < bestDist) { bestDist = d; best = { x: ep.x, y: ep.y, room }; }
      }
    };
    this.rooms.forEach(checkRoom);
    if (this.currentRoom) checkRoom(this.currentRoom);
    return best;
  }

  /* ── WALL GRAB HANDLE ── */

  _getWallGrabHandle(wall, room) {
    // Disable grab handles on rooms with cutouts — dragging individual walls corrupts
    // the geometry because the cutout overlay data doesn't update with the wall move.
    // Will be unnecessary once Option B (room rebuild after subtract) is implemented.
    if (room && room.cutouts && room.cutouts.length > 0) return null;
    let seg = wall;
    const mx = (seg.start.x + seg.end.x) / 2;
    const my = (seg.start.y + seg.end.y) / 2;
    if (room && room.closed) {
      const wdx = seg.end.x - seg.start.x, wdy = seg.end.y - seg.start.y;
      const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
      const perpX = wLen > 0 ? -wdy / wLen : 0, perpY = wLen > 0 ? wdx / wLen : 0;
      // Pass the visible segment for accurate outward sign calculation
      const off = this._getDoorOffset(seg, perpX, perpY, room);
      return { wx: mx + off.x, wy: my + off.y };
    }
    return { wx: mx, wy: my };
  }

  _hitTestWallGrabHandle(sx, sy) {
    if (this.activeTool !== 'select') return null;
    if (this.selectedWalls.size < 1) return null;
    const hitR = this._hitRadius;
    for (const wall of this.selectedWalls) {
      const room = this._findRoomForWall(wall);
      const mid = this._getWallGrabHandle(wall, room);
      if (!mid) continue; // wall has no visible segments (hidden by cutout)
      const sp = this.worldToScreen(mid.wx, mid.wy);
      if ((sx - sp.x) ** 2 + (sy - sp.y) ** 2 <= hitR * hitR) return { wall, mid };
    }
    return null;
  }

  _drawWallGrabHandle(scale) {
    if (this.activeTool !== 'select') return;
    if (this.selectedWalls.size < 1) return;
    const ctx = this.ctx;
    const handleSize = this._handleSize / scale;
    for (const wall of this.selectedWalls) {
      const room = this._findRoomForWall(wall);
      const mid = this._getWallGrabHandle(wall, room);
      if (!mid) continue; // wall has no visible segments
      const isActivatedGrab = this._selectHandleMode === 'wallGrab' && this._selectHandleInfo && this._selectHandleInfo.wall === wall;
      const isCyclePreviewGrab = this._isCycleHighlighted('wallGrab', { wall });
      const isActive = isActivatedGrab || isCyclePreviewGrab;
      const sz = isActive ? handleSize * 1.5 : handleSize;
      if (isActivatedGrab) {
        ctx.save();
        ctx.shadowColor = '#00cc00'; ctx.shadowBlur = 8 / scale;
        ctx.fillStyle = 'rgba(0, 200, 0, 0.3)';
        ctx.fillRect(mid.wx - sz / 2, mid.wy - sz / 2, sz, sz);
        ctx.restore();
        ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2 / scale;
        ctx.strokeRect(mid.wx - sz / 2, mid.wy - sz / 2, sz, sz);
      } else if (isCyclePreviewGrab) {
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2.5 / scale;
        ctx.fillRect(mid.wx - sz / 2, mid.wy - sz / 2, sz, sz);
        ctx.strokeRect(mid.wx - sz / 2, mid.wy - sz / 2, sz, sz);
      } else {
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = '#006BCC';
        ctx.lineWidth = 1.5 / scale;
        ctx.fillRect(mid.wx - sz / 2, mid.wy - sz / 2, sz, sz);
        ctx.strokeRect(mid.wx - sz / 2, mid.wy - sz / 2, sz, sz);
      }
    }
  }

  /* ── WALL CORNER HANDLES ── */

  _getWallCornerHandles(wall, room) {
    if (!room) return [];
    // Disable corner handles on rooms with cutouts — dragging corners corrupts
    // the geometry because the cutout overlay data doesn't update with the move.
    // Will be unnecessary once Option B (room rebuild after subtract) is implemented.
    if (room.cutouts && room.cutouts.length > 0) return [];
    // Original logic for rooms without cutouts
    const openEnds = this._getWallOpenEndpoints(wall, room);
    const handles = [];
    for (const whichEnd of ['start', 'end']) {
      if (openEnds.includes(whichEnd)) continue;
      const pt = wall[whichEnd];
      if (room.closed) {
        handles.push({ whichEnd, ...this._getOffsetCorner(pt, wall, room) });
      } else {
        handles.push({ whichEnd, wx: pt.x, wy: pt.y });
      }
    }
    return handles;
  }

  _getOffsetCorner(pt, wall, room) {
    const halfT = this.wallThickness / 2;
    const EPS = 0.05;
    // Current wall perpendicular + outward sign
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy) || 1;
    const perpX = -wdy / wLen, perpY = wdx / wLen;
    const outward = this._getWallOutwardSign(wall, perpX, perpY, room);
    const n1x = perpX * outward, n1y = perpY * outward;
    // Find the other wall sharing this endpoint
    // For rooms with cutouts, search the visible outline (allVisibleWalls) to find
    // adjacent walls at cutout-created corners; otherwise search room.walls
    let otherWall = null;
    let searchWalls;
    if (room.cutouts && room.cutouts.length > 0) {
      const geo = this._getRoomGeoCache(room);
      searchWalls = geo.allVisibleWalls || room.walls;
    } else {
      searchWalls = room.walls;
    }
    for (const w of searchWalls) {
      if (w === wall) continue;
      // Skip collinear walls on the same line (visible segments of the same original wall)
      const swdx = w.end.x - w.start.x, swdy = w.end.y - w.start.y;
      const sLen = Math.sqrt(swdx * swdx + swdy * swdy);
      if (sLen > EPS) {
        const cross = Math.abs(wdx * swdy - wdy * swdx);
        if (cross < EPS * wLen) {
          const dx = w.start.x - wall.start.x, dy = w.start.y - wall.start.y;
          const perpD = Math.abs(-wdy / wLen * dx + wdx / wLen * dy);
          if (perpD < EPS) continue; // same line, skip
        }
      }
      if ((Math.abs(w.start.x - pt.x) < EPS && Math.abs(w.start.y - pt.y) < EPS) ||
          (Math.abs(w.end.x - pt.x) < EPS && Math.abs(w.end.y - pt.y) < EPS)) {
        otherWall = w; break;
      }
    }
    if (!otherWall) return { wx: pt.x + n1x * halfT, wy: pt.y + n1y * halfT };
    // Other wall perpendicular + outward sign
    const odx = otherWall.end.x - otherWall.start.x, ody = otherWall.end.y - otherWall.start.y;
    const oLen = Math.sqrt(odx * odx + ody * ody) || 1;
    const oPerp = -ody / oLen, oPerpY = odx / oLen;
    const oOutward = this._getWallOutwardSign(otherWall, oPerp, oPerpY, room);
    const n2x = oPerp * oOutward, n2y = oPerpY * oOutward;
    // Miter offset
    const dot = n1x * n2x + n1y * n2y;
    if (Math.abs(1 + dot) < 0.001) return { wx: pt.x + n1x * halfT, wy: pt.y + n1y * halfT };
    const mx = (n1x + n2x) / 2, my = (n1y + n2y) / 2;
    const mLen = Math.sqrt(mx * mx + my * my) || 1;
    const cosHalf = (mx * n1x + my * n1y) / mLen;
    const miterLen = cosHalf !== 0 ? halfT / cosHalf : halfT;
    const clamped = Math.min(miterLen, halfT * 4);
    return { wx: pt.x + mx / mLen * clamped, wy: pt.y + my / mLen * clamped };
  }

  _hitTestWallCornerHandles(sx, sy) {
    if (this.activeTool !== 'select') return null;
    if (this.selectedWalls.size < 1) return null;
    const hitR = this._hitRadius;
    for (const wall of this.selectedWalls) {
      const room = this._findRoomForWall(wall);
      if (!room) continue;
      for (const h of this._getWallCornerHandles(wall, room)) {
        const sp = this.worldToScreen(h.wx, h.wy);
        if ((sx - sp.x) ** 2 + (sy - sp.y) ** 2 <= hitR * hitR) return { wall, room, whichEnd: h.whichEnd, wx: h.wx, wy: h.wy };
      }
    }
    return null;
  }

  _drawWallCornerHandles(scale) {
    if (this.activeTool !== 'select') return;
    if (this.selectedWalls.size < 1) return;
    const ctx = this.ctx;
    const handleSize = this._handleSize / scale;
    const hoverR = this._hitRadius;
    const pkey = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
    const drawn = new Set();
    for (const wall of this.selectedWalls) {
      const room = this._findRoomForWall(wall);
      if (!room) continue;
      for (const h of this._getWallCornerHandles(wall, room)) {
        const k = pkey({ x: h.wx, y: h.wy });
        if (drawn.has(k)) continue;
        drawn.add(k);
        const isActivatedCorner = this._selectHandleMode === 'wallCorner' && this._selectHandleInfo &&
          this._selectHandleInfo.wall === wall && this._selectHandleInfo.whichEnd === h.whichEnd;
        const isCyclePreviewCorner = this._isCycleHighlighted('wallCorner', { wall, whichEnd: h.whichEnd });
        const isActive = isActivatedCorner || isCyclePreviewCorner;
        const isHover = !isActive && this.mouseScreen && !this._wallCornerDragging && (() => {
          const sp = this.worldToScreen(h.wx, h.wy);
          return (this.mouseScreen.x - sp.x) ** 2 + (this.mouseScreen.y - sp.y) ** 2 <= hoverR * hoverR;
        })();
        const sz = (isHover || isActive) ? (handleSize * 1.5) : handleSize;
        if (isActivatedCorner) {
          ctx.save();
          ctx.shadowColor = '#00cc00'; ctx.shadowBlur = 8 / scale;
          ctx.fillStyle = 'rgba(0, 200, 0, 0.3)';
          ctx.fillRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
          ctx.restore();
          ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2 / scale;
          ctx.strokeRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
        } else if (isCyclePreviewCorner) {
          ctx.fillStyle = '#fff';
          ctx.strokeStyle = '#00aa00'; ctx.lineWidth = 2.5 / scale;
          ctx.fillRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
          ctx.strokeRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
        } else {
          ctx.fillStyle = '#fff';
          ctx.strokeStyle = '#006BCC';
          ctx.lineWidth = 1.5 / scale;
          ctx.fillRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
          ctx.strokeRect(h.wx - sz / 2, h.wy - sz / 2, sz, sz);
        }
      }
    }
  }

  /* ──────────────────────────────────────────
     WALL LABELS
     ────────────────────────────────────────── */

  _isObjectLabelObstructed(obj, labelY, labelH, labelW) {
    const halfH = labelH / 2 + 0.05;
    const halfW = labelW / 2 + 0.1;
    const pt = { x: obj.x, y: labelY };
    // Check room walls
    for (const room of this.rooms) {
      for (const wall of room.walls) {
        if (this.distanceToWall(pt, wall) < halfH + this.wallThickness) return true;
      }
    }
    // Check other objects
    for (const other of this.objects) {
      if (other === obj) continue;
      let oTop, oBot, oLeft, oRight;
      if (other.type === 'rectangle' || other.type === 'equipment') {
        const r = other.rotation || 0;
        const hW = other.width / 2, hD = other.depth / 2;
        const bW = Math.abs(hW * Math.cos(r)) + Math.abs(hD * Math.sin(r));
        const bH = Math.abs(hW * Math.sin(r)) + Math.abs(hD * Math.cos(r));
        oTop = other.y - bH; oBot = other.y + bH; oLeft = other.x - bW; oRight = other.x + bW;
      } else if (other.type === 'circle') {
        const r = other.diameter / 2;
        oTop = other.y - r; oBot = other.y + r; oLeft = other.x - r; oRight = other.x + r;
      } else continue;
      if (labelY - halfH < oBot && labelY + halfH > oTop &&
          obj.x - halfW < oRight && obj.x + halfW > oLeft) return true;
    }
    return false;
  }

  _drawObjects(scale) {
    if (!this.objects || this.objects.length === 0) return;
    const wallPx = this.getWallPixelWidth(scale);
    let _deferredEquip = null; // collected during loop, drawn in two-pass after

    // Show equipment at 50% opacity on floor tab when interacting with platforms
    const _pasteHasPlatform = this._pastePreview && this._pastePreview.objects && this._pastePreview.objects.some(o => o.type === 'platform');
    this._showEquipGhost = this._floorMode && (
      this.activeTool === 'platform-place' ||
      _pasteHasPlatform ||
      (this.selectedObjects.size > 0 && [...this.selectedObjects].some(o => o.type === 'platform'))
    );

    // Per-space print focus: pre-compute polygon for hit-testing objects
    const _focusPoly = this._printFocusRoom ? (this._getRoomGeoCache(this._printFocusRoom).roomPolygon || null) : null;

    // Render turf before platforms so platforms layer on top
    const _renderObjs = [...this.objects].sort((a, b) => (a.type === 'platform') - (b.type === 'platform'));
    for (const obj of _renderObjs) {
      // Per-space print: skip objects outside the focus room
      if (_focusPoly && !this.pointInPolygon(obj.x, obj.y, _focusPoly)) continue;
      // Hide equipment objects unless on the equipment tab (always show in print/view-only mode)
      // In multi-view view-only, _viewOnlyHideEquipment gates per-view visibility
      // Show as ghost when _showEquipGhost is active (floor tab + platform interaction)
      if (obj.type === 'equipment' && !this._equipmentMode && !this._printMode && !(this._viewOnly && !this._viewOnlyHideEquipment) && !this._showEquipGhost) continue;
      // Hide turf on Draw Space tab (neither floor nor equipment mode); on other tabs show when floor mode or overlay toggle is on (always in print/view-only)
      if (obj.type === 'turf' && !this._printMode && !this._viewOnly) {
        const _onCustomize = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
        if (!this._floorMode && !this._equipmentMode) continue; // Draw Space — always hide
        if (!this._floorMode && !_onCustomize && !this._equipmentMode && !this.showMaterialOverlay) continue; // non-floor tab, overlay off
      }
      // Hide platforms same as turf — only visible in floor mode or with overlay
      if (obj.type === 'platform' && !this._printMode && !this._viewOnly) {
        const _onCustomize = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
        if (!this._floorMode && !this._equipmentMode) continue;
        if (!this._floorMode && !_onCustomize && !this._equipmentMode && !this.showMaterialOverlay) continue;
      }
      // In floor mode, only dim objects inside floored rooms
      if (this._floorDimObjects) {
        let inFloored = false;
        for (const room of this.rooms) {
          if (!room.flooring || !room.closed) continue;
          const geo = this._getRoomGeoCache(room);
          if (geo.roomPolygon && this.pointInPolygon(obj.x, obj.y, geo.roomPolygon)) { inFloored = true; break; }
        }
        this.ctx.save();
        this.ctx.globalAlpha = inFloored ? 0.4 : 1.0;
      }
      this.ctx.strokeStyle = this._tc.objectStroke;
      this.ctx.lineWidth = wallPx / scale;
      this.ctx.fillStyle = this._tc.objectFill;

      if (obj.type === 'rectangle') {
        const halfW = obj.width / 2, halfD = obj.depth / 2;
        const rot = obj.rotation || 0;
        const wt = this.wallThickness;
        const smallObj = obj.width * 12 < 10 || obj.depth * 12 < 10;
        // Column drop shadow — tight contact shadow that thins and blurs outward
        if (this._printMode) {
          // Print mode: draw dark offset rects directly (canvas shadow* is invisible white-on-white)
          const sc = Math.min(halfW, halfD);
          const _colShadPasses = [
            { ox: 0,          oy: sc * 0.12,  expand: sc * 0.08,  a: 0.35 },
            { ox: -sc * 0.08, oy: sc * 0.20,  expand: sc * 0.20,  a: 0.22 },
            { ox: -sc * 0.16, oy: sc * 0.35,  expand: sc * 0.40,  a: 0.12 },
            { ox: -sc * 0.24, oy: sc * 0.50,  expand: sc * 0.65,  a: 0.06 },
          ];
          for (const sp of _colShadPasses) {
            this.ctx.save();
            this.ctx.globalAlpha = sp.a;
            this.ctx.fillStyle = '#000000';
            this.ctx.translate(obj.x + sp.ox, obj.y + sp.oy);
            this.ctx.rotate(rot);
            this.ctx.fillRect(-halfW - sp.expand, -halfD - sp.expand, obj.width + sp.expand * 2, obj.depth + sp.expand * 2);
            this.ctx.restore();
          }
          // White fill to cover shadows under column body
          this.ctx.save();
          this.ctx.fillStyle = '#ffffff';
          this.ctx.translate(obj.x, obj.y);
          this.ctx.rotate(rot);
          this.ctx.fillRect(-halfW, -halfD, obj.width, obj.depth);
          this.ctx.restore();
        } else {
          const colShadowPasses = [
            { ox: 0, oy: 1, blur: 2,   a: this.darkMode ? 0.95 : 0.90 },
            { ox: -3, oy: 6, blur: 10, a: this.darkMode ? 0.60 : 0.50 },
            { ox: -6, oy: 14, blur: 24, a: this.darkMode ? 0.35 : 0.25 },
            { ox: -10, oy: 22, blur: 40, a: this.darkMode ? 0.15 : 0.10 },
          ];
          for (const sp of colShadowPasses) {
            this.ctx.save();
            this.ctx.translate(obj.x, obj.y);
            this.ctx.rotate(rot);
            this.ctx.shadowColor = `rgba(0,0,0,${sp.a})`;
            this.ctx.shadowBlur = sp.blur;
            this.ctx.shadowOffsetX = sp.ox;
            this.ctx.shadowOffsetY = sp.oy;
            this.ctx.fillStyle = this.darkMode ? '#141c2e' : '#ffffff';
            this.ctx.fillRect(-halfW, -halfD, obj.width, obj.depth);
            this.ctx.shadowColor = 'transparent';
            this.ctx.restore();
          }
        }
        this.ctx.save();
        this.ctx.translate(obj.x, obj.y);
        this.ctx.rotate(rot);
        if (this.hollowWalls && !smallObj) {
          // Hollow: outer line at specified dims, inner line inset by wallThickness, fill inside
          const thinWidth = 1.5 / scale;
          this.ctx.lineWidth = thinWidth;
          this.ctx.fillRect(-halfW + wt, -halfD + wt, obj.width - wt * 2, obj.depth - wt * 2);
          this.ctx.strokeRect(-halfW, -halfD, obj.width, obj.depth);
          this.ctx.strokeRect(-halfW + wt, -halfD + wt, obj.width - wt * 2, obj.depth - wt * 2);
        } else if (smallObj) {
          // Small object: fill solid with interior color, outer line only
          const thinWidth = 1.5 / scale;
          this.ctx.lineWidth = thinWidth;
          this.ctx.fillRect(-halfW, -halfD, obj.width, obj.depth);
          this.ctx.strokeRect(-halfW, -halfD, obj.width, obj.depth);
        } else {
          // Solid: stroke inset by half thickness from specified dims
          this.ctx.fillRect(-halfW + wt / 2, -halfD + wt / 2, obj.width - wt, obj.depth - wt);
          this.ctx.strokeRect(-halfW + wt / 2, -halfD + wt / 2, obj.width - wt, obj.depth - wt);
        }

        this.ctx.restore();

        // Dimension labels (above object, fade in between 250%-300% zoom)
        if (this.zoomLevel >= 2.5) {
          const dimAlpha = this.zoomLevel >= 3.0 ? 1 : (this.zoomLevel - 2.5) / 0.5;
          const fontSize = 15 / scale;
          if (fontSize * scale > 3) {
            const prevAlpha = this.ctx.globalAlpha;
            this.ctx.globalAlpha = dimAlpha;
            const dimLabel = this.formatMeasurement(obj.width) + ' \u00d7 ' + this.formatMeasurement(obj.depth);
            this.ctx.font = `400 ${fontSize}px Arial`;
            this.ctx.fillStyle = this._tc.labelText;
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            const rot = obj.rotation || 0;
            // Use tight bounds for label positioning so it sits snug above visible outline
            const lw = (obj.type === 'equipment' && obj.tightWidth) ? obj.tightWidth : obj.width;
            const ld = (obj.type === 'equipment' && obj.tightDepth) ? obj.tightDepth : obj.depth;
            const ltox = (obj.type === 'equipment' ? (obj.tightOffsetX || 0) : 0);
            const ltoy = (obj.type === 'equipment' ? (obj.tightOffsetY || 0) : 0);
            const cosR = Math.cos(rot), sinR = Math.sin(rot);
            const tightCY = obj.y + ltox * sinR + ltoy * cosR;
            const bboxHalfH = (lw / 2) * Math.abs(sinR) + (ld / 2) * Math.abs(cosR);
            const gap = 4 / scale;
            let labelY = tightCY - bboxHalfH - gap - fontSize / 2;
            const tw = this.ctx.measureText(dimLabel).width;
            if (this._isObjectLabelObstructed(obj, labelY, fontSize, tw)) {
              labelY = tightCY + bboxHalfH + gap + fontSize / 2;
            }
            this.ctx.fillText(dimLabel, obj.x, labelY);
            this.ctx.globalAlpha = prevAlpha;
            this.ctx.fillStyle = this._tc.objectFill;
          }
        }
      } else if (obj.type === 'turf') {
        /* CUSTOM_PLANNER_BY_CLAUDE — Turf piece rendering */
        const halfW = obj.width / 2, halfD = obj.depth / 2;
        const rot = obj.rotation || 0;
        const turfStrokeW = (this._printMode || this._viewOnly || !this.showSeamLines) ? 0 : 1 / scale; // 1 screen-pixel outline (skip in print/view-only/seams-off)
        const hs = turfStrokeW / 2;
        // Clip turf to room polygon so it doesn't extend outside the room
        let _turfClipped = false;
        // During drag, use original position to find room (center may have left room)
        const _turfOrigPos = (this._selectDragging && this._selectDragOrigPositions) ? this._selectDragOrigPositions.get(obj) : null;
        const _turfTestX = _turfOrigPos ? _turfOrigPos.x : obj.x;
        const _turfTestY = _turfOrigPos ? _turfOrigPos.y : obj.y;
        for (const room of this.rooms) {
          if (!room.closed) continue;
          const geo = this._getRoomGeoCache(room);
          const poly = geo.roomPolygon;
          if (poly && poly.length >= 3 && this.pointInPolygon(_turfTestX, _turfTestY, poly)) {
            this.ctx.save();
            this.ctx.beginPath();
            for (let k = 0; k < poly.length; k++) {
              if (k === 0) this.ctx.moveTo(poly[k].x, poly[k].y);
              else this.ctx.lineTo(poly[k].x, poly[k].y);
            }
            this.ctx.closePath();
            this.ctx.clip();
            _turfClipped = true;
            break;
          }
        }
        this.ctx.save();
        this.ctx.translate(obj.x, obj.y);
        this.ctx.rotate(rot);
        // Filled rectangle — expanded by ~1 screen pixel past nominal boundary
        // to overlap the _applyTurfClip exclusion edge and eliminate anti-aliasing fringe
        // On view-only tabs without floor (Floor Plan / Equipment Layout), skip turf fill entirely —
        // the room background already has roomFill and adding another semi-transparent layer would make turf brighter.
        const _turfBleed = 1 / (this.basePixelsPerFoot * this.zoomLevel);
        if (!(this._viewOnly && !this._viewOnlyShowFloor)) {
          if (this.showMaterialOverlay && !this._overlayDetailSuppressed) {
            this.ctx.fillStyle = obj.turfColorHex || '#3a7d44';
            this.ctx.fillRect(-halfW, -halfD, obj.width, obj.depth);
          } else {
            // Opaque white/dark base to block any floor seam lines underneath,
            // then semi-transparent floored tint on top — matches platform appearance
            this.ctx.fillStyle = this.darkMode ? '#141c2e' : '#ffffff';
            this.ctx.fillRect(-halfW - _turfBleed, -halfD - _turfBleed, obj.width + 2 * _turfBleed, obj.depth + 2 * _turfBleed);
            this.ctx.fillStyle = this._tc.roomFlooredFill;
            this.ctx.fillRect(-halfW - _turfBleed, -halfD - _turfBleed, obj.width + 2 * _turfBleed, obj.depth + 2 * _turfBleed);
          }
        }
        // Border / perimeter band — solid fill from edge inward, drawn before markings
        const blDepth = (obj.turfBorderLine && this.showMaterialOverlay && !this._overlayDetailSuppressed)
          ? (obj.turfBorderLineInset || 4) / 12 : 0; // inches → feet
        if (blDepth > 0) {
          const blColor = obj.turfLineColor || '#FFFFFF';
          this.ctx.fillStyle = blColor;
          const blEdges = this._rotateEdges(obj.turfBorderLineEdges || ['top', 'right', 'bottom', 'left'], obj.rotation || 0);
          if (blEdges.includes('left'))   this.ctx.fillRect(-halfW, -halfD, blDepth, obj.depth);
          if (blEdges.includes('right'))  this.ctx.fillRect(halfW - blDepth, -halfD, blDepth, obj.depth);
          if (blEdges.includes('top'))    this.ctx.fillRect(-halfW, -halfD, obj.width, blDepth);
          if (blEdges.includes('bottom')) this.ctx.fillRect(-halfW, halfD - blDepth, obj.width, blDepth);
        }
        // Field lines & hash marks — inset by border depth so markings stay inside the band
        if (this.showMaterialOverlay && !this._overlayDetailSuppressed) {
          const markingsInset = blDepth > 0 ? blDepth : turfStrokeW;
          this._drawTurfMarkings(obj, halfW, halfD, markingsInset, scale);
        }
        // ── Turf Logos (only when material overlay is on) ──
        if (obj.turfLogos && obj.turfLogos.length > 0 && this.showMaterialOverlay && !this._overlayDetailSuppressed) {
          for (const logo of obj.turfLogos) {
            if (!logo.width || !logo.height) continue;
            if (logo.type === 'ladder') {
              // Draw ladder geometry inline
              this.ctx.save();
              this.ctx.beginPath();
              this.ctx.rect(-halfW + turfStrokeW, -halfD + turfStrokeW, obj.width - turfStrokeW * 2, obj.depth - turfStrokeW * 2);
              this.ctx.clip();
              this.ctx.translate(logo.x || 0, logo.y || 0);
              if (logo.rotation) this.ctx.rotate(logo.rotation);
              const _lLen = logo.width, _lWid = logo.height;
              const _hLen = _lLen / 2, _hWid = _lWid / 2;
              const _railW = 1.5 / 12, _rungW = 1.5 / 12; // 1.5" rails and rungs
              const _gapFt = 15 / 12; // 15" clear gap between rungs
              const _stepFt = _rungW + _gapFt; // 16.5" center-to-center
              const _lColor = obj.turfLineColor || '#FFFFFF';
              // Background tint
              this.ctx.fillStyle = 'rgba(0,0,0,0.08)';
              this.ctx.fillRect(-_hLen, -_hWid, _lLen, _lWid);
              // Side rails (run full length along X axis)
              this.ctx.fillStyle = _lColor;
              this.ctx.fillRect(-_hLen, -_hWid, _lLen, _railW);
              this.ctx.fillRect(-_hLen, _hWid - _railW, _lLen, _railW);
              // Uniform rungs: first at left edge, last at right edge, evenly spaced
              const _nGaps = Math.round((_lLen - _rungW) / _stepFt);
              for (let _ri = 0; _ri <= _nGaps; _ri++) {
                const _rx = -_hLen + _rungW / 2 + _ri * ((_lLen - _rungW) / _nGaps);
                this.ctx.fillRect(_rx - _rungW / 2, -_hWid, _rungW, _lWid);
              }
              this.ctx.restore();
              continue;
            }
            if (logo.type === 'dotdrill') {
              // Draw dot drill geometry inline (5-dot pattern: 4 corners + center)
              this.ctx.save();
              this.ctx.beginPath();
              this.ctx.rect(-halfW + turfStrokeW, -halfD + turfStrokeW, obj.width - turfStrokeW * 2, obj.depth - turfStrokeW * 2);
              this.ctx.clip();
              this.ctx.translate(logo.x || 0, logo.y || 0);
              if (logo.rotation) this.ctx.rotate(logo.rotation);
              const _ddW = logo.width, _ddH = logo.height;
              const _hW = _ddW / 2, _hH = _ddH / 2;
              const _dotR = 2.5 / 12; // 5" diameter = 2.5" radius in feet
              const _ddColor = obj.turfLineColor || '#FFFFFF';
              // 5 dots: center + 4 corners (edges tangent to rectangle boundary)
              this.ctx.fillStyle = _ddColor;
              const _ddPts = [
                [0, 0],                              // center
                [-_hW + _dotR, -_hH + _dotR],        // top-left
                [ _hW - _dotR, -_hH + _dotR],        // top-right
                [-_hW + _dotR,  _hH - _dotR],        // bottom-left
                [ _hW - _dotR,  _hH - _dotR]         // bottom-right
              ];
              for (let _di = 0; _di < 5; _di++) {
                this.ctx.beginPath();
                this.ctx.arc(_ddPts[_di][0], _ddPts[_di][1], _dotR, 0, Math.PI * 2);
                this.ctx.fill();
              }
              this.ctx.restore();
              continue;
            }
            if (!logo.image) continue;
            this.ctx.save();
            this.ctx.beginPath();
            this.ctx.rect(-halfW + turfStrokeW, -halfD + turfStrokeW, obj.width - turfStrokeW * 2, obj.depth - turfStrokeW * 2);
            this.ctx.clip();
            this.ctx.translate(logo.x || 0, logo.y || 0);
            if (logo.rotation) this.ctx.rotate(logo.rotation);
            if (logo.color) {
              const lw = logo.width, lh = logo.height;
              const scale = this.basePixelsPerFoot * this.zoomLevel * (window.devicePixelRatio || 1);
              const pw = Math.ceil(lw * scale), ph = Math.ceil(lh * scale);
              if (!this._logoColorCanvas || this._logoColorCanvas.width !== pw || this._logoColorCanvas.height !== ph) {
                this._logoColorCanvas = document.createElement('canvas');
                this._logoColorCanvas.width = pw;
                this._logoColorCanvas.height = ph;
              }
              const oc = this._logoColorCanvas;
              const octx = oc.getContext('2d');
              octx.clearRect(0, 0, pw, ph);
              octx.drawImage(logo.image, 0, 0, pw, ph);
              octx.globalCompositeOperation = 'source-atop';
              octx.fillStyle = logo.color;
              octx.fillRect(0, 0, pw, ph);
              octx.globalCompositeOperation = 'source-over';
              this.ctx.drawImage(oc, -lw / 2, -lh / 2, lw, lh);
            } else {
              this.ctx.drawImage(logo.image, -logo.width / 2, -logo.height / 2, logo.width, logo.height);
            }
            this.ctx.restore();
          }
        }
        // Texture overlay when Floor Overlay is on (drawn after lines/logo at 80% alpha so markings show through)
        if (this.showMaterialOverlay && !this._overlayDetailSuppressed) {
          const _ov = window.roomDesignerOverlay;
          if (_ov) {
            const _turfProd = _ov._getProduct(obj.turfProductId);
            const _turfCol = _turfProd && _turfProd.colors.find(c => c.id === obj.turfColorId);
            const _texImg = _turfCol && _ov._getTextureImage(_turfCol.textureUrl);
            if (_texImg) {
              const _inset = turfStrokeW;
              const _fillW = obj.width - _inset * 2, _fillD = obj.depth - _inset * 2;
              this.ctx.save();
              this.ctx.globalAlpha *= 0.8;
              this.ctx.beginPath();
              this.ctx.rect(-halfW + _inset, -halfD + _inset, _fillW, _fillD);
              this.ctx.clip();
              const _cellSize = 2;
              const _piStr = '1415926535897932384626433832795028841971693993751058209749445923078164062862089986280348253421170679821480865132823066470938446095505822317253594081284811174502841027019385211055596446229489549303819644288109756659334461284756482337867831652712019091456485669234603486104543266482133936072602491412737245870066063155881748815209209628292540917153643678925903600113305305488204665213841469519415116094330572703657595919530921861173819326117931051185480744623799627495673518857527248912279381830119491298336733624406566430860213949463952247371907021798609437027705392171762931767523846748184676694051320005681271452635608277857713427577896091736371787214684409012249534301465495853710507922796892589235420199561121290219608640344181598136297747713099605187072113499999983729780499510597317328160963185950244594553469083026425223082533446850352619311881710100031378387528865875332083814206171776691473035982534904287554687311595628638823537875937519577818577805321712268066130019278766111959092164201989';
              const _piLen = _piStr.length;
              const _sc = Math.floor((-halfW + _inset) / _cellSize);
              const _ec = Math.ceil((halfW - _inset) / _cellSize);
              const _sr = Math.floor((-halfD + _inset) / _cellSize);
              const _er = Math.ceil((halfD - _inset) / _cellSize);
              for (let _c = _sc; _c < _ec; _c++) {
                for (let _r = _sr; _r < _er; _r++) {
                  const _cx = _c * _cellSize + _cellSize / 2;
                  const _cy = _r * _cellSize + _cellSize / 2;
                  const _pi = (((_c * 997) + (_r * 1013)) % _piLen + _piLen) % _piLen;
                  const _dig = _piStr.charCodeAt(_pi) - 48;
                  const _rot = (_dig % 4) * Math.PI / 2;
                  this.ctx.save();
                  this.ctx.translate(_cx, _cy);
                  if (_rot) this.ctx.rotate(_rot);
                  this.ctx.drawImage(_texImg, -_cellSize / 2, -_cellSize / 2, _cellSize, _cellSize);
                  this.ctx.restore();
                }
              }
              this.ctx.restore();
            }
          }
        }
        // Red tint when dragged/nudged/resized over a wall or turf
        const isTurfBlocked = ((this._selectDragging && this._equipmentDragBlocked) || this._equipmentNudgeBlocked || (this._selectResizing && this._equipmentResizeBlocked) || (this._selectRotating && this._equipmentRotateBlocked));
        const _isTurfBlockedObj = isTurfBlocked && (this.selectedObjects.has(obj) || (this._selectResizeInfo && this._selectResizeInfo.obj === obj) || (this._selectRotateInfo && this._selectRotateInfo.obj === obj));
        // Full red tint when blocked (wall, turf, or platform overlap)
        if (_isTurfBlockedObj) {
          this.ctx.fillStyle = this.darkMode ? 'rgba(255, 68, 68, 0.55)' : 'rgba(204, 0, 0, 0.55)';
          this.ctx.fillRect(-halfW + turfStrokeW, -halfD + turfStrokeW, obj.width - turfStrokeW * 2, obj.depth - turfStrokeW * 2);
        }
        // Inner outline — inset by half stroke so outer edge = turf boundary (hidden when seam lines off)
        if (this.showSeamLines && !this._viewOnly) {
          this.ctx.strokeStyle = this._tc.wallColor;
          this.ctx.lineWidth = turfStrokeW;
          this.ctx.strokeRect(-halfW + hs, -halfD + hs, obj.width - turfStrokeW, obj.depth - turfStrokeW);
        }
        this.ctx.restore();
        if (_turfClipped) this.ctx.restore(); // restore room polygon clip
        // Full red tint outside room clip when blocked (covers part extending beyond walls)
        if (_isTurfBlockedObj && _turfClipped) {
          this.ctx.save();
          this.ctx.translate(obj.x, obj.y);
          this.ctx.rotate(rot);
          this.ctx.fillStyle = this.darkMode ? 'rgba(255, 68, 68, 0.55)' : 'rgba(204, 0, 0, 0.55)';
          this.ctx.fillRect(-halfW, -halfD, obj.width, obj.depth);
          this.ctx.restore();
        }
        // Edge dimension labels — deferred to draw after turf overage overlay
        // Skip turf dimensions in print mode and all view-only modes (floor plan tab, mobile)
        if (this._viewOnly || this._printMode) { /* no turf dims */ }
        else {
        const isTurfResizing = this._selectResizing && this._selectResizeInfo && this._selectResizeInfo.obj === obj;
        if (this.showMeasurements || isTurfResizing) {
          // Fade on zoom-out matching wall labels (skip in print/view-only Draw Space)
          const isViewOnlyDraw = this._viewOnly && !this._viewOnlyShowFloor && !this._equipmentMode;
          const skipDimFade = this._printMode || isViewOnlyDraw;
          let turfLabelAlpha = 1;
          if (isTurfResizing) {
            turfLabelAlpha = 1; // Always fully visible during resize
          } else if (!skipDimFade) {
            if (this.zoomLevel <= 0.2) turfLabelAlpha = 0;
            else if (this.zoomLevel < 0.3) turfLabelAlpha = (this.zoomLevel - 0.2) / 0.1;
          }
          if (turfLabelAlpha > 0) {
          const dpr = this._dpr || 1;
          const baseFontPx = this.largeUI ? 14.5 : 12;
          const screenFontSize = Math.round(baseFontPx);
          const padX = 1, padY = 2; // match wall label pill padding
          const cornerR = 3;
          const cosRot = Math.cos(rot), sinRot = Math.sin(rot);

          // Match wall label offset: wallThickness/2 + 4/scale + half pill height along normal
          // Wall labels sit this far from the wall centerline on the inside face.
          // Turf labels sit this far inward from the turf edge (same visual spacing).
          const pillHalfH = (screenFontSize / 2 + padY) / scale;
          const wallLabelInset = this.wallThickness / 2 + 4 / scale + pillHalfH;

          // Collect all room walls AND wall segments for proximity check
          const roomWalls = [];
          for (const r of this.rooms) {
            if (!r.closed) continue;
            for (const w of r.walls) roomWalls.push(w);
          }
          if (this.wallSegmentGroups) {
            for (const g of this.wallSegmentGroups) {
              for (const s of g.segments) roomWalls.push(s);
            }
          }

          // Helper: distance from point to line segment
          const ptSegDist = (px, py, ax, ay, bx, by) => {
            const dx = bx - ax, dy = by - ay;
            const len2 = dx * dx + dy * dy;
            if (len2 < 0.0001) return Math.sqrt((px - ax) ** 2 + (py - ay) ** 2);
            let t = ((px - ax) * dx + (py - ay) * dy) / len2;
            t = Math.max(0, Math.min(1, t));
            return Math.sqrt((px - ax - dx * t) ** 2 + (py - ay - dy * t) ** 2);
          };

          const edges = [
            { label: this.formatMeasurement(obj.width),  lx: 0, ly: -halfD + wallLabelInset, edgeLen: obj.width,  pushDx: 0, pushDy: 1,  emx: 0, emy: -halfD },  // top → push down
            { label: this.formatMeasurement(obj.width),  lx: 0, ly:  halfD - wallLabelInset, edgeLen: obj.width,  pushDx: 0, pushDy: -1, emx: 0, emy:  halfD },  // bottom → push up
            { label: this.formatMeasurement(obj.depth),  lx: -halfW + wallLabelInset, ly: 0, edgeLen: obj.depth,  pushDx: 1, pushDy: 0,  emx: -halfW, emy: 0 },  // left → push right
            { label: this.formatMeasurement(obj.depth),  lx:  halfW - wallLabelInset, ly: 0, edgeLen: obj.depth,  pushDx: -1, pushDy: 0, emx:  halfW, emy: 0 }   // right → push left
          ];

          // Temporarily set up screen-space transform to measure text widths
          this.ctx.save();
          this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          this.ctx.font = `600 ${screenFontSize}px Arial`;

          // Gap between wall label pill edge and turf label pill edge (world units)
          const labelGap = 5 / scale;

          if (!this._pendingTurfLabels) this._pendingTurfLabels = [];

          for (const edge of edges) {
            // Skip turf dimension if a wall with the same dimension runs along this edge.
            // Compute world-space edge midpoint on the actual turf boundary.
            const edgeWx = obj.x + edge.emx * cosRot - edge.emy * sinRot;
            const edgeWy = obj.y + edge.emx * sinRot + edge.emy * cosRot;
            let dupWall = false;
            const wls = this._pendingWallLabels;
            if (wls && !isTurfResizing) {
              for (const wl of wls) {
                if (wl.text !== edge.label) continue; // dimension doesn't match
                const sw = wl.sourceWall;
                if (!sw) continue;
                // Check wall is parallel to this turf edge (cross product of directions ≈ 0)
                const wdx = sw.end.x - sw.start.x, wdy = sw.end.y - sw.start.y;
                // Edge direction in world space (perpendicular to push direction)
                const edx = -edge.pushDy * cosRot - edge.pushDx * sinRot;
                const edy = -edge.pushDy * sinRot + edge.pushDx * cosRot;
                const cross = edx * wdy - edy * wdx;
                const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
                const eLen = edge.edgeLen; // already world units
                if (wLen < 0.01 || eLen < 0.01) continue;
                if (Math.abs(cross) / (wLen * eLen) > 0.05) continue; // not parallel
                // Check proximity — distance from turf edge midpoint to wall line
                const dist = ptSegDist(edgeWx, edgeWy, sw.start.x, sw.start.y, sw.end.x, sw.end.y);
                if (dist < this.wallThickness + 0.5) { dupWall = true; break; }
              }
            }
            if (dupWall) continue;

            // Measure in screen space
            const tw = this.ctx.measureText(edge.label).width;
            if (tw + padX * 2 > edge.edgeLen * scale * 0.9) continue; // skip if doesn't fit

            // Turf label pill half-extents in world units
            const turfHalfW = (tw / 2 + padX) / scale;
            const turfHalfH = pillHalfH; // already in world units

            // Compute world position (rotate local offset by turf rotation)
            let wx = obj.x + edge.lx * cosRot - edge.ly * sinRot;
            let wy = obj.y + edge.lx * sinRot + edge.ly * cosRot;

            // Check turf label rect against actual pending wall label rects.
            // Wall labels are already collected in _pendingWallLabels (from drawRoom)
            // with world-space { x, y, halfW, halfH }. Push inward if overlapping.
            // Skip during resize — the shifting looks jumpy as turf changes size.
            if (!isTurfResizing) {
            for (let pass = 0; pass < 2; pass++) {
              let bestPush = 0;
              const wls = this._pendingWallLabels;
              if (wls) {
                for (const wl of wls) {
                  // Axis-aligned rect overlap check (both labels are screen-space horizontal)
                  const overlapX = (turfHalfW + wl.halfW + labelGap) - Math.abs(wx - wl.x);
                  const overlapY = (turfHalfH + wl.halfH + labelGap) - Math.abs(wy - wl.y);
                  if (overlapX > 0 && overlapY > 0) {
                    // Push along the edge's push direction by whichever axis overlap applies
                    // pushDx≠0 means horizontal push → need to clear horizontal overlap
                    // pushDy≠0 means vertical push → need to clear vertical overlap
                    const needed = Math.abs(edge.pushDx) * overlapX + Math.abs(edge.pushDy) * overlapY;
                    if (needed > bestPush) bestPush = needed;
                  }
                }
              }
              if (bestPush <= 0) break;
              const pushWx = edge.pushDx * bestPush * cosRot - edge.pushDy * bestPush * sinRot;
              const pushWy = edge.pushDx * bestPush * sinRot + edge.pushDy * bestPush * cosRot;
              wx += pushWx;
              wy += pushWy;
            }
            }

            // Convert to screen coords
            const _ts = this.worldToScreen(wx, wy);
            let sx = _ts.x;
            let sy = _ts.y;

            // Background pill — match wall label style
            const halfTw = tw / 2;
            const halfH = screenFontSize / 2;

            // Clamp labels to viewport during resize so they don't go off-screen
            if (isTurfResizing) {
              const _cb = this._getLabelClampBounds();
              const pillW = halfTw + padX;
              const pillH = halfH + padY;
              sx = Math.max(_cb.left + pillW, Math.min(_cb.right - pillW, sx));
              sy = Math.max(_cb.top + pillH, Math.min(_cb.bottom - pillH, sy));
            }

            // Defer drawing — collect label data for _drawTurfLabels()
            this._pendingTurfLabels.push({
              sx, sy, text: edge.label, halfTw, halfH: halfH,
              padX, padY, cornerR, screenFontSize, alpha: turfLabelAlpha
            });
          }

          this.ctx.restore();
        } // end turfLabelAlpha > 0
        }
        } // end mobile view-only turf dims gate
        /* END CUSTOM_PLANNER_BY_CLAUDE */
      } else if (obj.type === 'platform') {
        /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — Inlaid platform rendering */
        const halfW = obj.width / 2, halfD = obj.depth / 2;
        const rot = obj.rotation || 0;
        const strokeW = 1 / scale;

        // Clip to inset room polygon so platform underlaps the inner wall edge
        let _platClipped = false;
        const _platInset = 0; // no inset — platform extends to room polygon boundary
        for (const room of this.rooms) {
          if (!room.closed) continue;
          const geo = this._getRoomGeoCache(room);
          const poly = geo.roomPolygon;
          if (poly && poly.length >= 3 && this.pointInPolygon(obj.x, obj.y, poly)) {
            let cx = 0, cy = 0;
            for (const p of poly) { cx += p.x; cy += p.y; }
            cx /= poly.length; cy /= poly.length;
            this.ctx.save();
            this.ctx.beginPath();
            for (let k = 0; k < poly.length; k++) {
              const dx = poly[k].x - cx, dy = poly[k].y - cy;
              const dist = Math.sqrt(dx * dx + dy * dy) || 1;
              const nx = poly[k].x - (dx / dist) * _platInset;
              const ny = poly[k].y - (dy / dist) * _platInset;
              if (k === 0) this.ctx.moveTo(nx, ny);
              else this.ctx.lineTo(nx, ny);
            }
            this.ctx.closePath();
            this.ctx.clip();
            _platClipped = true;
            break;
          }
        }

        this.ctx.save();
        this.ctx.translate(obj.x, obj.y);
        this.ctx.rotate(rot);

        // Fill center + wings + logo (original structure)
        // Center and wings get 100% texture; logo gets tint + 20% texture via offscreen canvas
        // Expand fill by ~1 screen pixel to overlap _applyTurfClip exclusion edge
        const _platBleed = 1 / (this.basePixelsPerFoot * this.zoomLevel);
        const _platOv = window.roomDesignerOverlay;
        const _platShowTex = this.showMaterialOverlay && !this._overlayDetailSuppressed;
        if (_platShowTex) {
          // Center texture at full opacity
          const centerTexImg = (obj.centerTextureUrl && _platOv) ? _platOv._getTextureImage(obj.centerTextureUrl) : null;
          if (centerTexImg) {
            this._drawTiledPlatformTexture(this.ctx, centerTexImg, -halfW - _platBleed, -halfD - _platBleed, obj.width + 2 * _platBleed, obj.depth + 2 * _platBleed);
          } else {
            this.ctx.fillStyle = obj.centerColor || '#1C1C1C';
            this.ctx.fillRect(-halfW - _platBleed, -halfD - _platBleed, obj.width + 2 * _platBleed, obj.depth + 2 * _platBleed);
          }
        } else {
          // Opaque base + semi-transparent tint — blocks any floor seam lines underneath
          this.ctx.fillStyle = this.darkMode ? '#141c2e' : '#ffffff';
          this.ctx.fillRect(-halfW - _platBleed, -halfD - _platBleed, obj.width + 2 * _platBleed, obj.depth + 2 * _platBleed);
          this.ctx.fillStyle = this._tc.roomFlooredFill;
          this.ctx.fillRect(-halfW - _platBleed, -halfD - _platBleed, obj.width + 2 * _platBleed, obj.depth + 2 * _platBleed);
        }

        // Wing zones (2' strips on left and right) — full-opacity texture
        if (obj.wings && _platShowTex) {
          const wingW = 2; // 2 feet each side
          const wingTexImg = (obj.wingTextureUrl && _platOv) ? _platOv._getTextureImage(obj.wingTextureUrl) : null;
          if (wingTexImg) {
            this._drawTiledPlatformTexture(this.ctx, wingTexImg, -halfW - _platBleed, -halfD - _platBleed, wingW + _platBleed, obj.depth + 2 * _platBleed);
            this._drawTiledPlatformTexture(this.ctx, wingTexImg, halfW - wingW, -halfD - _platBleed, wingW + _platBleed, obj.depth + 2 * _platBleed);
          } else {
            this.ctx.fillStyle = obj.wingColor || '#D32F2F';
            this.ctx.fillRect(-halfW - _platBleed, -halfD - _platBleed, wingW + _platBleed, obj.depth + 2 * _platBleed);
            this.ctx.fillRect(halfW - wingW, -halfD - _platBleed, wingW + _platBleed, obj.depth + 2 * _platBleed);
          }
        }

        // Platform logo: image → color tint → 20% texture (offscreen canvas, source-atop)
        if (_platShowTex) {
          const _ov = _platOv;
          const logo = _ov && _ov._platformLogo;
          if (logo && logo.image) {
            const centerW = obj.wings ? (obj.width - 4) : obj.width;
            const lw = Math.min(logo.width, centerW);
            const lh = logo.height * (lw / logo.width);
            const _plScale = this.basePixelsPerFoot * this.zoomLevel * (window.devicePixelRatio || 1);
            const pw = Math.ceil(lw * _plScale), ph = Math.ceil(lh * _plScale);
            if (pw > 0 && ph > 0) {
              if (!this._platformLogoColorCanvas || this._platformLogoColorCanvas.width !== pw || this._platformLogoColorCanvas.height !== ph) {
                this._platformLogoColorCanvas = document.createElement('canvas');
                this._platformLogoColorCanvas.width = pw;
                this._platformLogoColorCanvas.height = ph;
              }
              const oc = this._platformLogoColorCanvas;
              const octx = oc.getContext('2d');
              octx.clearRect(0, 0, pw, ph);
              // Draw logo image
              octx.drawImage(logo.image, 0, 0, pw, ph);
              // Apply color tint via source-atop (only on logo pixels)
              if (logo.color) {
                octx.globalCompositeOperation = 'source-atop';
                octx.fillStyle = logo.color;
                octx.fillRect(0, 0, pw, ph);
              }
              // Apply center texture at 20% alpha via source-atop (subtle rubber speckle)
              // Use 2ft cells to match _drawTiledPlatformTexture scale on the platform surface
              const logoTexImg = (obj.centerTextureUrl && _platOv) ? _platOv._getTextureImage(obj.centerTextureUrl) : null;
              if (logoTexImg) {
                octx.globalCompositeOperation = 'source-atop';
                octx.globalAlpha = 0.20;
                const cellPx = 2 * _plScale; // 2ft cells in pixel space
                for (let tx = 0; tx < pw; tx += cellPx) {
                  for (let ty = 0; ty < ph; ty += cellPx) {
                    const tw = Math.min(cellPx, pw - tx);
                    const th = Math.min(cellPx, ph - ty);
                    octx.drawImage(logoTexImg, tx, ty, tw, th);
                  }
                }
                octx.globalAlpha = 1.0;
              }
              octx.globalCompositeOperation = 'source-over';
              // Stamp logo onto main canvas
              this.ctx.save();
              this.ctx.translate(logo.x || 0, logo.y || 0);
              if (logo.rotation) this.ctx.rotate(logo.rotation);
              this.ctx.drawImage(oc, -lw / 2, -lh / 2, lw, lh);
              this.ctx.restore();
            }
          }
        }

        // Border stroke — thin wallColor outline, hidden in print/view-only/seam-off
        if (this.showSeamLines && !this._viewOnly && !this._printMode) {
          this.ctx.strokeStyle = this._tc.wallColor;
          this.ctx.lineWidth = 1 / scale;
          this.ctx.setLineDash([]);
          this.ctx.strokeRect(-halfW, -halfD, obj.width, obj.depth);
          // Wing divider lines
          if (obj.wings) {
            this.ctx.beginPath();
            this.ctx.moveTo(-halfW + 2, -halfD); this.ctx.lineTo(-halfW + 2, halfD);
            this.ctx.moveTo(halfW - 2, -halfD); this.ctx.lineTo(halfW - 2, halfD);
            this.ctx.stroke();
          }
        }

        // Red tint when blocked
        const isPlatBlocked = ((this._selectDragging && this._equipmentDragBlocked) || this._equipmentNudgeBlocked || (this._selectResizing && this._equipmentResizeBlocked) || (this._selectRotating && this._equipmentRotateBlocked));
        if (isPlatBlocked && (this.selectedObjects.has(obj) || (this._selectResizeInfo && this._selectResizeInfo.obj === obj) || (this._selectRotateInfo && this._selectRotateInfo.obj === obj))) {
          this.ctx.fillStyle = this.darkMode ? 'rgba(255, 68, 68, 0.55)' : 'rgba(204, 0, 0, 0.55)';
          this.ctx.fillRect(-halfW, -halfD, obj.width, obj.depth);
        }

        this.ctx.restore();
        if (_platClipped) this.ctx.restore();

        // No dimension labels for platforms — size is shown in the side panel
        /* END CUSTOM_ROOMDESIGNER_BY_CLAUDE */
      } else if (obj.type === 'equipment') {
        // Equipment drawn in deferred two-pass (shadow pass + art pass) to prevent
        // shadows from bleeding onto neighboring equipment. Collect here, draw after loop.
        if (!_deferredEquip) _deferredEquip = [];
        _deferredEquip.push({ obj, floorAlpha: this._floorDimObjects ? this.ctx.globalAlpha : 1 });
      } else if (obj.type === 'circle') {
        const r = obj.diameter / 2;
        const wt = this.wallThickness;
        const smallObj = obj.diameter * 12 < 10;
        // Pillar drop shadow — tight contact shadow that thins and blurs outward
        if (this._printMode) {
          // Print mode: draw dark offset circles directly (canvas shadow* is invisible white-on-white)
          const _pilShadPasses = [
            { ox: 0,         oy: r * 0.12,  expand: r * 0.08,  a: 0.35 },
            { ox: -r * 0.08, oy: r * 0.20,  expand: r * 0.20,  a: 0.22 },
            { ox: -r * 0.16, oy: r * 0.35,  expand: r * 0.40,  a: 0.12 },
            { ox: -r * 0.24, oy: r * 0.50,  expand: r * 0.65,  a: 0.06 },
          ];
          for (const sp of _pilShadPasses) {
            this.ctx.save();
            this.ctx.globalAlpha = sp.a;
            this.ctx.fillStyle = '#000000';
            this.ctx.beginPath();
            this.ctx.arc(obj.x + sp.ox, obj.y + sp.oy, r + sp.expand, 0, Math.PI * 2);
            this.ctx.fill();
            this.ctx.restore();
          }
          // White fill to cover shadows under pillar body
          this.ctx.save();
          this.ctx.fillStyle = '#ffffff';
          this.ctx.beginPath();
          this.ctx.arc(obj.x, obj.y, r, 0, Math.PI * 2);
          this.ctx.fill();
          this.ctx.restore();
        } else {
          const pilShadowPasses = [
            { ox: 0, oy: 1, blur: 2,   a: this.darkMode ? 0.95 : 0.90 },
            { ox: -3, oy: 6, blur: 10, a: this.darkMode ? 0.60 : 0.50 },
            { ox: -6, oy: 14, blur: 24, a: this.darkMode ? 0.35 : 0.25 },
            { ox: -10, oy: 22, blur: 40, a: this.darkMode ? 0.15 : 0.10 },
          ];
          for (const sp of pilShadowPasses) {
            this.ctx.save();
            this.ctx.shadowColor = `rgba(0,0,0,${sp.a})`;
            this.ctx.shadowBlur = sp.blur;
            this.ctx.shadowOffsetX = sp.ox;
            this.ctx.shadowOffsetY = sp.oy;
            this.ctx.fillStyle = this.darkMode ? '#141c2e' : '#ffffff';
            this.ctx.beginPath();
            this.ctx.arc(obj.x, obj.y, r, 0, Math.PI * 2);
            this.ctx.fill();
            this.ctx.shadowColor = 'transparent';
            this.ctx.restore();
          }
        }
        this.ctx.strokeStyle = this._tc.objectStroke;
        this.ctx.lineWidth = wallPx / scale;
        this.ctx.fillStyle = this._tc.objectFill;
        if (this.hollowWalls && !smallObj) {
          // Hollow: outer circle at specified radius, inner circle inset by wallThickness, fill inside
          const thinWidth = 1.5 / scale;
          this.ctx.lineWidth = thinWidth;
          if (r > wt) {
            this.ctx.beginPath();
            this.ctx.arc(obj.x, obj.y, r - wt, 0, Math.PI * 2);
            this.ctx.fill();
          }
          this.ctx.beginPath();
          this.ctx.arc(obj.x, obj.y, r, 0, Math.PI * 2);
          this.ctx.stroke();
          if (r > wt) {
            this.ctx.beginPath();
            this.ctx.arc(obj.x, obj.y, r - wt, 0, Math.PI * 2);
            this.ctx.stroke();
          }
        } else if (smallObj) {
          // Small object: fill solid with interior color, outer line only
          const thinWidth = 1.5 / scale;
          this.ctx.lineWidth = thinWidth;
          this.ctx.beginPath();
          this.ctx.arc(obj.x, obj.y, r, 0, Math.PI * 2);
          this.ctx.fill();
          this.ctx.stroke();
        } else {
          // Solid: filled circle inset by half thickness from specified radius
          this.ctx.beginPath();
          this.ctx.arc(obj.x, obj.y, r - wt / 2, 0, Math.PI * 2);
          this.ctx.fill();
          this.ctx.stroke();
        }

        // Dimension label (above object, fade in between 250%-300% zoom)
        if (this.zoomLevel >= 2.5) {
          const dimAlpha = this.zoomLevel >= 3.0 ? 1 : (this.zoomLevel - 2.5) / 0.5;
          const fontSize = 15 / scale;
          if (fontSize * scale > 3) {
            const prevAlpha = this.ctx.globalAlpha;
            this.ctx.globalAlpha = dimAlpha;
            const dimText = this.formatMeasurement(obj.diameter);
            this.ctx.font = `400 ${fontSize}px Arial`;
            this.ctx.fillStyle = this._tc.labelText;
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            const r = obj.diameter / 2;
            const gap = 4 / scale;
            let labelY = obj.y - r - gap - fontSize / 2;
            const tw = this.ctx.measureText(dimText).width;
            if (this._isObjectLabelObstructed(obj, labelY, fontSize, tw)) {
              labelY = obj.y + r + gap + fontSize / 2;
            }
            this.ctx.fillText(dimText, obj.x, labelY);
            this.ctx.globalAlpha = prevAlpha;
            this.ctx.textAlign = 'start';
            this.ctx.textBaseline = 'alphabetic';
            this.ctx.fillStyle = this._tc.objectFill;
          }
        }
      }

      // Center snap dot while dragging
      if (this._selectDragging && this.selectedObjects.has(obj)) {
        this.ctx.fillStyle = 'rgba(0, 107, 204, 0.7)';
        this.ctx.beginPath();
        this.ctx.arc(obj.x, obj.y, 4 / scale, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.strokeStyle = '#006BCC';
        this.ctx.lineWidth = 1.5 / scale;
        this.ctx.beginPath();
        this.ctx.arc(obj.x, obj.y, 4 / scale, 0, Math.PI * 2);
        this.ctx.stroke();
      }
      if (this._floorDimObjects) this.ctx.restore();
    }
    // Three-pass equipment rendering for platforms + standard pieces.
    // Floor platforms (3223, 3194, 3195) are flat — they receive shadows from equipment on top.
    // Pass 0: platforms (silhouette + art, no shadow, no opaque cover)
    // Pass 1: non-platform shadows + silhouettes
    // Pass 2: non-platform opaque fills + art (covers stray shadows between non-platforms)
    const _platformSkus = new Set(['3223', '3194', '3195']);
    if (_deferredEquip && _deferredEquip.length > 0) {
      // Tag each entry as platform or not
      for (const de of _deferredEquip) {
        const info = this._equipmentImages && this._equipmentImages[de.obj.equipmentId];
        de._isPlatform = !!(info && _platformSkus.has(info.sku));
      }
      // Ghost mode: reduce all equipment alpha to 50% on floor tab during platform interaction
      if (this._showEquipGhost) {
        for (const de of _deferredEquip) de.floorAlpha = Math.min(de.floorAlpha, 0.5);
      }
      // Shadow helper — draws tight contact + wider diffuse shadow for an equipment silhouette
      const _drawEquipShadows = (obj, sil, floorAlpha) => {
        const halfW = obj.width / 2, halfD = obj.depth / 2;
        const rot = obj.rotation || 0;
        const lineArtLinear = this.zoomLevel >= 1.0 ? 1.0
          : this.zoomLevel >= 0.20 ? (this.zoomLevel - 0.20) / 0.80 : 0;
        const lineArtAlpha = lineArtLinear * lineArtLinear;
        // Sub-pass A: tight contact shadow (defined edge close to base)
        this.ctx.save();
        this.ctx.globalAlpha = floorAlpha;
        this.ctx.translate(obj.x, obj.y);
        this.ctx.rotate(rot);
        this.ctx.shadowColor = this.darkMode ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.55)';
        this.ctx.shadowBlur = 5; this.ctx.shadowOffsetX = -1; this.ctx.shadowOffsetY = 3;
        this.ctx.drawImage(sil, -halfW, -halfD, obj.width, obj.depth);
        this.ctx.restore();
        // Sub-pass B: wider diffuse shadow
        this.ctx.save();
        this.ctx.globalAlpha = floorAlpha;
        this.ctx.translate(obj.x, obj.y);
        this.ctx.rotate(rot);
        if (lineArtAlpha > 0) {
          this.ctx.shadowColor = this.darkMode ? 'rgba(0,0,0,0.65)' : 'rgba(0,0,0,0.46)';
          this.ctx.shadowBlur = 10; this.ctx.shadowOffsetX = -3; this.ctx.shadowOffsetY = 5;
        } else {
          this.ctx.shadowColor = this.darkMode ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.25)';
          this.ctx.shadowBlur = 4; this.ctx.shadowOffsetX = -1; this.ctx.shadowOffsetY = 2;
        }
        this.ctx.drawImage(sil, -halfW, -halfD, obj.width, obj.depth);
        this.ctx.restore();
      };

      // ---- Wall shadow exclusion clip ----
      // Clip shadow passes (Pass 0 + Pass 1) to exclude wall areas so
      // equipment shadows don't bleed onto room walls or wall segments.
      // Uses even-odd rule: huge outer rect + wall rects → shadows only
      // render where walls are NOT.
      // Clip rects are shrunk to a thin centerline core so shadows can
      // extend naturally up to the wall edge (dark wall paint covers them).
      {
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.rect(-50000, -50000, 100000, 100000);
        const _addWallClipRect = (wall, halfThick) => {
          const dx = wall.end.x - wall.start.x;
          const dy = wall.end.y - wall.start.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len < 0.001) return;
          const ext = halfThick * 0.15;
          const nx = -dy / len * ext;
          const ny = dx / len * ext;
          this.ctx.moveTo(wall.start.x + nx, wall.start.y + ny);
          this.ctx.lineTo(wall.end.x + nx, wall.end.y + ny);
          this.ctx.lineTo(wall.end.x - nx, wall.end.y - ny);
          this.ctx.lineTo(wall.start.x - nx, wall.start.y - ny);
          this.ctx.closePath();
        };
        const _roomHalfT = (this.wallThickness || 0.375) / 2;
        const _segHalfT = (1 / 3) / 2;
        for (const room of this.rooms) {
          if (!room.walls) continue;
          for (const w of room.walls) _addWallClipRect(w, _roomHalfT);
          if (room.cutouts) for (const c of room.cutouts) {
            if (c.walls) for (const w of c.walls) _addWallClipRect(w, _roomHalfT);
          }
        }
        if (this.currentRoom && this.currentRoom.walls) {
          for (const w of this.currentRoom.walls) _addWallClipRect(w, _roomHalfT);
        }
        // Dividing walls exclude shadows (physical walls); dividing lines allow them (visual-only)
        if (this.wallSegmentGroups) {
          for (const g of this.wallSegmentGroups) {
            if ((g.type || 'wall') === 'line') continue;
            if (g.segments) for (const s of g.segments) _addWallClipRect(s, _segHalfT);
          }
        }
        if (this.currentWallSegmentGroup && this.currentWallSegmentGroup.segments && (this._wallSegmentType || 'wall') !== 'line') {
          for (const s of this.currentWallSegmentGroup.segments) _addWallClipRect(s, _segHalfT);
        }
        this.ctx.clip('evenodd');
      }

      // Pass 0: platforms — full shadows + silhouette (drawn first so other equipment shadows land on them)
      {
        for (const de of _deferredEquip) {
          if (!de._isPlatform) continue;
          const obj = de.obj;
          const halfW = obj.width / 2, halfD = obj.depth / 2;
          const rot = obj.rotation || 0;
          const info = this._equipmentImages && this._equipmentImages[obj.equipmentId];
          if (!info || !info.loaded) continue;
          const sil = this._ensureEquipmentCanvas(info, 'sil');
          if (!sil) continue;
          // Both shadow passes (tight + wide)
          _drawEquipShadows(obj, sil, de.floorAlpha);
          // Colored silhouette + material layers on top (covers shadow under platform body).
          // Materials are drawn here (before Pass 1) so non-platform shadows land on
          // the fully-rendered platform surface including rubber and wood regions.
          this.ctx.save();
          this.ctx.globalAlpha = de.floorAlpha;
          this.ctx.translate(obj.x, obj.y);
          this.ctx.rotate(rot);
          const brandConf = this._brandColors && this._brandColors[info.vendor];
          const tkoDefault = info.vendor === 'TKO' ? '#ffffff' : null;
          const pcColor = (brandConf && brandConf.powderCoat) || tkoDefault || (this.darkMode ? '#B1B3B3' : '#004B87');
          const glossy = brandConf ? !(brandConf.pcMatte) : true;
          const matte = brandConf ? !!(brandConf.pcMatte) : false;
          const sparkle = brandConf ? !!(brandConf.pcSparkle) : false;
          // Powder coat: pattern overlay (e.g. Silver Vein) or flat color
          const pcPatKey = brandConf && brandConf.pcPattern;
          const pcPatEntry = pcPatKey && this._patternImages && this._patternImages[pcPatKey];
          if (pcPatEntry && pcPatEntry.loaded) {
            const patSil = this._getPatternedPcCanvas(sil, pcPatEntry.img, pcPatKey, info, obj.width);
            this.ctx.drawImage(patSil, -halfW, -halfD, obj.width, obj.depth);
          } else {
            const coloredSil = this._getColoredCanvas(sil, pcColor, info, '_coloredSilCanvas', '_powderCoatCachedColor', glossy, matte, sparkle);
            this.ctx.drawImage(coloredSil, -halfW, -halfD, obj.width, obj.depth);
          }
          // Upholstery
          {
            const uphColor = (brandConf && brandConf.upholstery) || '#2D2926';
            if (info.hasUpholstery && info.upholsteryCanvas) {
              const patKey = brandConf && brandConf.uphPattern;
              const patEntry = patKey && this._patternImages && this._patternImages[patKey];
              const isCamoPlat = patEntry && patEntry.loaded;
              if (isCamoPlat) {
                const patUph = this._getPatternedCanvas(info.upholsteryCanvas, patEntry.img, patKey, info);
                this.ctx.drawImage(patUph, -halfW, -halfD, obj.width, obj.depth);
              } else {
                const coloredUph = this._getColoredCanvas(info.upholsteryCanvas, uphColor, info, '_coloredUphCanvas', '_upholsteryCachedColor');
                this.ctx.drawImage(coloredUph, -halfW, -halfD, obj.width, obj.depth);
              }
              // Crinkle leather texture overlay (skip for camo patterns)
              if (!isCamoPlat) {
                const crinkle = this._buildCrinkleUphCanvas(info);
                if (crinkle) {
                  this.ctx.drawImage(crinkle, -halfW, -halfD, obj.width, obj.depth);
                }
              }
              // Edge highlight for cushioned look
              const edgeHLplat = this._buildUphEdgeHighlight(info);
              if (edgeHLplat) {
                this.ctx.drawImage(edgeHLplat, -halfW, -halfD, obj.width, obj.depth);
              }
            }
          }
          // Rubber
          if (info.hasRubber && info.rubberCanvas) {
            const coloredRubber = this._getColoredCanvas(info.rubberCanvas, '#1A1A1A', info, '_coloredRubberCanvas', '_rubberCachedColor');
            this.ctx.drawImage(coloredRubber, -halfW, -halfD, obj.width, obj.depth);
          }
          // Wood
          if (info.hasWood && info.woodCanvas) {
            const coloredWood = this._getColoredCanvas(info.woodCanvas, '#C4A46B', info, '_coloredWoodCanvas', '_woodCachedColor');
            this.ctx.drawImage(coloredWood, -halfW, -halfD, obj.width, obj.depth);
          }
          // Chrome
          if (info.hasChrome && info.chromeCanvas) {
            const coloredChrome = this._getColoredCanvas(info.chromeCanvas, '#C0C0C0', info, '_coloredChromeCanvas', '_chromeCachedColor', true);
            this.ctx.drawImage(coloredChrome, -halfW, -halfD, obj.width, obj.depth);
            if (!info._chromeGlossCanvas) {
              const gloss = this._buildGlossCanvas(info);
              if (gloss) {
                const cg = document.createElement('canvas');
                cg.width = info.cw; cg.height = info.ch;
                const cgCtx = cg.getContext('2d');
                cgCtx.drawImage(gloss, 0, 0);
                cgCtx.globalCompositeOperation = 'destination-in';
                cgCtx.drawImage(info.chromeCanvas, 0, 0);
                cgCtx.globalCompositeOperation = 'source-over';
                info._chromeGlossCanvas = cg;
              }
            }
            if (info._chromeGlossCanvas) {
              for (let gi = 0; gi < 3; gi++) this.ctx.drawImage(info._chromeGlossCanvas, -halfW, -halfD, obj.width, obj.depth);
            }
            // Checker plate texture on chrome
            const checkerPlat = this._buildCheckerChromeCanvas(info);
            if (checkerPlat) {
              this.ctx.drawImage(checkerPlat, -halfW, -halfD, obj.width, obj.depth);
            }
          }
          this.ctx.restore();
        }
      }
      // Pass 1: non-platform shadows + silhouettes (shadow bleeds onto canvas + platforms)
      for (const de of _deferredEquip) {
        if (de._isPlatform) continue;
        const info = this._equipmentImages && this._equipmentImages[de.obj.equipmentId];
        if (!info || !info.loaded) continue;
        const sil = this._ensureEquipmentCanvas(info, 'sil');
        if (!sil) continue;
        _drawEquipShadows(de.obj, sil, de.floorAlpha);
      }
      // ---- End wall shadow exclusion clip ----
      this.ctx.restore();
      // Pass 2: non-platform opaque silhouette fill (covers stray shadows) + line art + outlines
      // Platform line art is also drawn here (after shadows land on them)
      for (const de of _deferredEquip) {
        const obj = de.obj;
        const halfW = obj.width / 2, halfD = obj.depth / 2;
        const rot = obj.rotation || 0;
        const info = this._equipmentImages && this._equipmentImages[obj.equipmentId];
        this.ctx.save();
        this.ctx.globalAlpha = de.floorAlpha;
        this.ctx.translate(obj.x, obj.y);
        this.ctx.rotate(rot);
        if (info && info.loaded) {
          // Print mode: clean black outlines + subtle off-white fill, no shadows/silhouette
          if (this._printMode) {
            const sil = this._ensureEquipmentCanvas(info, 'sil');
            if (sil) {
              // Check for brand color overrides — use dark mode defaults for print
              // (light mode default #EAEAEA is invisible on white PDF background)
              const brandConf = this._brandColors && this._brandColors[info.vendor];
              const tkoDefPrint = info.vendor === 'TKO' ? '#ffffff' : null;
              const pcColor = (brandConf && brandConf.powderCoat) || tkoDefPrint || '#004B87';
              const glossy = brandConf ? !(brandConf.pcMatte) : true;
              // Powder coat pattern overlay (e.g. Silver Vein) in print mode
              const pcPatKey = brandConf && brandConf.pcPattern;
              const pcPatEntry = pcPatKey && this._patternImages && this._patternImages[pcPatKey];
              if (pcPatEntry && pcPatEntry.loaded) {
                const patSil = this._getPatternedPcCanvas(sil, pcPatEntry.img, pcPatKey, info, obj.width);
                this.ctx.drawImage(patSil, -halfW, -halfD, obj.width, obj.depth);
              } else {
                if (!this._ghostRecolorCanvas) this._ghostRecolorCanvas = document.createElement('canvas');
                const gc = this._ghostRecolorCanvas;
                if (gc.width !== sil.width || gc.height !== sil.height) { gc.width = sil.width; gc.height = sil.height; }
                const gctx = gc.getContext('2d');
                gctx.clearRect(0, 0, gc.width, gc.height);
                gctx.drawImage(sil, 0, 0);
                gctx.globalCompositeOperation = 'source-in';
                gctx.fillStyle = pcColor;
                gctx.fillRect(0, 0, gc.width, gc.height);
                // Per-panel glossy sheen
                if (glossy) {
                  const gloss = this._buildGlossCanvas(info);
                  if (gloss) {
                    gctx.globalCompositeOperation = 'source-atop';
                    gctx.drawImage(gloss, 0, 0);
                  }
                }
                // Grain texture for matte/textured coats
                if (brandConf && brandConf.pcMatte) {
                  const grain = this._buildGrainCanvas(info);
                  if (grain) {
                    gctx.globalCompositeOperation = 'source-atop';
                    gctx.drawImage(grain, 0, 0);
                  }
                }
                gctx.globalCompositeOperation = 'source-over';
                this.ctx.drawImage(gc, -halfW, -halfD, obj.width, obj.depth);
              }
              // Upholstery color in print mode
              const uphColor = (brandConf && brandConf.upholstery) || '#2D2926';
              if (info.hasUpholstery && info.upholsteryCanvas) {
                const patKey = brandConf && brandConf.uphPattern;
                const patEntry = patKey && this._patternImages && this._patternImages[patKey];
                const isCamoPrint = patEntry && patEntry.loaded;
                if (isCamoPrint) {
                  const patUph = this._getPatternedCanvas(info.upholsteryCanvas, patEntry.img, patKey, info);
                  this.ctx.drawImage(patUph, -halfW, -halfD, obj.width, obj.depth);
                } else {
                  const coloredUph = this._getColoredCanvas(info.upholsteryCanvas, uphColor, info, '_coloredUphCanvas', '_upholsteryCachedColor');
                  this.ctx.drawImage(coloredUph, -halfW, -halfD, obj.width, obj.depth);
                }
                // Crinkle leather texture overlay (skip for camo patterns)
                if (!isCamoPrint) {
                  const crinkle = this._buildCrinkleUphCanvas(info);
                  if (crinkle) {
                    this.ctx.drawImage(crinkle, -halfW, -halfD, obj.width, obj.depth);
                  }
                }
                // Edge highlight for cushioned look
                const edgeHLp = this._buildUphEdgeHighlight(info);
                if (edgeHLp) {
                  this.ctx.drawImage(edgeHLp, -halfW, -halfD, obj.width, obj.depth);
                }
              }
              // Rubber regions in print mode: always charcoal
              if (info.hasRubber && info.rubberCanvas) {
                const coloredRubber = this._getColoredCanvas(info.rubberCanvas, '#1A1A1A', info, '_coloredRubberCanvas', '_rubberCachedColor');
                this.ctx.drawImage(coloredRubber, -halfW, -halfD, obj.width, obj.depth);
              }
              // Wood regions in print mode: always oak
              if (info.hasWood && info.woodCanvas) {
                const coloredWood = this._getColoredCanvas(info.woodCanvas, '#C4A46B', info, '_coloredWoodCanvas', '_woodCachedColor');
                this.ctx.drawImage(coloredWood, -halfW, -halfD, obj.width, obj.depth);
              }
              // Chrome regions in print mode: always silver with amplified gloss
              if (info.hasChrome && info.chromeCanvas) {
                const coloredChrome = this._getColoredCanvas(info.chromeCanvas, '#C0C0C0', info, '_coloredChromeCanvas', '_chromeCachedColor', true);
                this.ctx.drawImage(coloredChrome, -halfW, -halfD, obj.width, obj.depth);
                if (!info._chromeGlossCanvas) {
                  const gloss = this._buildGlossCanvas(info);
                  if (gloss) {
                    const cg = document.createElement('canvas');
                    cg.width = info.cw; cg.height = info.ch;
                    const cgCtx = cg.getContext('2d');
                    cgCtx.drawImage(gloss, 0, 0);
                    cgCtx.globalCompositeOperation = 'destination-in';
                    cgCtx.drawImage(info.chromeCanvas, 0, 0);
                    cgCtx.globalCompositeOperation = 'source-over';
                    info._chromeGlossCanvas = cg;
                  }
                }
                if (info._chromeGlossCanvas) {
                  for (let gi = 0; gi < 3; gi++) this.ctx.drawImage(info._chromeGlossCanvas, -halfW, -halfD, obj.width, obj.depth);
                }
                // Checker plate texture on chrome
                const checkerP = this._buildCheckerChromeCanvas(info);
                if (checkerP) {
                  this.ctx.drawImage(checkerP, -halfW, -halfD, obj.width, obj.depth);
                }
              }
            }
            // Draw line art directly — normalCanvas keeps original black strokes in both modes.
            if (info.normalCanvas) this.ctx.drawImage(info.normalCanvas, -halfW, -halfD, obj.width, obj.depth);
            this.ctx.restore();
            continue;
          }
          const bitmap = this._ensureEquipmentCanvas(info, 'normal');
          const screenPx = Math.min(obj.width, obj.depth) * scale;
          const lineArtLinear = this.zoomLevel >= 1.0 ? 1.0
            : this.zoomLevel >= 0.20 ? (this.zoomLevel - 0.20) / 0.80 : 0;
          const lineArtAlpha = lineArtLinear * lineArtLinear;
          // Redraw silhouette without shadow (opaque fill covers neighbor shadows)
          // Skip for platforms — they keep shadows from equipment on top
          const sil = this._ensureEquipmentCanvas(info, 'sil');
          if (sil && !de._isPlatform) {
            // Powder coat: explicit brand selection, or theme-dependent default
            const brandConf = this._brandColors && this._brandColors[info.vendor];
            const tkoDefP2 = info.vendor === 'TKO' ? '#ffffff' : null;
            const pcColor = (brandConf && brandConf.powderCoat) || tkoDefP2 || (this.darkMode ? '#B1B3B3' : '#004B87');
            const glossy = brandConf ? !(brandConf.pcMatte) : true;
            const matte = brandConf ? !!(brandConf.pcMatte) : false;
            const sparkle = brandConf ? !!(brandConf.pcSparkle) : false;
            const pcPatKey = brandConf && brandConf.pcPattern;
            const pcPatEntry = pcPatKey && this._patternImages && this._patternImages[pcPatKey];
            if (pcPatEntry && pcPatEntry.loaded) {
              const patSil = this._getPatternedPcCanvas(sil, pcPatEntry.img, pcPatKey, info, obj.width);
              this.ctx.drawImage(patSil, -halfW, -halfD, obj.width, obj.depth);
            } else {
              const coloredSil = this._getColoredCanvas(sil, pcColor, info, '_coloredSilCanvas', '_powderCoatCachedColor', glossy, matte, sparkle);
              this.ctx.drawImage(coloredSil, -halfW, -halfD, obj.width, obj.depth);
            }
          }
          // Material layers — skip for platforms (already drawn in Pass 0 so shadows land on them)
          if (!de._isPlatform) {
            // Upholstery recoloring: drawn BEFORE line art so pad outlines
            // naturally draw on top, keeping pad boundaries visible.
            {
              const brandConf = this._brandColors && this._brandColors[info.vendor];
              const uphColor = (brandConf && brandConf.upholstery) || '#2D2926';
              if (info.hasUpholstery && info.upholsteryCanvas) {
                // Camo pattern: tile swatch image instead of solid color
                const patKey = brandConf && brandConf.uphPattern;
                const patEntry = patKey && this._patternImages && this._patternImages[patKey];
                const isCamo = patEntry && patEntry.loaded;
                if (isCamo) {
                  const patUph = this._getPatternedCanvas(info.upholsteryCanvas, patEntry.img, patKey, info);
                  this.ctx.drawImage(patUph, -halfW, -halfD, obj.width, obj.depth);
                } else {
                  const coloredUph = this._getColoredCanvas(info.upholsteryCanvas, uphColor, info, '_coloredUphCanvas', '_upholsteryCachedColor');
                  this.ctx.drawImage(coloredUph, -halfW, -halfD, obj.width, obj.depth);
                }
                // Crinkle leather texture overlay (skip for camo patterns)
                if (!isCamo) {
                  const crinkle = this._buildCrinkleUphCanvas(info);
                  if (crinkle) {
                    this.ctx.drawImage(crinkle, -halfW, -halfD, obj.width, obj.depth);
                  }
                }
                // Edge highlight: white glow fading inward for cushioned look
                const edgeHL = this._buildUphEdgeHighlight(info);
                if (edgeHL) {
                  this.ctx.drawImage(edgeHL, -halfW, -halfD, obj.width, obj.depth);
                }
              }
            }
            // Rubber regions: always charcoal, not affected by brand colors
            if (info.hasRubber && info.rubberCanvas) {
              const coloredRubber = this._getColoredCanvas(info.rubberCanvas, '#1A1A1A', info, '_coloredRubberCanvas', '_rubberCachedColor');
              this.ctx.drawImage(coloredRubber, -halfW, -halfD, obj.width, obj.depth);
            }
            // Wood regions: always oak, not affected by brand colors
            if (info.hasWood && info.woodCanvas) {
              const coloredWood = this._getColoredCanvas(info.woodCanvas, '#C4A46B', info, '_coloredWoodCanvas', '_woodCachedColor');
              this.ctx.drawImage(coloredWood, -halfW, -halfD, obj.width, obj.depth);
            }
            // Chrome regions: always silver with amplified gloss
            if (info.hasChrome && info.chromeCanvas) {
              const coloredChrome = this._getColoredCanvas(info.chromeCanvas, '#C0C0C0', info, '_coloredChromeCanvas', '_chromeCachedColor', true);
              this.ctx.drawImage(coloredChrome, -halfW, -halfD, obj.width, obj.depth);
              // Triple gloss pass: chrome is much shinier than powder coat
              if (!info._chromeGlossCanvas) {
                const gloss = this._buildGlossCanvas(info);
                if (gloss) {
                  const cg = document.createElement('canvas');
                  cg.width = info.cw; cg.height = info.ch;
                  const cgCtx = cg.getContext('2d');
                  cgCtx.drawImage(gloss, 0, 0);
                  cgCtx.globalCompositeOperation = 'destination-in';
                  cgCtx.drawImage(info.chromeCanvas, 0, 0);
                  cgCtx.globalCompositeOperation = 'source-over';
                  info._chromeGlossCanvas = cg;
                }
              }
              if (info._chromeGlossCanvas) {
                for (let gi = 0; gi < 3; gi++) this.ctx.drawImage(info._chromeGlossCanvas, -halfW, -halfD, obj.width, obj.depth);
              }
              // Checker plate texture on chrome
              const checker = this._buildCheckerChromeCanvas(info);
              if (checker) {
                this.ctx.drawImage(checker, -halfW, -halfD, obj.width, obj.depth);
              }
            }
          }
          // Line art
          if (lineArtAlpha > 0) {
            const prevAlpha = this.ctx.globalAlpha;
            if (lineArtAlpha < 1) this.ctx.globalAlpha = prevAlpha * lineArtAlpha;
            const boldAlpha = screenPx < 60 ? 1 : screenPx < 200 ? (200 - screenPx) / 140 : 0;
            if (boldAlpha > 0) {
              const boldBitmap = this._ensureEquipmentCanvas(info, 'bold');
              if (boldAlpha >= 1) {
                this.ctx.drawImage(boldBitmap, -halfW, -halfD, obj.width, obj.depth);
              } else {
                this.ctx.drawImage(bitmap, -halfW, -halfD, obj.width, obj.depth);
                this.ctx.globalAlpha = prevAlpha * lineArtAlpha * boldAlpha;
                this.ctx.drawImage(boldBitmap, -halfW, -halfD, obj.width, obj.depth);
              }
            } else {
              this.ctx.drawImage(bitmap, -halfW, -halfD, obj.width, obj.depth);
            }
            this.ctx.globalAlpha = prevAlpha;
          }
          // Inner stroke
          const strokeBase = this.zoomLevel <= 0.5 ? 0.8 : this.zoomLevel <= 1.2 ? (1.2 - this.zoomLevel) / 0.7 * 0.8 : 0;
          const strokeAlpha = strokeBase * lineArtAlpha;
          if (strokeAlpha > 0) {
            const strokeCanvas = this._ensureEquipmentCanvas(info, 'stroke');
            if (strokeCanvas) {
              this.ctx.globalAlpha = strokeAlpha;
              this.ctx.drawImage(strokeCanvas, -halfW, -halfD, obj.width, obj.depth);
              this.ctx.globalAlpha = 1;
            }
          }
          // Marker artifact cover-up: after all line art, draw small patches at
          // each marker position using the effective pad color. The position-based
          // wipe in equipment.js clears a generous circle at each marker, but bold
          // line art spread or anti-aliased edges can leave faint residual pixels.
          // These patches cover only the marker circles, not the pad boundaries.
          {
            const brandConf = this._brandColors && this._brandColors[info.vendor];
            const uphColor = (brandConf && brandConf.upholstery) || '#2D2926';
            const pcColor = brandConf && brandConf.powderCoat;
            const patchColor = uphColor || pcColor;
            if (info._upholsteryMarkers && info._upholsteryMarkers.length > 0) {
              const scaleX = obj.width / info.cw;
              const scaleY = obj.depth / info.ch;
              this.ctx.fillStyle = patchColor;
              for (const m of info._upholsteryMarkers) {
                const cx = -halfW + m.x * scaleX;
                const cy = -halfD + m.y * scaleY;
                const baseR = m.rRaw != null ? m.rRaw : m.r - 3;
                const r = baseR * Math.max(scaleX, scaleY);
                this.ctx.beginPath();
                this.ctx.arc(cx, cy, r, 0, Math.PI * 2);
                this.ctx.fill();
              }
              // Re-draw upholstery texture clipped to marker circles so patches
              // blend with the pad surface (camo pattern or solid color + crinkle)
              this.ctx.save();
              this.ctx.beginPath();
              for (const m of info._upholsteryMarkers) {
                const cx = -halfW + m.x * scaleX;
                const cy = -halfD + m.y * scaleY;
                const baseR = m.rRaw != null ? m.rRaw : m.r - 3;
                const r = baseR * Math.max(scaleX, scaleY);
                this.ctx.moveTo(cx + r, cy);
                this.ctx.arc(cx, cy, r, 0, Math.PI * 2);
              }
              this.ctx.clip();
              const _patK = brandConf && brandConf.uphPattern;
              const _patE = _patK && this._patternImages && this._patternImages[_patK];
              if (_patE && _patE.loaded) {
                const patUph = this._getPatternedCanvas(info.upholsteryCanvas, _patE.img, _patK, info);
                this.ctx.drawImage(patUph, -halfW, -halfD, obj.width, obj.depth);
              } else {
                const crinkle = this._buildCrinkleUphCanvas(info);
                if (crinkle) {
                  this.ctx.drawImage(crinkle, -halfW, -halfD, obj.width, obj.depth);
                }
              }
              // Edge highlight on marker patches too
              const edgeHLm = this._buildUphEdgeHighlight(info);
              if (edgeHLm) {
                this.ctx.drawImage(edgeHLm, -halfW, -halfD, obj.width, obj.depth);
              }
              this.ctx.restore();
            }
            // Rubber marker patch cover-up: always charcoal
            if (info._rubberMarkers && info._rubberMarkers.length > 0) {
              const scaleX = obj.width / info.cw;
              const scaleY = obj.depth / info.ch;
              this.ctx.fillStyle = '#1A1A1A';
              for (const m of info._rubberMarkers) {
                const cx = -halfW + m.x * scaleX;
                const cy = -halfD + m.y * scaleY;
                const baseR = m.rRaw != null ? m.rRaw : m.r - 3;
                const r = baseR * Math.max(scaleX, scaleY);
                this.ctx.beginPath();
                this.ctx.arc(cx, cy, r, 0, Math.PI * 2);
                this.ctx.fill();
              }
            }
            // Chrome marker patch cover-up: always silver
            if (info._chromeMarkers && info._chromeMarkers.length > 0) {
              const scaleX = obj.width / info.cw;
              const scaleY = obj.depth / info.ch;
              this.ctx.fillStyle = '#C0C0C0';
              for (const m of info._chromeMarkers) {
                const cx = -halfW + m.x * scaleX;
                const cy = -halfD + m.y * scaleY;
                const baseR = m.rRaw != null ? m.rRaw : m.r - 3;
                const r = baseR * Math.max(scaleX, scaleY);
                this.ctx.beginPath();
                this.ctx.arc(cx, cy, r, 0, Math.PI * 2);
                this.ctx.fill();
              }
              // Re-draw checker plate clipped to chrome marker circles
              const checkerM = this._buildCheckerChromeCanvas(info);
              if (checkerM) {
                this.ctx.save();
                this.ctx.beginPath();
                for (const m of info._chromeMarkers) {
                  const cx = -halfW + m.x * scaleX;
                  const cy = -halfD + m.y * scaleY;
                  const baseR = m.rRaw != null ? m.rRaw : m.r - 3;
                  const r = baseR * Math.max(scaleX, scaleY);
                  this.ctx.moveTo(cx + r, cy);
                  this.ctx.arc(cx, cy, r, 0, Math.PI * 2);
                }
                this.ctx.clip();
                this.ctx.drawImage(checkerM, -halfW, -halfD, obj.width, obj.depth);
                this.ctx.restore();
              }
            }
            // Wood marker patch cover-up: always oak
            if (info._woodMarkers && info._woodMarkers.length > 0) {
              const scaleX = obj.width / info.cw;
              const scaleY = obj.depth / info.ch;
              this.ctx.fillStyle = '#C4A46B';
              for (const m of info._woodMarkers) {
                const cx = -halfW + m.x * scaleX;
                const cy = -halfD + m.y * scaleY;
                const baseR = m.rRaw != null ? m.rRaw : m.r - 3;
                const r = baseR * Math.max(scaleX, scaleY);
                this.ctx.beginPath();
                this.ctx.arc(cx, cy, r, 0, Math.PI * 2);
                this.ctx.fill();
              }
            }
          }
          // Perimeter outline
          if (this.zoomLevel <= 1.5) {
            const outAlpha = this.zoomLevel <= 0.25 ? 1.0 : (1.5 - this.zoomLevel) / 1.25;
            if (outAlpha > 0) {
              const outlineC = this._ensureEquipmentCanvas(info, 'outline');
              if (outlineC) {
                this.ctx.globalAlpha = outAlpha;
                this.ctx.drawImage(outlineC, -halfW, -halfD, obj.width, obj.depth);
                this.ctx.globalAlpha = 1;
              }
            }
          }
          // Red tint overlay when dragging or nudging equipment over a wall
          if (((this._selectDragging && this._equipmentDragBlocked) || this._equipmentNudgeBlocked) && this.selectedObjects.has(obj) && sil) {
            if (!this._ghostRecolorCanvas) this._ghostRecolorCanvas = document.createElement('canvas');
            const gc = this._ghostRecolorCanvas;
            if (gc.width !== sil.width || gc.height !== sil.height) { gc.width = sil.width; gc.height = sil.height; }
            const gctx = gc.getContext('2d');
            gctx.clearRect(0, 0, gc.width, gc.height);
            gctx.drawImage(sil, 0, 0);
            gctx.globalCompositeOperation = 'source-in';
            gctx.fillStyle = this.darkMode ? '#ff4444' : '#cc0000';
            gctx.fillRect(0, 0, gc.width, gc.height);
            gctx.globalCompositeOperation = 'source-over';
            this.ctx.globalAlpha = 0.55;
            this.ctx.drawImage(gc, -halfW, -halfD, obj.width, obj.depth);
            this.ctx.globalAlpha = 1;
          }
        } else {
          // Fallback: labeled rectangle
          const thinWidth = 1.5 / scale;
          this.ctx.lineWidth = thinWidth;
          this.ctx.strokeStyle = this._tc.objectStroke;
          this.ctx.setLineDash([0.2, 0.1]);
          this.ctx.strokeRect(-halfW, -halfD, obj.width, obj.depth);
          this.ctx.setLineDash([]);
          const fontSize = Math.min(obj.width, obj.depth) * 0.15;
          this.ctx.font = `400 ${fontSize}px Arial`;
          this.ctx.fillStyle = this._tc.labelText;
          this.ctx.textAlign = 'center';
          this.ctx.textBaseline = 'middle';
          this.ctx.fillText(obj.equipmentId || 'Equipment', 0, 0);
        }
        this.ctx.restore();
      }
    }
  }

  // CUSTOM_PROJECTASSEMBLY_BY_CLAUDE — print-mode wall dim labels in seam-plan style.
  // Outside-perimeter positioning, white rounded pill, bold black text. Used by the
  // print pipeline (legacy save AND Path B variant gen) when showMeasurements is true.
  // The editor's _drawWallLabels uses inside-face positioning + dynamic fade and is
  // unsuitable for print output; this is its print-only counterpart.
  _drawPrintWallLabels(scale) {
    const ctx = this.ctx;
    if (!ctx) return;
    const dpr = this._dpr || 1;
    const wallThickness = this.wallThickness || 0.375;

    // Font size mirrors seam plan PDF (28-40px on the print canvas)
    const dimFontSize = Math.max(28, Math.min(40, 28 * (scale / 20)));
    const pillPadX = 4, pillPadY = 3, cr = 3;

    const roomsToDraw = this._printFocusRoom ? [this._printFocusRoom] : this.rooms;
    const candidates = [];

    for (const room of roomsToDraw) {
      if (!room || !room.closed || room.type !== 'add' || !room.walls || room.walls.length < 3) continue;
      const geo = this._getRoomGeoCache(room);
      if (!geo || !geo.roomPolygon || !geo.labelWalls) continue;
      const poly = geo.roomPolygon;
      const labelWalls = geo.labelWalls;

      // Centroid (tiebreak when polygon-inside test is ambiguous)
      let cx = 0, cy = 0;
      for (const p of poly) { cx += p.x; cy += p.y; }
      cx /= poly.length; cy /= poly.length;

      for (const wall of labelWalls) {
        const distance = this.getDistance(wall.start, wall.end);
        if (distance < 0.5) continue;
        const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) continue;
        const nx = -dy / len, ny = dx / len;
        const midX = (wall.start.x + wall.end.x) / 2, midY = (wall.start.y + wall.end.y) / 2;
        const testDist = 0.2;
        const aInside = this.pointInPolygon(midX + nx * testDist, midY + ny * testDist, poly);
        const bInside = this.pointInPolygon(midX - nx * testDist, midY - ny * testDist, poly);
        let sign;
        if (aInside && !bInside) sign = -1;       // outside lies along -nx
        else if (!aInside && bInside) sign = 1;    // outside lies along +nx
        else { const dot = (midX - cx) * nx + (midY - cy) * ny; sign = dot >= 0 ? 1 : -1; }

        const label = this.formatMeasurement(distance);
        ctx.font = `600 ${dimFontSize}px Arial`;
        const tww = ctx.measureText(label).width;
        const th = dimFontSize;
        const bw = tww + pillPadX * 2, bh = th + pillPadY * 2;

        // Skip pills wider than 1.2× the wall on screen (matches seam plan rule)
        const wallScreenLen = len * scale;
        if (bw > wallScreenLen * 1.2) continue;

        // World-space offset = wall thickness clearance + half pill height (in feet)
        const offsetFeet = (wallThickness / 2 + 0.3) + (th / 2 + pillPadY) / scale;
        const lwx = midX + sign * nx * offsetFeet;
        const lwy = midY + sign * ny * offsetFeet;
        const sp = this.worldToScreen(lwx, lwy);
        candidates.push({ scrX: sp.x, scrY: sp.y, label, distance, bw, bh });
      }
    }

    // Filter overlapping labels — keep the longer wall's label (tiebreak by index)
    const survivors = [];
    for (let i = 0; i < candidates.length; i++) {
      const a = candidates[i];
      let dominated = false;
      for (let j = 0; j < candidates.length; j++) {
        if (i === j) continue;
        const b = candidates[j];
        const ox = (a.bw / 2 + b.bw / 2 + 2) - Math.abs(a.scrX - b.scrX);
        const oy = (a.bh / 2 + b.bh / 2 + 2) - Math.abs(a.scrY - b.scrY);
        if (ox > 0 && oy > 0 && (b.distance > a.distance || (b.distance === a.distance && j < i))) { dominated = true; break; }
      }
      if (!dominated) survivors.push(a);
    }

    // Draw in raw screen pixels for crisp text + crisp pill edges
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    for (const wl of survivors) {
      ctx.font = `600 ${dimFontSize}px Arial`;
      const tww = ctx.measureText(wl.label).width;
      const th = dimFontSize;
      const bx = wl.scrX - tww / 2 - pillPadX, by = wl.scrY - th / 2 - pillPadY;
      const bw = tww + pillPadX * 2, bh = th + pillPadY * 2;

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(bx + cr, by);
      ctx.lineTo(bx + bw - cr, by);
      ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + cr);
      ctx.lineTo(bx + bw, by + bh - cr);
      ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - cr, by + bh);
      ctx.lineTo(bx + cr, by + bh);
      ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - cr);
      ctx.lineTo(bx, by + cr);
      ctx.quadraticCurveTo(bx, by, bx + cr, by);
      ctx.fill();

      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(wl.label, wl.scrX, wl.scrY);
    }
    ctx.textAlign = 'start';
    ctx.textBaseline = 'alphabetic';
    ctx.restore();
  }
  // END CUSTOM_PROJECTASSEMBLY_BY_CLAUDE

  _drawWallLabels(scale) {
    if (!this._pendingWallLabels || this._pendingWallLabels.length === 0) return;

    // De-overlap labels
    const labels = this._pendingWallLabels;
    const placed = [];

    labels.sort((a, b) => b.wallLength - a.wallLength);

    const margin = 3 / scale; // 3px gap between label pills
    for (const label of labels) {
      let overlaps = false;
      for (const p of placed) {
        if (Math.abs(label.x - p.x) < (label.halfW + p.halfW + margin) &&
            Math.abs(label.y - p.y) < (label.halfH + p.halfH + margin)) {
          overlaps = true;
          break;
        }
      }
      if (!overlaps) {
        placed.push(label);
      }
    }

    // Store placed wall labels so _drawRoomLabels can avoid them
    this._placedWallLabels = placed;

    // Fade wall labels when zoomed out past 30% (skip fade in print mode and view-only Draw Space / Floor Plan)
    const isViewOnlyDraw = this._viewOnly && !this._viewOnlyShowFloor && !this._equipmentMode;
    const skipDimFade = this._printMode || isViewOnlyDraw;
    let labelAlpha = 1;
    if (!skipDimFade) {
      if (this.zoomLevel <= 0.2) labelAlpha = 0;
      else if (this.zoomLevel < 0.3) labelAlpha = (this.zoomLevel - 0.2) / 0.1;
    }
    if (labelAlpha <= 0) return;
    const prevAlpha = this.ctx.globalAlpha;

    // Short-wall fade: walls under 4' fade out between 175% and 30% zoom (skip in print/view-only Floor Plan)
    const shortFade = skipDimFade ? 1 : this.zoomLevel >= 1.75 ? 1 : this.zoomLevel >= 0.3 ? (this.zoomLevel - 0.3) / 1.45 : 0;

    // Draw labels in screen space for crisp rendering (no sub-pixel jitter)
    const dpr = this._dpr || 1;
    for (const label of placed) {
      const effAlpha = label.wallLength < 4 ? labelAlpha * shortFade : labelAlpha;
      if (effAlpha <= 0) continue;

      // Convert world position to screen coordinates
      const _wls = this.worldToScreen(label.x, label.y);
      const sx = _wls.x;
      const sy = _wls.y;
      const screenFontSize = Math.round(label.fontSize * scale);
      if (screenFontSize < 4) continue;

      this.ctx.save();
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      this.ctx.font = `600 ${screenFontSize}px Arial`;
      const halfW = this.ctx.measureText(label.text).width / 2;
      const halfH = screenFontSize / 2;
      const padX = 1;
      const padY = 2;
      const cornerR = 3;

      const bx = sx - halfW - padX;
      const by = sy - halfH - padY;
      const bw = (halfW + padX) * 2;
      const bh = (halfH + padY) * 2;

      // Background pill — draw at full opacity so overage can't bleed through
      this.ctx.fillStyle = this._tc.labelBg;
      this.ctx.beginPath();
      this.ctx.moveTo(bx + cornerR, by);
      this.ctx.lineTo(bx + bw - cornerR, by);
      this.ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + cornerR);
      this.ctx.lineTo(bx + bw, by + bh - cornerR);
      this.ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - cornerR, by + bh);
      this.ctx.lineTo(bx + cornerR, by + bh);
      this.ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - cornerR);
      this.ctx.lineTo(bx, by + cornerR);
      this.ctx.quadraticCurveTo(bx, by, bx + cornerR, by);
      this.ctx.closePath();
      this.ctx.fill();

      // Border and text fade with zoom
      this.ctx.globalAlpha = effAlpha;
      this.ctx.strokeStyle = this._tc.labelBorder;
      this.ctx.lineWidth = 0.5;
      this.ctx.stroke();

      // Text
      this.ctx.fillStyle = label.color;
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(label.text, sx, sy + screenFontSize * 0.07);

      this.ctx.restore();
    }

    // Reset alpha
    this.ctx.globalAlpha = prevAlpha;
  }

  // Draw field lines and hash marks on a turf piece
  // Called inside the turf's save/restore block (already translated + rotated to obj center)
  _drawTurfMarkings(obj, halfW, halfD, inset, scale) {
    try { this._drawTurfMarkingsInner(obj, halfW, halfD, inset, scale); }
    catch (e) { console.warn('Turf markings render error:', e); }
  }

  _drawTurfMarkingsInner(obj, halfW, halfD, inset, scale) {
    const hasLines = obj.turfLines || false;
    const hasHashes = obj.turfHashes || false;
    if (!hasLines && !hasHashes && !obj.turfSledLanes) return;

    const color = obj.turfLineColor || '#FFFFFF';
    const lineW = 4 / 12; // 4 inches in feet
    const rotated = obj.turfMarkingsRotated || false;

    // "run axis" = lengthwise dimension (what lines divide)
    // Default: lines cross the width, divide the depth
    // Rotated: lines cross the depth, divide the width
    const crossHalf = rotated ? halfD : halfW;
    const runHalf = rotated ? halfW : halfD;
    const crossFull = crossHalf * 2;
    const runFull = runHalf * 2;

    this.ctx.save();
    if (rotated) this.ctx.rotate(-Math.PI / 2);

    this.ctx.fillStyle = color;

    // Clip to turf interior
    this.ctx.beginPath();
    this.ctx.rect(-crossHalf + inset, -runHalf + inset, crossFull - inset * 2, runFull - inset * 2);
    this.ctx.clip();

    // Build per-row logo exclusion lookup. For each marking Y position, returns
    // an array of X ranges (in markings-local coords) to skip, or null if no overlap.
    // Supports multiple logos — each logo produces its own exclusion function,
    // and the wrapper merges overlapping ranges at query time.
    let _logoExclAtY = null; // function(y) → array of {xMin,xMax} or null
    const _logoExclFns = []; // per-logo {fn, pad} pairs
    if (obj.turfLogos && obj.turfLogos.length > 0) {
      for (const logo of obj.turfLogos) {
        if ((!logo.image && logo.type !== 'ladder' && logo.type !== 'dotdrill') || !logo.width || !logo.height) continue;
        const pad = 4 / 12; // 4 inches clearance from opaque content
        const lx = logo.x || 0, ly = logo.y || 0;
        const lr = logo.rotation || 0;
        const lw = logo.width, lh = logo.height;
        const tb = logo.tightBounds;
        const hasRowProfile = tb && tb.rowProfile && tb.scanH;
        const hasColProfile = tb && tb.colProfile && tb.scanW;
        const thresh = 0.09; // ~5°
        const absLr = Math.abs(lr % (Math.PI / 2));
        const nearAxisAligned = (absLr < thresh || Math.abs(absLr - Math.PI / 2) < thresh);
        let fn = null;
        if (nearAxisAligned && (hasRowProfile || hasColProfile)) {
          // Detect if logo itself is rotated ~90° (rows↔columns swap in world space)
          const lrMod = ((lr % Math.PI) + Math.PI) % Math.PI;
          const logoIs90 = (Math.abs(lrMod - Math.PI / 2) < thresh);
          // When logo is rotated 90°, image rows become world columns and vice versa,
          // so we need the opposite profile from what markings rotation alone dictates.
          const wantRow = (rotated === logoIs90); // XOR: same = row, different = col
          const useColProfile = !wantRow && hasColProfile;
          const useRowProfile = wantRow && hasRowProfile;
          if (useColProfile && !logoIs90) {
            // Case 2: markings rotated, logo upright
            const scanW = tb.scanW;
            const colProfile = tb.colProfile;
            const padCols = Math.ceil((pad / lw) * scanW);
            fn = (my) => {
              const turfX = my;
              const logoLocalX = turfX - lx;
              const imgFracX = logoLocalX / lw + 0.5;
              const colCenter = Math.floor(imgFracX * scanW);
              const colMin = Math.max(0, colCenter - padCols);
              const colMax = Math.min(scanW - 1, colCenter + padCols);
              let unionMinY = null, unionMaxY = null;
              for (let c = colMin; c <= colMax; c++) {
                const cp = colProfile[c];
                if (cp) {
                  if (unionMinY === null || cp.minY < unionMinY) unionMinY = cp.minY;
                  if (unionMaxY === null || cp.maxY > unionMaxY) unionMaxY = cp.maxY;
                }
              }
              if (unionMinY === null) return null;
              const logoYMin = (unionMinY - 0.5) * lh - pad;
              const logoYMax = (unionMaxY - 0.5) * lh + pad;
              const turfYMin = ly + logoYMin;
              const turfYMax = ly + logoYMax;
              return { xMin: -turfYMax, xMax: -turfYMin };
            };
          } else if (useColProfile && logoIs90) {
            // Case 3: markings normal, logo rotated ~90°
            // Markings Y → turf Y = my; logo at 90° so turf Y maps to image X axis
            // Inverse: localX = wy - ly → imgFracX = (my - ly) / lw + 0.5
            // colProfile Y extent → world X: wx = lx - (frac - 0.5) * lh (sign flip)
            const scanW = tb.scanW;
            const colProfile = tb.colProfile;
            const padCols = Math.ceil((pad / lw) * scanW);
            fn = (my) => {
              const localX = my - ly;
              const imgFracX = localX / lw + 0.5;
              const colCenter = Math.floor(imgFracX * scanW);
              const colMin = Math.max(0, colCenter - padCols);
              const colMax = Math.min(scanW - 1, colCenter + padCols);
              let unionMinY = null, unionMaxY = null;
              for (let c = colMin; c <= colMax; c++) {
                const cp = colProfile[c];
                if (cp) {
                  if (unionMinY === null || cp.minY < unionMinY) unionMinY = cp.minY;
                  if (unionMaxY === null || cp.maxY > unionMaxY) unionMaxY = cp.maxY;
                }
              }
              if (unionMinY === null) return null;
              // Image Y frac → world X: wx = lx - (frac - 0.5) * lh
              const wxMin = lx - (unionMaxY - 0.5) * lh - pad;
              const wxMax = lx - (unionMinY - 0.5) * lh + pad;
              return { xMin: wxMin, xMax: wxMax };
            };
          } else if (useRowProfile && !logoIs90) {
            // Case 1: markings normal, logo upright
            const scanH = tb.scanH;
            const rowProfile = tb.rowProfile;
            const padRows = Math.ceil((pad / lh) * scanH);
            fn = (my) => {
              const turfY = my;
              const logoLocalY = turfY - ly;
              const imgFracY = logoLocalY / lh + 0.5;
              const rowCenter = Math.floor(imgFracY * scanH);
              const rowMin = Math.max(0, rowCenter - padRows);
              const rowMax = Math.min(scanH - 1, rowCenter + padRows);
              let unionMinX = null, unionMaxX = null;
              for (let r = rowMin; r <= rowMax; r++) {
                const rp = rowProfile[r];
                if (rp) {
                  if (unionMinX === null || rp.minX < unionMinX) unionMinX = rp.minX;
                  if (unionMaxX === null || rp.maxX > unionMaxX) unionMaxX = rp.maxX;
                }
              }
              if (unionMinX === null) return null;
              const logoXMin = (unionMinX - 0.5) * lw - pad;
              const logoXMax = (unionMaxX - 0.5) * lw + pad;
              return { xMin: lx + logoXMin, xMax: lx + logoXMax };
            };
          } else if (useRowProfile && logoIs90) {
            // Case 4: markings rotated, logo rotated ~90°
            // Markings Y → turf X = my; logo at 90° so turf X maps to image Y axis
            // Inverse: localY = lx - wx = lx - my → imgFracY = (lx - my) / lh + 0.5
            // rowProfile X extent → world Y: wy = ly + (frac - 0.5) * lw
            const scanH = tb.scanH;
            const rowProfile = tb.rowProfile;
            const padRows = Math.ceil((pad / lh) * scanH);
            fn = (my) => {
              const localY = lx - my;
              const imgFracY = localY / lh + 0.5;
              const rowCenter = Math.floor(imgFracY * scanH);
              const rowMin = Math.max(0, rowCenter - padRows);
              const rowMax = Math.min(scanH - 1, rowCenter + padRows);
              let unionMinX = null, unionMaxX = null;
              for (let r = rowMin; r <= rowMax; r++) {
                const rp = rowProfile[r];
                if (rp) {
                  if (unionMinX === null || rp.minX < unionMinX) unionMinX = rp.minX;
                  if (unionMaxX === null || rp.maxX > unionMaxX) unionMaxX = rp.maxX;
                }
              }
              if (unionMinX === null) return null;
              // Image X frac → world Y: wy = ly + (frac - 0.5) * lw
              const wyMin = ly + (unionMinX - 0.5) * lw - pad;
              const wyMax = ly + (unionMaxX - 0.5) * lw + pad;
              // Markings X = -turf Y when markings are rotated
              return { xMin: -wyMax, xMax: -wyMin };
            };
          }
        }
        if (!fn) {
          // Fallback: rotated-rectangle exclusion via scanline–edge intersection
          let clipHalfW, clipHalfH, clipCX, clipCY;
          if (tb) {
            clipHalfW = (tb.w * lw) / 2 + pad;
            clipHalfH = (tb.h * lh) / 2 + pad;
            const offX = (tb.x + tb.w / 2 - 0.5) * lw;
            const offY = (tb.y + tb.h / 2 - 0.5) * lh;
            const cosLr = Math.cos(lr), sinLr = Math.sin(lr);
            clipCX = lx + offX * cosLr - offY * sinLr;
            clipCY = ly + offX * sinLr + offY * cosLr;
          } else {
            clipHalfW = lw / 2 + pad;
            clipHalfH = lh / 2 + pad;
            clipCX = lx; clipCY = ly;
          }
          const markRot = rotated ? Math.PI / 2 : 0;
          const cmr = Math.cos(markRot), smr = Math.sin(markRot);
          const mlx = cmr * clipCX - smr * clipCY;
          const mly = smr * clipCX + cmr * clipCY;
          const mlr = lr + markRot;
          const cR = Math.cos(mlr), sR = Math.sin(mlr);
          const rc = [
            { x: mlx + (-clipHalfW) * cR - (-clipHalfH) * sR, y: mly + (-clipHalfW) * sR + (-clipHalfH) * cR },
            { x: mlx + ( clipHalfW) * cR - (-clipHalfH) * sR, y: mly + ( clipHalfW) * sR + (-clipHalfH) * cR },
            { x: mlx + ( clipHalfW) * cR - ( clipHalfH) * sR, y: mly + ( clipHalfW) * sR + ( clipHalfH) * cR },
            { x: mlx + (-clipHalfW) * cR - ( clipHalfH) * sR, y: mly + (-clipHalfW) * sR + ( clipHalfH) * cR }
          ];
          const rcYMin = Math.min(rc[0].y, rc[1].y, rc[2].y, rc[3].y);
          const rcYMax = Math.max(rc[0].y, rc[1].y, rc[2].y, rc[3].y);
          fn = (my) => {
            if (my < rcYMin || my > rcYMax) return null;
            let xMin = Infinity, xMax = -Infinity, hits = 0;
            for (let i = 0; i < 4; i++) {
              const a = rc[i], b = rc[(i + 1) % 4];
              if ((a.y <= my && b.y >= my) || (b.y <= my && a.y >= my)) {
                const dy = b.y - a.y;
                const x = Math.abs(dy) < 1e-12 ? (Math.min(a.x, b.x)) : a.x + (my - a.y) / dy * (b.x - a.x);
                if (x < xMin) xMin = x;
                if (x > xMax) xMax = x;
                hits++;
                if (Math.abs(dy) < 1e-12) {
                  const x2 = Math.max(a.x, b.x);
                  if (x2 > xMax) xMax = x2;
                }
              }
            }
            return hits >= 2 ? { xMin, xMax } : null;
          };
        }
        _logoExclFns.push({ fn, pad });
      }
      if (_logoExclFns.length === 1) {
        const _f0 = _logoExclFns[0].fn;
        _logoExclAtY = (my) => {
          const r = _f0(my);
          return r ? [r] : null;
        };
      } else if (_logoExclFns.length > 1) {
        // Content-aware merge: only merge when actual content (unpadded) overlaps.
        // When content zones don't overlap (physical gap exists), trim padding at
        // the boundary so field lines can resume in the gap between logos/ladders.
        _logoExclAtY = (my) => {
          const raw = [];
          for (const { fn, pad: p } of _logoExclFns) {
            const r = fn(my);
            if (r) raw.push({ xMin: r.xMin, xMax: r.xMax, pad: p });
          }
          if (raw.length === 0) return null;
          if (raw.length === 1) return [raw[0]];
          raw.sort((a, b) => a.xMin - b.xMin);
          const result = [];
          let cur = { xMin: raw[0].xMin, xMax: raw[0].xMax,
                      cMin: raw[0].xMin + raw[0].pad, cMax: raw[0].xMax - raw[0].pad };
          for (let i = 1; i < raw.length; i++) {
            const r = raw[i];
            const rCMin = r.xMin + r.pad, rCMax = r.xMax - r.pad;
            if (rCMin <= cur.cMax) {
              // Content overlaps — merge
              cur.xMin = Math.min(cur.xMin, r.xMin);
              cur.xMax = Math.max(cur.xMax, r.xMax);
              cur.cMin = Math.min(cur.cMin, rCMin);
              cur.cMax = Math.max(cur.cMax, rCMax);
            } else {
              // Content doesn't overlap — keep separate, trim padding at midpoint
              const mid = (cur.cMax + rCMin) / 2;
              result.push({ xMin: cur.xMin, xMax: Math.min(cur.xMax, mid) });
              cur = { xMin: Math.max(r.xMin, mid), xMax: r.xMax, cMin: rCMin, cMax: rCMax };
            }
          }
          result.push({ xMin: cur.xMin, xMax: cur.xMax });
          return result;
        };
      }
    }

    // Helper: draw a horizontal marking bar, splitting around logo exclusion zones.
    // _logoExclAtY returns an array of sorted non-overlapping {xMin,xMax} ranges.
    const fillBar = (x, y, w, h) => {
      if (!_logoExclAtY) { this.ctx.fillRect(x, y, w, h); return; }
      const ranges = _logoExclAtY(y + h / 2);
      if (!ranges) { this.ctx.fillRect(x, y, w, h); return; }
      const barLeft = x, barRight = x + w;
      // Draw visible segments between exclusion zones
      let cursor = barLeft;
      for (const r of ranges) {
        if (r.xMin >= barRight) break; // past bar end
        if (r.xMax <= cursor) continue; // before cursor
        // Draw segment from cursor to start of this exclusion
        if (r.xMin > cursor) {
          this.ctx.fillRect(cursor, y, r.xMin - cursor, h);
        }
        cursor = Math.max(cursor, r.xMax);
      }
      // Draw remaining segment after last exclusion
      if (cursor < barRight) {
        this.ctx.fillRect(cursor, y, barRight - cursor, h);
      }
    };

    // Compute sled lane widths per side (field markings sit adjacent, not overlapping)
    const SLED_LANE_W = 3.25, SLED_DIV_W = 0.5;
    const _sledBW = (c) => c * SLED_LANE_W + (c - 1) * SLED_DIV_W;
    let sledLeftW = 0, sledRightW = 0, sledTopW = 0, sledBottomW = 0;
    if (obj.turfSledLanes) {
      const sledCount = obj.turfSledLaneCount || 2;
      const edges = this._rotateEdges(obj.turfSledLaneEdges || ['right'], obj.rotation || 0);
      // Use raw edge names — _drawTurfSledLanes uses the same raw names to decide
      // cross-axis (left/right) vs run-axis (top/bottom) placement, so narrowing must match
      const cL = edges.includes('left');
      const cR = edges.includes('right');
      const rT = edges.includes('top');
      const rB = edges.includes('bottom');
      if (cL) sledLeftW = _sledBW(sledCount);
      if (cR) sledRightW = _sledBW(sledCount);
      if (rT) sledTopW = _sledBW(sledCount);
      if (rB) sledBottomW = _sledBW(sledCount);
    }
    // Field zone boundaries (narrowed by sled lanes on each side)
    const fieldLeft = -crossHalf + inset + sledLeftW;
    const fieldRight = crossHalf - inset - sledRightW;
    const fieldW = fieldRight - fieldLeft;

    // Center-outward placement. Field lines every 15' (5 yards), hashes every 3' (1 yard).
    // Both share the same grid origin so hashes always align perfectly with field lines.
    // "Halfway line" toggle: when ON, a line sits at center (y=0); when OFF, the two
    // innermost lines are 7.5' (2.5 yards) from center on each side.
    const lineOrigin = obj.turfLineOrigin || (obj.turfHalfwayLine !== false ? 'center' : 'offset');
    const halfwayLine = lineOrigin === 'center';
    const lineSpacing = 15;  // 5 yards between field lines
    const yardSpacing = 3;   // 1 yard between hash marks
    const runLimit = runHalf - inset - Math.max(sledTopW, sledBottomW); // max distance from center

    // Grid offset: 0 for center/edge, half-spacing for offset
    const gridOffset = (lineOrigin === 'offset') ? lineSpacing / 2 : 0; // 0 or 7.5'

    const markingLimit = runLimit; // field lines/hashes extend to turf edge
    const hasEndZones = false; // end zones not yet implemented

    // Collect field line Y positions (as Set of rounded values for hash skip)
    const lineYs = new Set();
    const fullX = fieldLeft, fullW = fieldW;
    if (hasLines) {
      if (lineOrigin === 'edge') {
        // Edge-to-edge: lines march from bottom edge toward top every 5 yards (15')
        const startY = -runLimit;
        for (let y = startY + lineSpacing; y < runLimit - 0.01; y += lineSpacing) {
          fillBar(fullX, y - lineW / 2, fullW, lineW);
          lineYs.add(Math.round(y * 1000));
        }
      } else {
        // Center-outward logic for both center and offset modes
        for (let d = gridOffset; d <= markingLimit + 0.01; d += lineSpacing) {
          if (d === 0) {
            // Center line
            fillBar(fullX, -lineW / 2, fullW, lineW);
            lineYs.add(0);
          } else {
            // Symmetric pair
            fillBar(fullX, d - lineW / 2, fullW, lineW);
            fillBar(fullX, -d - lineW / 2, fullW, lineW);
            lineYs.add(Math.round(d * 1000));
            lineYs.add(Math.round(-d * 1000));
          }
        }
      }
    }

    // Yard numbers + chevrons at every 10-yard field line
    if (hasLines && (obj.turfYardNumbers || false)) {
      // 7-segment digit definitions — each segment is [x, y, w, h] on a 5×7 normalized grid
      const segDefs = [
        [0, 0, 5, 1],    // 0: top
        [0, 0, 1, 4],    // 1: top-left
        [4, 0, 1, 4],    // 2: top-right
        [0, 3, 5, 1],    // 3: middle
        [0, 3, 1, 4],    // 4: bottom-left
        [4, 3, 1, 4],    // 5: bottom-right
        [0, 6, 5, 1],    // 6: bottom
      ];
      const digitSegs = [
        [0,1,2,4,5,6],    // 0
        [2,5],             // 1
        [0,2,3,4,6],      // 2
        [0,2,3,5,6],      // 3
        [1,2,3,5],         // 4
        [0,1,3,5,6],      // 5
        [0,1,3,4,5,6],    // 6
        [0,2,5],           // 7
        [0,1,2,3,4,5,6],  // 8
        [0,1,2,3,5,6],    // 9
      ];

      // Scale proportionally: 6' tall on a regulation 300' run, smaller for smaller turf.
      // Minimum: internal gaps in digits (e.g. "8" half-height hollow) must stay ≥ 8".
      // Tightest constraint is (digitH - 3*strokeW)/2 ≥ strokeW → digitH ≥ 5*strokeW.
      const refRunFt = 300;
      const strokeW = 8 / 12; // always 8 inches
      const minDigitH = 5 * strokeW; // ~3.33 ft — smallest before "8" gap < 8"
      const scaleFactor = runFull / refRunFt;
      const digitH = Math.max(6 * scaleFactor, minDigitH);
      const digitW = digitH * (4 / 6); // maintain 2:3 aspect
      const digitGap = lineW + (4 / 12) * 2; // always 4" from each side of the line
      const chevronH = digitH * 0.45;
      const chevronW = chevronH * 0.5;
      const chevronGap = strokeW * 1.5;
      // Top of digit is 5.625% from the sideline (9' on a 160' wide regulation field).
      // numberInset is measured from the inset boundary (inner edge of border band).
      // Numbers must be at least 20" from that inner edge.
      const minFromInner = 20 / 12; // never closer than 20" from inner edge
      const topFromInner = Math.max(fieldW * 0.05625, minFromInner);
      const numberInset = topFromInner + digitH / 2;

      // Draw one 7-segment digit at (cx, cy) as center.
      // Segments are positioned by outer bounds (digitW × digitH) but stroke is fixed 4".
      // Horizontal bars: full width, strokeW tall. Vertical bars: strokeW wide, half height.
      const drawDigit = (digit, cx, cy) => {
        const segs = digitSegs[digit];
        const left = cx - digitW / 2;
        const top = cy - digitH / 2;
        const midY = cy;                  // vertical center
        const halfH = (digitH - strokeW) / 2; // inner half (top of mid bar to top of top bar)
        for (const s of segs) {
          switch (s) {
            case 0: // top horizontal
              this.ctx.fillRect(left, top, digitW, strokeW); break;
            case 1: // top-left vertical
              this.ctx.fillRect(left, top, strokeW, halfH + strokeW); break;
            case 2: // top-right vertical
              this.ctx.fillRect(left + digitW - strokeW, top, strokeW, halfH + strokeW); break;
            case 3: // middle horizontal
              this.ctx.fillRect(left, midY - strokeW / 2, digitW, strokeW); break;
            case 4: // bottom-left vertical
              this.ctx.fillRect(left, midY - strokeW / 2, strokeW, halfH + strokeW); break;
            case 5: // bottom-right vertical
              this.ctx.fillRect(left + digitW - strokeW, midY - strokeW / 2, strokeW, halfH + strokeW); break;
            case 6: // bottom horizontal
              this.ctx.fillRect(left, top + digitH - strokeW, digitW, strokeW); break;
          }
        }
      };

      // Draw single chevron triangle pointing in direction dir
      const drawChevron = (cx, cy, dir) => {
        this.ctx.beginPath();
        this.ctx.moveTo(cx + dir * chevronW / 2, cy);
        this.ctx.lineTo(cx - dir * chevronW / 2, cy - chevronH / 2);
        this.ctx.lineTo(cx - dir * chevronW / 2, cy + chevronH / 2);
        this.ctx.closePath();
        this.ctx.fill();
      };

      // Two rows: near each long edge of the field zone, offset inward
      const rowXs = [];
      const fieldCenter = (fieldLeft + fieldRight) / 2;
      const leftRowX = fieldLeft + numberInset;
      const rightRowX = fieldRight - numberInset;
      if (rightRowX - leftRowX > digitW * 2) {
        rowXs.push(leftRowX, rightRowX);
      }

      // Yard number spacing: 5 or 10 yards
      const yardNumSpacing = (obj.turfYardNumberSpacing === 5) ? 5 : 10;
      const numSpacing = yardNumSpacing * 3; // feet between numbered lines
      // Edge mode forces sequential
      const yardNumMode = lineOrigin === 'edge' ? 'sequential' : (obj.turfYardNumberMode || 'football');

      // Build list of { y, yardNumber, chevronDir } entries
      const numEntries = [];
      const goalLineHalf = hasEndZones ? (markingLimit + inset) : runHalf;

      if (yardNumMode === 'sequential') {
        // Collect numbered line positions depending on lineOrigin
        const positions = [];
        if (lineOrigin === 'edge') {
          // Edge mode: lines at fixed Y positions from bottom edge
          const startY = -runLimit;
          for (let y = startY + lineSpacing; y < runLimit - 0.01; y += lineSpacing) {
            positions.push(y);
          }
          // Filter to only numSpacing intervals (every 5 or 10 yards from bottom)
          const filteredPos = [];
          for (const y of positions) {
            const distFromBottomYd = (y + runLimit) / 3; // distance from bottom edge in yards
            const rounded = Math.round(distFromBottomYd / yardNumSpacing) * yardNumSpacing;
            if (Math.abs(distFromBottomYd - rounded) < 0.1 && rounded >= yardNumSpacing) {
              filteredPos.push(y);
            }
          }
          for (const y of filteredPos) {
            const distFromBottomYd = (y + runLimit) / 3;
            const yardNumber = Math.round(distFromBottomYd);
            numEntries.push({ y: y, yardNumber: yardNumber, chevronDir: 0 });
          }
        } else {
          // Center/offset sequential: collect from center-outward grid
          for (let d = gridOffset; d <= markingLimit + 0.01; d += numSpacing) {
            if (d === 0) positions.push(0);
            else { positions.push(-d); positions.push(d); }
          }
          positions.sort((a, b) => a - b);

          // Sequential: number lines 10, 20, 30, ... from bottom to top
          for (let i = 0; i < positions.length; i++) {
            const yardNumber = (i + 1) * yardNumSpacing;
            numEntries.push({ y: positions[i], yardNumber: yardNumber, chevronDir: 0 });
          }
        }
      } else {
        // Football mode: count up to 50 at center, mirror back down
        // First pass: collect all d values and their yard numbers
        const _fbEntries = [];
        for (let d = gridOffset; d <= markingLimit + 0.01; d += numSpacing) {
          const yardNumber = Math.min(50, Math.round((goalLineHalf - d) / 3 / yardNumSpacing) * yardNumSpacing);
          _fbEntries.push({ d: d, yardNumber: yardNumber });
        }
        // If the outermost line rounds to 0 but has at least 1.5 yards (4.5 ft) of
        // visible turf beyond it, and the next inner line doesn't already carry
        // the minimum yard number, force it to yardNumSpacing (5 or 10)
        if (_fbEntries.length >= 2) {
          const last = _fbEntries[_fbEntries.length - 1];
          const prev = _fbEntries[_fbEntries.length - 2];
          if (last.yardNumber < yardNumSpacing && (runHalf - last.d) >= 4.5 && prev.yardNumber !== yardNumSpacing) {
            last.yardNumber = yardNumSpacing;
          }
        }
        for (const fb of _fbEntries) {
          if (fb.yardNumber < yardNumSpacing) continue;
          const yPositions = (fb.d === 0) ? [0] : [fb.d, -fb.d];
          for (const y of yPositions) {
            const chevronDir = (fb.yardNumber === 50) ? 0 : (y > 0.01) ? 1 : (y < -0.01) ? -1 : 0;
            numEntries.push({ y: y, yardNumber: fb.yardNumber, chevronDir: chevronDir });
          }
        }
      }

      // Draw all yard number entries
      for (const entry of numEntries) {
        const tens = Math.floor(entry.yardNumber / 10);
        const ones = entry.yardNumber % 10;
        // Three-digit numbers: draw hundreds too
        const hundreds = Math.floor(entry.yardNumber / 100);
        const isThreeDigit = entry.yardNumber >= 100;

        // Skip yard numbers that would be clipped at the turf edge
        // After 90° rotation, local X maps to the run axis — compute half-extent
        let _ynHalfExtent;
        if (isThreeDigit) {
          _ynHalfExtent = digitGap + digitW * 1.5;
        } else if (tens === 0) {
          _ynHalfExtent = digitGap / 2 + digitW;
        } else {
          _ynHalfExtent = digitGap / 2 + digitW;
          if (entry.chevronDir !== 0) _ynHalfExtent += chevronGap + chevronW;
        }
        if (Math.abs(entry.y) + _ynHalfExtent > runHalf - inset) continue;

        for (const rx of rowXs) {
          this.ctx.save();
          this.ctx.translate(rx, entry.y);
          const rot = (rx > 0) ? -Math.PI / 2 : Math.PI / 2;
          this.ctx.rotate(rot);

          if (isThreeDigit) {
            const localHundX = -digitGap - digitW;
            const localTensX = 0;
            const localOnesX = digitGap + digitW;
            drawDigit(hundreds, localHundX, 0);
            drawDigit(tens % 10, localTensX, 0);
            drawDigit(ones, localOnesX, 0);
          } else if (tens === 0) {
            // Single-digit: draw on the right side of the field line (positive local X)
            const localX = digitGap / 2 + digitW / 2;
            drawDigit(ones, localX, 0);
          } else {
            const localTensX = -digitGap / 2 - digitW / 2;
            const localOnesX = digitGap / 2 + digitW / 2;
            drawDigit(tens, localTensX, 0);
            drawDigit(ones, localOnesX, 0);

            // Single chevron on the side toward the nearest end zone (football mode only)
            if (entry.chevronDir !== 0) {
              const localChevDir = (rx > 0) ? -entry.chevronDir : entry.chevronDir;
              const chevX = (localChevDir > 0)
                ? localOnesX + digitW / 2 + chevronGap + chevronW / 2
                : localTensX - digitW / 2 - chevronGap - chevronW / 2;
              drawChevron(chevX, 0, localChevDir);
            }
          }

          this.ctx.restore();
        }
      }
    }

    // Hash marks — every 3' from same grid origin, skip field line positions
    if (hasHashes) {
      const hashLen = 2; // 2 feet long
      const sides = obj.turfHashSides || 'long';
      const drawLong = (sides === 'long' || sides === 'all');
      const drawShort = (sides === 'short' || sides === 'all');

      // Hash grid aligns to field line grid.
      // Edge mode: march from bottom edge every 3'. Center/offset: symmetric from center.
      const hashStart = gridOffset % yardSpacing; // 0 when center, 1.5 when offset (7.5%3=1.5)

      if (drawLong) {
        if (lineOrigin === 'edge') {
          // Edge-to-edge: hashes from bottom edge every 3', skip field lines
          const startY = -runLimit;
          for (let y = startY + yardSpacing; y < runLimit - 0.01; y += yardSpacing) {
            if (hasLines && lineYs.has(Math.round(y * 1000))) continue;
            fillBar(fieldLeft, y - lineW / 2, hashLen, lineW);
            fillBar(fieldRight - hashLen, y - lineW / 2, hashLen, lineW);
          }
        } else {
          for (let d = hashStart; d <= markingLimit + 0.01; d += yardSpacing) {
            if (d === 0) {
              if (hasLines && lineYs.has(0)) continue;
              fillBar(fieldLeft, -lineW / 2, hashLen, lineW);
              fillBar(fieldRight - hashLen, -lineW / 2, hashLen, lineW);
            } else {
              const skip = hasLines && (lineYs.has(Math.round(d * 1000)) || lineYs.has(Math.round(-d * 1000)));
              if (!skip) {
                fillBar(fieldLeft, d - lineW / 2, hashLen, lineW);
                fillBar(fieldRight - hashLen, d - lineW / 2, hashLen, lineW);
                fillBar(fieldLeft, -d - lineW / 2, hashLen, lineW);
                fillBar(fieldRight - hashLen, -d - lineW / 2, hashLen, lineW);
              }
            }
          }
        }
      }

      // Center hashes — two rows at 1/3 and 2/3 across the field zone
      if (drawLong && (obj.turfCenterHashes || false)) {
        const third = fieldW / 3;
        const cx1 = fieldLeft + third;       // 1/3 mark
        const cx2 = fieldLeft + third * 2;   // 2/3 mark
        if (lineOrigin === 'edge') {
          const startY = -runLimit;
          for (let y = startY + yardSpacing; y < runLimit - 0.01; y += yardSpacing) {
            if (hasLines && lineYs.has(Math.round(y * 1000))) continue;
            fillBar(cx1 - hashLen / 2, y - lineW / 2, hashLen, lineW);
            fillBar(cx2 - hashLen / 2, y - lineW / 2, hashLen, lineW);
          }
        } else {
          for (let d = hashStart; d <= markingLimit + 0.01; d += yardSpacing) {
            if (d === 0) {
              if (hasLines && lineYs.has(0)) continue;
              fillBar(cx1 - hashLen / 2, -lineW / 2, hashLen, lineW);
              fillBar(cx2 - hashLen / 2, -lineW / 2, hashLen, lineW);
            } else {
              const skip = hasLines && (lineYs.has(Math.round(d * 1000)) || lineYs.has(Math.round(-d * 1000)));
              if (!skip) {
                fillBar(cx1 - hashLen / 2, d - lineW / 2, hashLen, lineW);
                fillBar(cx2 - hashLen / 2, d - lineW / 2, hashLen, lineW);
                fillBar(cx1 - hashLen / 2, -d - lineW / 2, hashLen, lineW);
                fillBar(cx2 - hashLen / 2, -d - lineW / 2, hashLen, lineW);
              }
            }
          }
        }
      }

      if (drawShort) {
        const crossLimit = crossHalf - inset; // iterate full range, then bounds-check
        const shortRunStart = markingLimit;
        for (let d = hashStart; d <= crossLimit + 0.01; d += yardSpacing) {
          // Short hashes are vertical bars — skip any that fall outside field zone
          const positions = (d === 0) ? [0] : [d, -d];
          for (const px of positions) {
            if (px < fieldLeft + 0.01 || px > fieldRight - 0.01) continue;
            this.ctx.fillRect(px - lineW / 2, -shortRunStart, lineW, hashLen);
            this.ctx.fillRect(px - lineW / 2, shortRunStart - hashLen, lineW, hashLen);
          }
        }
      }
    }

    // Sled/Sprint Lanes — drawn in same coordinate space as field markings
    // so they respect the lengthwise/widthwise direction toggle
    if (obj.turfSledLanes) {
      this._drawTurfSledLanes(obj, crossHalf, runHalf, inset, lineW, color);
    }

    this.ctx.restore();
  }

  // Rotate edge names by the object's world rotation so that stored edge names
  // (which are screen-relative at placement time) map to the correct turf-local edges.
  _rotateEdges(edges, rotation) {
    // Normalize to 0..3 quarter-turns (clockwise)
    const q = (((Math.round(rotation / (Math.PI / 2)) % 4) + 4) % 4);
    if (q === 0) return edges;
    const cycle = ['top', 'right', 'bottom', 'left'];
    return edges.map(e => {
      const i = cycle.indexOf(e);
      return i >= 0 ? cycle[(i + q) % 4] : e;
    });
  }

  // Draw sled/sprint lanes along the edges of the turf
  // Drawn inside the rotated coordinate space so they follow the direction toggle
  _drawTurfSledLanes(obj, crossHalf, runHalf, inset, lineW, color) {
    const count = obj.turfSledLaneCount || 2;
    const rawEdges = obj.turfSledLaneEdges || ['right'];
    const LANE_W = 3.25;  // ft per lane
    const DIV_W = 0.5;    // ft divider between lanes

    // Compensate for object world rotation so edges match what user sees on screen
    const edges = this._rotateEdges(rawEdges, obj.rotation || 0);

    // Map edges after rotation compensation
    const cL = edges.includes('left');
    const cR = edges.includes('right');
    const rT = edges.includes('top');
    const rB = edges.includes('bottom');

    const ctx = this.ctx;

    // Clip to turf interior
    ctx.save();
    ctx.beginPath();
    ctx.rect(-crossHalf + inset, -runHalf + inset, (crossHalf - inset) * 2, (runHalf - inset) * 2);
    ctx.clip();

    const drawBlock = (sideCount, startX, direction, myCrossHalf, myRunHalf, barFn) => {
      if (sideCount <= 0) return;
      const blockW = sideCount * LANE_W + (sideCount - 1) * DIV_W;
      const blockX = direction > 0 ? startX : startX - blockW;
      const runStart = -myRunHalf + inset;
      const runLen = (myRunHalf - inset) * 2;

      // Semi-transparent lane zone background
      ctx.save();
      ctx.globalAlpha = 0.12;
      ctx.fillStyle = '#000000';
      ctx.fillRect(blockX, runStart, blockW, runLen);
      ctx.restore();

      // Outer boundary lines
      ctx.fillStyle = color;
      ctx.fillRect(blockX - lineW / 2, runStart, lineW, runLen);
      ctx.fillRect(blockX + blockW - lineW / 2, runStart, lineW, runLen);

      // Divider lines between lanes
      for (let i = 1; i < sideCount; i++) {
        const dx = blockX + i * (LANE_W + DIV_W) - DIV_W / 2;
        ctx.fillRect(dx - lineW / 2, runStart, lineW, runLen);
      }

      // Start/finish lines at top and bottom
      if (obj.turfSledLaneStartFinish) {
        barFn(blockX, runStart - lineW / 2, blockW, lineW);
        barFn(blockX, runStart + runLen - lineW / 2, blockW, lineW);
      }

      // Interval yard markers across each lane
      const interval = obj.turfSledLaneInterval || 0;
      if (interval > 0) {
        const spacingFt = interval * 3; // 5yd = 15ft, 10yd = 30ft
        const markerW = 2 / 12; // 2 inches
        const hasNums = obj.turfSledLaneNumbers || false;

        // Pre-compute number dimensions for gap splitting
        const strokeW = 3 / 12; // 3 inches
        const digitH = Math.max(LANE_W * 0.55, 5 * strokeW);
        const digitW = digitH * (4 / 6);
        const digitGap = strokeW * 0.5;
        const numPad = 3 / 12; // 3 inch clearance around number
        // Number sits upright in lane — its width is the total digit span
        const maxDigits = 2; // max "99" yards
        const maxNumW = maxDigits * digitW + (maxDigits - 1) * digitGap;
        const numClearHalfX = maxNumW / 2 + numPad; // half-width of gap to cut in marker

        // 7-segment digit definitions
        const digitSegs7 = [
          [0,1,2,4,5,6], [2,5], [0,2,3,4,6], [0,2,3,5,6], [1,2,3,5],
          [0,1,3,5,6], [0,1,3,4,5,6], [0,2,5], [0,1,2,3,4,5,6], [0,1,2,3,5,6],
        ];

        const drawDigit = (digit, cx, cy) => {
          const segs = digitSegs7[digit];
          const left = cx - digitW / 2;
          const top = cy - digitH / 2;
          const midY = cy;
          const halfH = (digitH - strokeW) / 2;
          for (const s of segs) {
            switch (s) {
              case 0: ctx.fillRect(left, top, digitW, strokeW); break;
              case 1: ctx.fillRect(left, top, strokeW, halfH + strokeW); break;
              case 2: ctx.fillRect(left + digitW - strokeW, top, strokeW, halfH + strokeW); break;
              case 3: ctx.fillRect(left, midY - strokeW / 2, digitW, strokeW); break;
              case 4: ctx.fillRect(left, midY - strokeW / 2, strokeW, halfH + strokeW); break;
              case 5: ctx.fillRect(left + digitW - strokeW, midY - strokeW / 2, strokeW, halfH + strokeW); break;
              case 6: ctx.fillRect(left, top + digitH - strokeW, digitW, strokeW); break;
            }
          }
        };

        for (let i = 1; i * spacingFt < runLen; i++) {
          const y = runStart + runLen - i * spacingFt;

          if (!hasNums) {
            // No numbers — full-width marker line
            ctx.fillRect(blockX, y - markerW / 2, blockW, markerW);
          } else {
            // Split marker line around each lane's number
            // Build sorted list of gaps (one per lane center)
            const gaps = [];
            for (let lane = 0; lane < sideCount; lane++) {
              const laneCenter = blockX + lane * (LANE_W + DIV_W) + LANE_W / 2;
              gaps.push({ left: laneCenter - numClearHalfX, right: laneCenter + numClearHalfX });
            }
            // Draw marker segments between gaps
            let cursor = blockX;
            for (const gap of gaps) {
              if (cursor < gap.left) {
                ctx.fillRect(cursor, y - markerW / 2, gap.left - cursor, markerW);
              }
              cursor = gap.right;
            }
            if (cursor < blockX + blockW) {
              ctx.fillRect(cursor, y - markerW / 2, blockX + blockW - cursor, markerW);
            }
          }
        }

        // Yard numbers — rotated to read from the start-line direction
        // "1" is visually narrow (just strokeW), so use per-digit visual width for centering
        const digitVisW = (d) => d === 1 ? strokeW : digitW;
        // drawDigit draws "1" right-aligned in its digitW box; this offset
        // shifts it so its visual center aligns with the target cx
        const digitDrawOffset = (d) => d === 1 ? -(digitW - strokeW) / 2 : 0;

        if (hasNums) {
          ctx.fillStyle = color;
          for (let i = 1; i * spacingFt < runLen; i++) {
            const y = runStart + runLen - i * spacingFt;
            const yardNum = i * interval;
            const digs = String(yardNum).split('').map(Number);
            const totalVisW = digs.reduce((s, d) => s + digitVisW(d), 0) + (digs.length - 1) * digitGap;

            for (let lane = 0; lane < sideCount; lane++) {
              const laneCenter = blockX + lane * (LANE_W + DIV_W) + LANE_W / 2;
              ctx.save();
              ctx.translate(laneCenter, y);
              let dx = -totalVisW / 2;
              for (const d of digs) {
                const vw = digitVisW(d);
                const cx = dx + vw / 2 + digitDrawOffset(d);
                drawDigit(d, cx, 0);
                dx += vw + digitGap;
              }
              ctx.restore();
            }
          }
        }
      }
    };

    // Simple fillRect wrapper for run-axis lanes (no logo exclusions needed)
    const simpleFillBar = (x, y, w, h) => ctx.fillRect(x, y, w, h);

    // Cross-axis lanes (left/right of field lines)
    if (cL) drawBlock(count, -crossHalf + inset, +1, crossHalf, runHalf, simpleFillBar);
    if (cR) drawBlock(count, crossHalf - inset, -1, crossHalf, runHalf, simpleFillBar);

    // Run-axis lanes (top/bottom — rotate canvas -90°, swap dimensions, reuse drawBlock)
    if (rT || rB) {
      ctx.save();
      ctx.rotate(-Math.PI / 2);
      // After -90° rotation: old +X becomes +Y, old +Y becomes -X
      // crossHalf/runHalf swap roles
      if (rB) drawBlock(count, -runHalf + inset, +1, runHalf, crossHalf, simpleFillBar);
      if (rT) drawBlock(count, runHalf - inset, -1, runHalf, crossHalf, simpleFillBar);
      ctx.restore();
    }

    ctx.restore(); // restore clip
  }

  // Draw deferred turf dimension labels (after turf overage overlay)
  _drawTurfLabels() {
    if (!this._pendingTurfLabels || this._pendingTurfLabels.length === 0) return;
    const dpr = this._dpr || 1;

    for (const label of this._pendingTurfLabels) {
      const { sx, sy, text, halfTw, halfH, padX, padY, cornerR, screenFontSize, alpha } = label;
      const bx = sx - halfTw - padX, by = sy - halfH - padY;
      const bw = (halfTw + padX) * 2, bh = (halfH + padY) * 2;

      this.ctx.save();
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Pill fill at full opacity so overage can't bleed through
      this.ctx.fillStyle = this._tc.labelBg;
      this.ctx.beginPath();
      this.ctx.moveTo(bx + cornerR, by);
      this.ctx.lineTo(bx + bw - cornerR, by);
      this.ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + cornerR);
      this.ctx.lineTo(bx + bw, by + bh - cornerR);
      this.ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - cornerR, by + bh);
      this.ctx.lineTo(bx + cornerR, by + bh);
      this.ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - cornerR);
      this.ctx.lineTo(bx, by + cornerR);
      this.ctx.quadraticCurveTo(bx, by, bx + cornerR, by);
      this.ctx.closePath();
      this.ctx.fill();

      // Border and text fade with zoom
      this.ctx.globalAlpha = alpha;
      this.ctx.strokeStyle = this._tc.labelBorder;
      this.ctx.lineWidth = 0.5;
      this.ctx.stroke();

      this.ctx.font = `600 ${screenFontSize}px Arial`;
      this.ctx.fillStyle = this._tc.labelText;
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(text, sx, sy + screenFontSize * 0.07);

      this.ctx.restore();
    }
  }

  /** Return array of AABBs for every door swing arc across all rooms. */
  _getDoorSwingBounds() {
    const bounds = [];
    const allRooms = this.rooms.concat(this.currentRoom && this.currentRoom.closed ? [this.currentRoom] : []);
    for (const room of allRooms) {
      if (!room || !room.closed) continue;
      for (const door of room.doors) {
        if (!door.swing || door.type === 'roller') continue;
        const wall = door.wall;
        const hingePoint = this.paramToPoint(wall, door.hingeParam);
        const endPoint = this.paramToPoint(wall, door.endParam);
        const doorWidth = this.getDistance(hingePoint, endPoint);
        if (doorWidth <= 0) continue;

        const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
        const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
        if (wLen === 0) continue;
        const wallDirX = wdx / wLen, wallDirY = wdy / wLen;
        const perpX = -wallDirY, perpY = wallDirX;
        const s = door.swing;

        if (door.type === 'double') {
          const halfW = doorWidth / 2;
          const midX = (hingePoint.x + endPoint.x) / 2, midY = (hingePoint.y + endPoint.y) / 2;
          // Left leaf arc (pivot = hingePoint)
          const closedL = Math.atan2(midY - hingePoint.y, midX - hingePoint.x);
          const openL = Math.atan2(perpY * s.perpSign, perpX * s.perpSign);
          let diffL = openL - closedL; while (diffL > Math.PI) diffL -= 2 * Math.PI; while (diffL < -Math.PI) diffL += 2 * Math.PI;
          bounds.push(this._arcAABB(hingePoint.x, hingePoint.y, halfW, closedL, openL, diffL < 0));
          // Right leaf arc (pivot = endPoint)
          const closedR = Math.atan2(midY - endPoint.y, midX - endPoint.x);
          const openR = Math.atan2(perpY * s.perpSign, perpX * s.perpSign);
          let diffR = openR - closedR; while (diffR > Math.PI) diffR -= 2 * Math.PI; while (diffR < -Math.PI) diffR += 2 * Math.PI;
          bounds.push(this._arcAABB(endPoint.x, endPoint.y, halfW, closedR, openR, diffR < 0));
        } else {
          // Single door
          const hingeEnd = s.hingeEnd || 'hinge';
          const pivotPt = hingeEnd === 'hinge' ? hingePoint : endPoint;
          const otherPt = hingeEnd === 'hinge' ? endPoint : hingePoint;
          const closedAngle = Math.atan2(otherPt.y - pivotPt.y, otherPt.x - pivotPt.x);
          const openAngle = Math.atan2(perpY * s.perpSign, perpX * s.perpSign);
          let diff = openAngle - closedAngle; while (diff > Math.PI) diff -= 2 * Math.PI; while (diff < -Math.PI) diff += 2 * Math.PI;
          bounds.push(this._arcAABB(pivotPt.x, pivotPt.y, doorWidth, closedAngle, openAngle, diff < 0));
        }
      }
    }
    return bounds;
  }

  _drawRoomLabels(scale) {
    if (!this._pendingRoomLabels || this._pendingRoomLabels.length === 0) return;

    const labels = this._pendingRoomLabels.slice();
    // Larger rooms get placement priority
    labels.sort((a, b) => b.area - a.area);

    // Collect bounding boxes for all closed rooms (used for overlap checks)
    const roomBounds = [];
    const allRooms = this.rooms.concat(this.currentRoom && this.currentRoom.closed ? [this.currentRoom] : []);
    for (const r of allRooms) {
      if (!r.closed || r.walls.length < 3) continue;
      let rMinX = Infinity, rMinY = Infinity, rMaxX = -Infinity, rMaxY = -Infinity;
      r.walls.forEach(w => { rMinX = Math.min(rMinX, w.start.x, w.end.x); rMinY = Math.min(rMinY, w.start.y, w.end.y); rMaxX = Math.max(rMaxX, w.start.x, w.end.x); rMaxY = Math.max(rMaxY, w.start.y, w.end.y); });
      roomBounds.push({ room: r, minX: rMinX, minY: rMinY, maxX: rMaxX, maxY: rMaxY });
    }

    // Collect door swing arc bounding boxes for overlap avoidance
    const doorSwingBounds = this._getDoorSwingBounds();

    // Collect all wall segments for collision checking
    const allWallSegments = [];
    for (const r of allRooms) {
      if (!r.closed || r.walls.length < 3) continue;
      for (const w of r.walls) {
        allWallSegments.push({ x1: w.start.x, y1: w.start.y, x2: w.end.x, y2: w.end.y, room: r });
      }
    }

    // Placed wall dimension labels (set by _drawWallLabels which runs first)
    const wallLabels = this._placedWallLabels || [];

    const placed = []; // { x, y, halfW, halfH } of already-placed room labels

    const rectsOverlap = (ax, ay, ahw, ahh, bx, by, bhw, bhh) =>
      Math.abs(ax - bx) < (ahw + bhw) && Math.abs(ay - by) < (ahh + bhh);

    const overlapAmount = (ax, ay, ahw, ahh, bx, by, bhw, bhh) => {
      const ox = Math.max(0, (ahw + bhw) - Math.abs(ax - bx));
      const oy = Math.max(0, (ahh + bhh) - Math.abs(ay - by));
      return ox * oy;
    };

    // Liang-Barsky segment vs AABB intersection test
    const segIntersectsRect = (sx1, sy1, sx2, sy2, rMinX, rMinY, rMaxX, rMaxY) => {
      let t0 = 0, t1 = 1;
      const dx = sx2 - sx1, dy = sy2 - sy1;
      const p = [-dx, dx, -dy, dy];
      const q = [sx1 - rMinX, rMaxX - sx1, sy1 - rMinY, rMaxY - sy1];
      for (let i = 0; i < 4; i++) {
        if (Math.abs(p[i]) < 1e-10) { if (q[i] < -1e-10) return false; }
        else {
          const r = q[i] / p[i];
          if (p[i] < 0) { if (r > t1) return false; t0 = Math.max(t0, r); }
          else { if (r < t0) return false; t1 = Math.min(t1, r); }
        }
      }
      return t0 <= t1;
    };

    // Wall thickness padding in world coords (accounts for rendered wall width)
    const wallPad = (this.getWallPixelWidth(scale) / scale) / 2 + 2 / scale;

    for (const label of labels) {
      const halfW = label.boxW / 2, halfH = label.boxH / 2;
      const roomMidY = (label.minY + label.maxY) / 2;

      // Helper: compute overlap score for a candidate position
      const scoreCandidate = (cand) => {
        let totalOverlap = 0;

        // Check against already-placed room labels
        for (const p of placed) {
          if (rectsOverlap(cand.x, cand.y, halfW, halfH, p.x, p.y, p.halfW, p.halfH)) {
            totalOverlap += overlapAmount(cand.x, cand.y, halfW, halfH, p.x, p.y, p.halfW, p.halfH);
          }
        }

        // Check against other rooms' bounding boxes (not our own room)
        for (const rb of roomBounds) {
          if (rb.room === label.room) continue;
          const rbCx = (rb.minX + rb.maxX) / 2, rbCy = (rb.minY + rb.maxY) / 2;
          const rbHw = (rb.maxX - rb.minX) / 2, rbHh = (rb.maxY - rb.minY) / 2;
          if (rectsOverlap(cand.x, cand.y, halfW, halfH, rbCx, rbCy, rbHw, rbHh)) {
            totalOverlap += overlapAmount(cand.x, cand.y, halfW, halfH, rbCx, rbCy, rbHw, rbHh);
          }
        }

        // Check against door swing arc bounding boxes
        for (const dsb of doorSwingBounds) {
          const dsCx = (dsb.minX + dsb.maxX) / 2, dsCy = (dsb.minY + dsb.maxY) / 2;
          const dsHw = (dsb.maxX - dsb.minX) / 2, dsHh = (dsb.maxY - dsb.minY) / 2;
          if (rectsOverlap(cand.x, cand.y, halfW, halfH, dsCx, dsCy, dsHw, dsHh)) {
            totalOverlap += overlapAmount(cand.x, cand.y, halfW, halfH, dsCx, dsCy, dsHw, dsHh);
          }
        }

        // Check against wall segments (with thickness padding)
        if (totalOverlap === 0) {
          const lMinX = cand.x - halfW - wallPad, lMaxX = cand.x + halfW + wallPad;
          const lMinY = cand.y - halfH - wallPad, lMaxY = cand.y + halfH + wallPad;
          for (const ws of allWallSegments) {
            if (ws.room === label.room) continue;
            if (segIntersectsRect(ws.x1, ws.y1, ws.x2, ws.y2, lMinX, lMinY, lMaxX, lMaxY)) {
              totalOverlap += halfW * halfH;
              break;
            }
          }
        }

        // Check against placed wall dimension labels (no extra padding — near-miss is readable)
        if (totalOverlap === 0) {
          for (const wl of wallLabels) {
            if (rectsOverlap(cand.x, cand.y, halfW, halfH, wl.x, wl.y, wl.halfW, wl.halfH)) {
              totalOverlap += overlapAmount(cand.x, cand.y, halfW, halfH, wl.x, wl.y, wl.halfW, wl.halfH);
            }
          }
        }

        return totalOverlap;
      };

      // === Zone-based placement with strict priority ===
      // 1. ABOVE (near-to-far scan from just above room)
      // 2. BELOW (near-to-far scan)
      // 3. LEFT
      // 4. RIGHT
      // 5. External fallback (never inside the room)

      let bestCandidate = null;

      // --- Zone 1: ABOVE (near-to-far scan) ---
      // Start just above the room and step upward (farther away).
      // Take the first clear position (closest to room).
      const aboveSteps = 24;
      const roomHeight = label.maxY - label.minY;
      const aboveStart = label.aboveY; // just above room + gap
      const aboveFar = aboveStart - roomHeight; // one room-height above
      for (let i = 0; i <= aboveSteps; i++) {
        const cand = { x: label.roomCenterX, y: aboveStart - (aboveStart - aboveFar) * i / aboveSteps };
        if (scoreCandidate(cand) === 0) {
          bestCandidate = cand;
          break;
        }
      }

      // --- Zone 2: BELOW (near-to-far scan) ---
      if (!bestCandidate) {
        const belowSteps = 12;
        const belowStart = label.belowY; // just below room + gap
        const belowFar = belowStart + roomHeight / 2; // half room-height below
        for (let i = 0; i <= belowSteps; i++) {
          const cand = { x: label.roomCenterX, y: belowStart + (belowFar - belowStart) * i / belowSteps };
          if (scoreCandidate(cand) === 0) {
            bestCandidate = cand;
            break;
          }
        }
      }

      // --- Zone 3: LEFT ---
      if (!bestCandidate) {
        const leftCand = { x: label.minX - label.gap - halfW, y: roomMidY };
        if (scoreCandidate(leftCand) === 0) bestCandidate = leftCand;
      }

      // --- Zone 4: RIGHT ---
      if (!bestCandidate) {
        const rightCand = { x: label.maxX + label.gap + halfW, y: roomMidY };
        if (scoreCandidate(rightCand) === 0) bestCandidate = rightCand;
      }

      // --- Zone 5: External fallback — never inside the room ---
      if (!bestCandidate) {
        // Try ABOVE with minimum gap (just clear of room top edge)
        const minAbove = { x: label.roomCenterX, y: label.minY - halfH - 4 / scale };
        if (scoreCandidate(minAbove) === 0) {
          bestCandidate = minAbove;
        } else {
          // Try BELOW with minimum gap
          const minBelow = { x: label.roomCenterX, y: label.maxY + halfH + 4 / scale };
          if (scoreCandidate(minBelow) === 0) {
            bestCandidate = minBelow;
          } else {
            // Last resort: force ABOVE at original aboveY (may overlap a dim label, but never inside room)
            bestCandidate = { x: label.roomCenterX, y: label.aboveY };
          }
        }
      }

      const cx = bestCandidate.x, cy = bestCandidate.y;
      placed.push({ x: cx, y: cy, halfW, halfH });

      // Draw box and text in screen space for crisp rendering
      const rlDpr = this._dpr || 1;
      this.ctx.save();
      this.ctx.setTransform(rlDpr, 0, 0, rlDpr, 0, 0);

      const _rls = this.worldToScreen(cx, cy);
      const screenCx = _rls.x;
      const screenCy = _rls.y;
      const screenNameFont = Math.round(label.nameFontSize * scale);
      const screenAreaFont = Math.round(label.areaFontSize * scale);
      const screenLineH = screenNameFont * 1.3;
      const screenPadX = 12;
      const screenPadY = 6;
      const screenCornerR = 4;

      // Measure text widths in screen space for accurate box sizing
      this.ctx.font = `700 ${screenNameFont}px Arial`;
      const screenNameW = label.nameText ? this.ctx.measureText(label.nameText).width : 0;
      this.ctx.font = `${screenAreaFont}px Arial`;
      const screenAreaW = this.ctx.measureText(label.areaText).width;
      const screenMaxTextW = Math.max(screenNameW, screenAreaW);
      const screenBoxW = screenMaxTextW + screenPadX * 2;
      const screenBoxH = (label.nameText ? screenLineH * 2 : screenLineH) + screenPadY * 2;

      const screenBoxX = screenCx - screenBoxW / 2;
      const screenBoxY = screenCy - screenBoxH / 2;

      this.ctx.fillStyle = this._tc.labelBg;
      this.ctx.lineWidth = 1;
      // Label drop shadow — applied to fill only
      this.ctx.shadowColor = 'rgba(0,0,0,0.22)';
      this.ctx.shadowBlur = 12;
      this.ctx.shadowOffsetX = -2;
      this.ctx.shadowOffsetY = 3;
      this.ctx.beginPath();
      this.ctx.moveTo(screenBoxX + screenCornerR, screenBoxY);
      this.ctx.lineTo(screenBoxX + screenBoxW - screenCornerR, screenBoxY);
      this.ctx.quadraticCurveTo(screenBoxX + screenBoxW, screenBoxY, screenBoxX + screenBoxW, screenBoxY + screenCornerR);
      this.ctx.lineTo(screenBoxX + screenBoxW, screenBoxY + screenBoxH - screenCornerR);
      this.ctx.quadraticCurveTo(screenBoxX + screenBoxW, screenBoxY + screenBoxH, screenBoxX + screenBoxW - screenCornerR, screenBoxY + screenBoxH);
      this.ctx.lineTo(screenBoxX + screenCornerR, screenBoxY + screenBoxH);
      this.ctx.quadraticCurveTo(screenBoxX, screenBoxY + screenBoxH, screenBoxX, screenBoxY + screenBoxH - screenCornerR);
      this.ctx.lineTo(screenBoxX, screenBoxY + screenCornerR);
      this.ctx.quadraticCurveTo(screenBoxX, screenBoxY, screenBoxX + screenCornerR, screenBoxY);
      this.ctx.closePath();
      this.ctx.fill();
      // Clear shadow before stroke and text (prevents double-shadow / blurry text)
      this.ctx.shadowColor = 'transparent';
      this.ctx.strokeStyle = this._tc.labelBorder;
      this.ctx.stroke();

      // Draw text
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      if (label.nameText) {
        this.ctx.font = `700 ${screenNameFont}px Arial`;
        this.ctx.fillStyle = this._tc.roomLabelText;
        this.ctx.fillText(label.nameText, screenCx, screenCy - screenLineH * 0.35);
      }
      this.ctx.font = `${screenAreaFont}px Arial`;
      this.ctx.fillStyle = this._tc.roomLabelText;
      this.ctx.fillText(label.areaText, screenCx, screenCy + screenLineH * (label.nameText ? 0.45 : 0));

      this.ctx.restore();

      // Persist hit-box for click-to-edit
      if (this._clickableLabels) {
        this._clickableLabels.push({
          type: 'room', room: label.room,
          sx: screenCx, sy: screenCy, hw: screenBoxW / 2, hh: screenBoxH / 2
        });
      }
    }
  }

  // ── Exterior Zone Fill & Area ──────────────

  /** Compute exterior zones (cached) and associate each with its nearest room.
   *  Returns { zones: [...], areaByRoom: Map<room, totalExtraArea> } */
  _getExteriorZones() {
    if (!this.wallSegmentGroups || this.wallSegmentGroups.length === 0) return { zones: [], areaByRoom: new Map() };
    if (!this.rooms || this.rooms.length === 0) return { zones: [], areaByRoom: new Map() };

    // Collect closed room polygons
    const roomPolygons = [];
    const roomRefs = []; // parallel array: roomPolygons[i] came from roomRefs[i]
    for (const room of this.rooms) {
      if (!room || !room.closed || room.type !== 'add') continue;
      const geo = this._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      roomPolygons.push(geo.roomPolygon);
      roomRefs.push(room);
    }
    if (roomPolygons.length === 0) return { zones: [], areaByRoom: new Map() };

    // Collect ALL wall segment group segments (both 'wall' and 'line' types)
    const allSegs = [];
    for (const group of this.wallSegmentGroups) {
      for (const seg of group.segments) {
        allSegs.push(seg);
      }
    }
    if (allSegs.length === 0) return { zones: [], areaByRoom: new Map() };

    // Filter to exterior segments: at least one endpoint outside or on boundary
    const exteriorSegs = [];
    for (const seg of allSegs) {
      let sInsideAny = false, eInsideAny = false;
      for (const poly of roomPolygons) {
        if (RoomDesignerMath.pointInPolygon(seg.start.x, seg.start.y, poly)) sInsideAny = true;
        if (RoomDesignerMath.pointInPolygon(seg.end.x, seg.end.y, poly)) eInsideAny = true;
      }
      // On boundary check
      const onBoundary = (pt) => {
        for (const poly of roomPolygons) {
          for (let i = 0; i < poly.length; i++) {
            const j = (i + 1) % poly.length;
            if (RoomDesignerMath.distanceToWall(pt, { start: poly[i], end: poly[j] }) < 0.1) return true;
          }
        }
        return false;
      };
      const sOnBoundary = !sInsideAny && onBoundary(seg.start);
      const eOnBoundary = !eInsideAny && onBoundary(seg.end);
      if (!sInsideAny || !eInsideAny || sOnBoundary || eOnBoundary) {
        exteriorSegs.push(seg);
      }
    }
    if (exteriorSegs.length === 0) return { zones: [], areaByRoom: new Map() };

    // Build fingerprint for caching
    let fp = '';
    for (let i = 0; i < roomRefs.length; i++) {
      fp += this._roomGeoFingerprint(roomRefs[i]) + '|';
    }
    for (const seg of exteriorSegs) {
      fp += seg.start.x.toFixed(3) + ',' + seg.start.y.toFixed(3) + '-' + seg.end.x.toFixed(3) + ',' + seg.end.y.toFixed(3) + '|';
    }

    if (this._extZoneCache && this._extZoneCache.fp === fp) return this._extZoneCache.result;

    // Compute zones via DCEL
    const zones = RoomDesignerMath.computeExteriorZones(roomPolygons, exteriorSegs);

    // Associate each zone with its nearest room
    const areaByRoom = new Map();
    for (const zone of zones) {
      let bestRoom = null, bestDist = Infinity;
      for (let ri = 0; ri < roomRefs.length; ri++) {
        const room = roomRefs[ri];
        for (const w of room.walls) {
          const d = RoomDesignerMath.distanceToWall(zone.centroid, w);
          if (d < bestDist) { bestDist = d; bestRoom = room; }
        }
      }
      if (bestRoom) {
        areaByRoom.set(bestRoom, (areaByRoom.get(bestRoom) || 0) + zone.area);
      }
    }

    const result = { zones, areaByRoom };
    this._extZoneCache = { fp, result };
    return result;
  }

  /** Draw exterior zone fill polygons (same light blue as room fill). */
  _drawExteriorZoneFills(zones) {
    if (!zones || zones.length === 0) return;
    const _overlay = window.roomDesignerOverlay;
    if (this._bgTraceActive && (!_overlay || _overlay.currentStep === 'draw')) return;
    const dimColor = this._tc.roomGridDim;
    for (const zone of zones) {
      const poly = zone.polygon;
      if (!poly || poly.length < 3) continue;
      // Dim grid inside exterior zones (dark mode only)
      if (dimColor) {
        this.ctx.fillStyle = dimColor;
        this.ctx.beginPath();
        this.ctx.moveTo(poly[0].x, poly[0].y);
        for (let i = 1; i < poly.length; i++) this.ctx.lineTo(poly[i].x, poly[i].y);
        this.ctx.closePath();
        this.ctx.fill();
      }
      this.ctx.fillStyle = this._tc.roomFill;
      this.ctx.beginPath();
      this.ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) this.ctx.lineTo(poly[i].x, poly[i].y);
      this.ctx.closePath();
      this.ctx.fill();
    }
  }

  /* Tile a texture image across a platform rectangle in 2×2 ft cells with
     pseudo-random 90° rotation per cell — similar to the flooring roll tiling
     but at a scale appropriate for rubber EPDM platform surfaces. */
  _drawTiledPlatformTexture(ctx, img, x, y, w, h) {
    const cellSize = 2; // 2 ft cells — good speckle density for platforms
    const cols = Math.ceil(w / cellSize);
    const rows = Math.ceil(h / cellSize);
    const bleed = 0.02; // slight overlap to prevent hairline gaps
    const pi = '14159265358979323846264338327950288419716939937510';
    const piLen = pi.length;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    for (let ci = 0; ci < cols; ci++) {
      for (let ri = 0; ri < rows; ri++) {
        const cx = x + ci * cellSize;
        const cy2 = y + ri * cellSize;
        const cw = Math.min(cellSize, x + w - cx);
        const ch = Math.min(cellSize, y + h - cy2);
        const idx = (((ci * 997) + (ri * 1013)) % piLen + piLen) % piLen;
        const dig = pi.charCodeAt(idx) - 48;
        const rot = (dig % 4) * Math.PI / 2;
        if (rot) {
          ctx.save();
          ctx.translate(cx + cw / 2, cy2 + ch / 2);
          ctx.rotate(rot);
          const isOrtho = (rot === Math.PI / 2 || rot === 3 * Math.PI / 2);
          const dw = isOrtho ? ch : cw;
          const dh = isOrtho ? cw : ch;
          ctx.drawImage(img, -dw / 2 - bleed, -dh / 2 - bleed, dw + 2 * bleed, dh + 2 * bleed);
          ctx.restore();
        } else {
          ctx.drawImage(img, cx - bleed, cy2 - bleed, cw + 2 * bleed, ch + 2 * bleed);
        }
      }
    }
    ctx.restore();
  }

}

window.RoomDesignerRender = RoomDesignerRender;
